import { EventEmitter, Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { evaluate } from 'mathjs'
import moment from 'moment';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { WidgetDashboardService } from '../../../../core/widget-dashboard/widget-dashboard.service';
import { evaluateCustomFormulaNodes, prepareTreeMetaData, treeToArray } from './configuration-modal/treeUtils';
import { heatmapLayout } from '../widget/graphJson';
import _ from 'lodash';
import { UserService } from '../../../../core/auth/_services/user.service';
import { FacilityService } from '../../../../core/configuration/_services/facility.service';
import { SystemService } from '../../../../core/configuration/_services/system.service';
import { UnitService } from '../../../../core/configuration/_services/unit.service';
import { AlertService } from '../../../../core/alert/alert.service';
import { DatePipe } from '@angular/common';
import { AssetService } from '../../../../core/configuration/_services/asset.service';
import { TagService } from '../../../../core/configuration/_services/tag.service';
import { FilterOperator, ICalcDrilldownFilter, IDrillDownObject, INormalDrilldownFilter, IPopulatedField, ISlicerFilter, WidgetSlicerState, IGroupByChartDrillDownObject, IGroupByChartCalcDrilldownFilter, IGroupByChartNormalDrilldownFilter } from '../../../../core/widget-dashboard/widget-dashboard.interface';
import { FONT_FAMILY } from '../../../../core/common/constants/styles.constants';
import { TablePanelData } from '../../../../core/widget-dashboard/widget-dashboard.model';

@Injectable({
    providedIn: 'root'
})
export class EditPanelNewService {
    dashboards: any[]
    dynamicPiechartWidth: any;
    dynamicPiechartHeight: any;
    completeTagList: any[];
    private tagListObservable$: Observable<any[]>;
    completeAssetList: any[];
    SurveyChartPanelDataNew: any;
    selectGraph = new EventEmitter<any>();
    widgetChanged = new EventEmitter<any>();
    tagListChanged = new EventEmitter<any>();
    groupStatOptionChanged = new EventEmitter<any>();
    besidePieChartSizeChanged = new EventEmitter<any>();
    trendChartEnablesForSingleStats = new EventEmitter<any>();
    showSensorTablePreview = new EventEmitter<TablePanelData>();
    private customFormulaComponentRefresher = new Subject<string>();
    refreshCustomFormulaData = this.customFormulaComponentRefresher.asObservable()
    targetCollectionChanged = new Subject<any>();
    isInValidForm: boolean = false;
    userProjection: string = "name,_id,admin,email";
    unitProjection: string = "_id,name";
    facilityProjection: string = "_id,name";
    systemProjection: string = "_id,name";
    constructor(private readonly widgetDashboardService: WidgetDashboardService,
        private readonly userService: UserService,
        private readonly facilityService: FacilityService,
        private readonly systemService: SystemService,
        private readonly unitService: UnitService,
        private readonly alertService: AlertService,
        private readonly assetService: AssetService,
        private readonly tagService: TagService,
        private readonly datePipe: DatePipe) { this.getListData(); }

    getTagList(): Observable<any[]> {
        if (this.completeTagList?.length) {
            return of(this.completeTagList);
        }
        if (!this.tagListObservable$) {
            this.tagListObservable$ = this.tagService.getTag(null, true, false, false, '-1', 'name').pipe(
                map((res: any) => {
                    this.completeTagList = res?.data || [];
                    return this.completeTagList;
                }),
                catchError(err => {
                    this.tagListObservable$ = null;
                    return throwError(() => err);
                }),
                shareReplay(1)
            );
        }
        return this.tagListObservable$;
    }

      getListData() {
        if (!this.userService?.userList?.length)
          this.userService.getUser(this.userProjection);
        if (!this.systemService?.systemList?.length)
          this.systemService.getSystem(0, {}, false, this.systemProjection);
        if (!this.facilityService?.facilityList?.length)
          this.facilityService.getFacility(false, '', null, this.facilityProjection);
        if (!this.unitService?.unitList?.length)
            this.unitService.getUnit(this.unitProjection);
        if(!this.completeAssetList?.length)
            this.assetService.getAsset(null, true, 'name').subscribe((res: any) => {
                this.completeAssetList = res.data;
            });
        if(!this.completeTagList?.length)
            this.getTagList().subscribe();
      }
    tableColumnTimeWindow = [
    { id: 1, label: 'Today' },
    { id: 5 , label: 'Yesterday' },
    { id: 2, label: 'This Week' },
    { id: 3, label: 'This Month' },
    { id: 4, label: 'This Year' },
    { id: 99, label: 'This Financial Year' }
  ];
     limitFilterColorMapping = {
        lessThan: '#10955E',
        greaterThan: '#CC2D55',
        between: '#F5971B'
     }

    getTimeRangeById(rangeId, fyDate = null) {
        const now = moment();
        let from = moment(now);

        switch (rangeId) {
            case 1: // Today
                from.startOf('day');
                break;

            case 2: // This week (Monday start)
                from.startOf('isoWeek');
                break;

            case 3: // This month
                from.startOf('month');
                break;

            case 4: // This year
                from.startOf('year');
                break;

            case 5: // Yesterday
                from.subtract(1, 'day').startOf('day');
                now.subtract(1, 'day').endOf('day');
                break;

            case 99: { // Custom financial year
                const fyStart = moment(fyDate);
                const fyStartMonth = fyStart.month();
                const fyStartDate = fyStart.date();

                from
                    .month(fyStartMonth)
                    .date(fyStartDate)
                    .startOf('day');

                // If today is before FY start → FY started last year
                if (now.isBefore(from)) {
                    from.subtract(1, 'year');
                }
                break;
            }
        }

        return {
            from,
            to: now
        };
    }

    common = {
        panel: {
            displayText: "Panel",
            //order: 1,
            name: {
                displayText: "Name",
            },
            description: {
                displayText: "Description"
            }
        },
        axis: {
            displayText: "Axis",
            // order: 3,
        },
        graph: {
            displayText: "Graph Options",
            decimalPlaces: {
                displayText: "Decimal Places",
                options: [
                    {
                        displayText: 0,
                        value: 0
                    },
                    {
                        displayText: 1,
                        value: 1
                    },
                    {
                        displayText: 2,
                        value: 2
                    },
                    {
                        displayText: 3,
                        value: 3
                    },
                    {
                        displayText: 4,
                        value: 4
                    },
                    {
                        displayText: 5,
                        value: 5
                    }
                ]
            }
            //order: 4,
        },
        filter: {
            unit: {
                maxSelect: -1
            },
            system: {
                maxSelect: -1
            }
        }

    }

    specific = {
        //this graph option will override common graph options
        '13': {
            extraInputs: [
                {
                    displayText: 'Measure',
                    key: 'measure',
                    type: 'dropdown',
                    show: true,
                    options: [{
                        displayText: 'Absolute',
                        value: 'absolute'
                    }, {
                        displayText: 'Relative',
                        value: 'relative'
                    },
                    {
                        displayText: 'Total',
                        value: 'total'
                    }]
                }
            ],
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                valueDirection: {
                    displayText: "Value Direction",
                    options: [
                        {
                            displayText: "Original (Increase +, decrease -)",
                            value: 1
                        },
                        {
                            displayText: "Reverse (Increase -, decrease +)",
                            value: 2
                        }
                    ]
                },
                orientation: {
                    displayText: "Orientation",
                    options: [
                        {
                            displayText: "Horizontal",
                            value: 'h'
                        },
                        {
                            displayText: "Vertical",
                            value: 'v'
                        }
                    ]
                }
            }, axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                },
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            }
        },

        '14': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                groupBy: {
                    displayText: "Group By",
                    options: [
                        {
                            displayText: 'None',
                            value: ''
                        },
                        {
                            displayText: 'Unit',
                            value: 'unit'
                        },
                        {
                            displayText: 'Label',
                            value: 'label'
                        }
                    ]
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true,
                },
                // tableMode: true,
                orientation: {
                    displayText: "Orientation",
                    options: [
                        {
                            displayText: "Horizontal",
                            value: 'h'
                        },
                        {
                            displayText: "Vertical",
                            value: 'v'
                        }
                    ]
                },
                barmode: {
                    displayText: "BarMode",
                    options: [
                        {
                            displayText: "Group",
                            value: 'group'
                        },
                        {
                            displayText: "Stack",
                            value: 'stack'
                        }
                    ]
                },
                quickPlot: {
                    displayText: "Quick Plot"
                },
            },
            axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                },
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                }
            }
        },
        '16': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                orientation: {
                    displayText: "Orientation",
                    options: [
                        {
                            displayText: "Horizontal",
                            value: 'h'
                        },
                        {
                            displayText: "Vertical",
                            value: 'v'
                        }
                    ]
                }
            },
            axis: {
                displayText: "Axis",
                xaxis: {
                   showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                   }
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            }
        },
        '15':{
            extraInputs: [
                {
                    displayText: 'DV Value',
                    key: 'dv_value',
                    type: 'dropdown',
                    show: true
                }
            ],
            axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                },
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                }
            },
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            }
        },
        '8': {
            filter: {
                system: {
                    maxSelect: 1
                }
            }
        },
        '4': {
            graph: {
                hole: {
                    displayText: "Donut Size",
                    options: [
                        {
                            displayText: "None",
                            value: 0
                        },
                        {
                            displayText: "Small",
                            value: 0.4
                        },
                        {
                            displayText: "Medium",
                            value: 0.6
                        },
                        {
                            displayText: "Large",
                            value: 0.85
                        },
                    ]
                },
                semiPieChart: {
                    displayText: "Semi Pie Chart"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    legendType: {
                        displayText: "Legend Type",
                        options: [
                            {
                                displayText: "Default",
                                value: 'd'
                            },
                            {
                                displayText: "Beside Chart",
                                value: 'b'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                },
                quickPlot: {
                    displayText: "Quick Plot"
                }
            }
        },
        '9': {
            filter: {
                system: {
                    maxSelect: 1
                }
            },
            graph: {
                orientation: {
                    displayText: "Orientation",
                    options: [
                        {
                            displayText: "Horizontal",
                            value: 'h'
                        },
                        {
                            displayText: "Vertical",
                            value: 'v'
                        }
                    ]
                }
            }
        },
        '10': {
            filter: {
                system: {
                    maxSelect: 1
                }
            }
        },
        '11': {
            graph: {
                quickPlot: {
                    displayText: "Quick Plot"
                },
                showTrend: {
                    displayText: "Show Trend Chart"
                },
                autoScaleFont: {
                    displayText: "Auto Scale Font"
                },
                Shorthand: {
                    displayText: "Shorthand Font"
                },
                GroupStat: {
                    displayText: "Group Stats"
                },
                titleColor: {
                    displayText: "Title Color",
                },
                titlePosition: {
                    displayText: 'Title Position',
                    options: [
                        {
                            displayText: 'Left',
                            value: 'left'
                        },
                        {
                            displayText: 'Right',
                            value: 'right'
                        },
                        {
                            displayText: 'Center',
                            value: 'center'
                        }
                    ]
                },
                trendChartColor: {
                    displayText: "Trend Chart Color",
                },
                borderType: {
                    displayText: "Border Type",
                    options : [
                        {
                            displayText: 'None',
                            value: 'none'
                        },
                        {
                            displayText: 'Default',
                            value: 'default'
                        },
                        {
                            displayText: 'All',
                            value: 'all'
                        },
                        {
                            displayText: 'Top',
                            value: 'top'
                        },
                        {
                            displayText: 'Bottom',
                            value: 'bottom'
                        },
                        {
                            displayText: 'Left',
                            value: 'left'
                        },
                        {
                            displayText: 'Right',
                            value: 'right'
                        },
                        {
                            displayText: 'Top And Bottom',
                            value: 'top-bottom'
                        },
                        {
                            displayText: 'Left And Right',
                            value: 'left-right'
                        }
                    ]
                },
                borderColor: {
                    displayText: "Border Color"
                },
                hidePanelHeader: {
                    displayText: 'Hide Panel Header'
                }
            }
        },
        '1': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                quickPlot: {
                    displayText: "Quick Plot"
                }
                ,
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            },
            axis: {
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                },
            }
        },
        '2': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            },
            axis: {
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            }
        },
        '3': {
            axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                },
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            },
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            }
        },
        '5': {
            axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                },
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            },
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                stacked: {
                    displayText: "Stacked"
                },
                quickPlot: {
                    displayText: "Quick Plot"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            }
        },
        '7': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                quickPlot: {
                    displayText: "Quick Plot"
                },
            }
        },
        '6': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                },
                quickPlot: {
                    displayText: "Quick Plot"
                },
                barColor: {
                    displayText: "Bar Color",
                    type: 'color'
                },
                curveColor: {
                    displayText: "Curve Color",
                    type: 'color'
                }
            },
            axis: {
                displayText: "Axis",
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            }
        },
        '12': {
            axis: {
                displayText: "Axis",
                // order: 3,
                xaxis: {
                    showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                    }
                }
            }
        },
        '18': {
            axis: {
                xaxis: {
                    unit: {
                        displayText: 'X axis unit'
                    }
                },
                displayText: 'Axis',
                type: {
                    displayText: "Axis Type",
                    options: [
                        {
                            displayText: "Normal",
                            value: 'linear'
                        },
                        {
                            displayText: "Log Scale",
                            value: 'log'
                        }
                    ]
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            },
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend: {
                    displayText: "Legend",
                    position: {
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation: {
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                }
            }
        },
        '19': {
            graph: {
                toolbar: {
                    displayText: "Show toolbar"
                },
                legend:{
                    displayText: "Legend",
                    position:{
                        displayText: "Legend Position",
                        options: [
                            {
                                displayText: "Top left",
                                value: 1
                            },
                            {
                                displayText: "Top right",
                                value: 2
                            },
                            {
                                displayText: "Bottom left",
                                value: 3
                            },
                            {
                                displayText: " Bottom right",
                                value: 4
                            }
                        ]
                    },
                    orientation:{
                        displayText: "Legend Orientation",
                        options: [
                            {
                                displayText: "Horizontal",
                                value: 'h'
                            },
                            {
                                displayText: "Vertical",
                                value: 'v'
                            }
                        ]
                    },
                    showLegend: true,
                    overlapLegend: true
                },
                groupBy: {
                    displayText: "Group By Time",
                    options: [
                        {
                            displayText: 'None',
                            value: '',
                            disable: false
                        },
                        {
                            displayText: 'Hours',
                            value: 'hour',
                            disable : false
                        },
                        {
                            displayText: 'Days',
                            value: 'day',
                            disable : false
                        },
                        {
                            displayText: 'Weeks',
                            value: 'week',
                            disable : false
                        },
                        {
                            displayText: 'Months',
                            value: 'month',
                            disable : false
                        },
                        {
                            displayText: 'Years',
                            value: 'year',
                            disable : false
                        }
                    ]
                }
            },
            axis: {
                displayText: "Axis",
                xaxis: {
                   showLabel: {
                        displayText: "Axis Label",
                        options: [
                            {
                                displayText: "Show",
                                value: true
                            },
                            {
                                displayText: "Hide",
                                value: false
                            }
                        ]
                   }
                },
                yaxis: {
                    unit : {
                        displayText: "Y Axis Unit"
                    },
                    minRange: {
                        displayText: "Y Axis Min Range"
                    },
                    maxRange: {
                        displayText: "Y Axis Max Range"
                    }
                }
            }
        },
    }

    matJsonList = {
        "3d_rotation": "3D Rotation",
        "accessibility": "Accessibility",
        "accessible": "Accessible",
        "account_balance": "Account Balance",
        "account_balance_wallet": "Account Balance Wallet",
        "account_box": "Account Box",
        "account_circle": "Account Circle",
        "add_shopping_cart": "Add Shopping Cart",
        "alarm": "Alarm",
        "alarm_add": "Alarm Add",
        "alarm_off": "Alarm Off",
        "alarm_on": "Alarm On",
        "android": "Android",
        "announcement": "Announcement",
        "aspect_ratio": "Aspect Ratio",
        "assessment": "Assessment",
        "assignment": "Assignment",
        "assignment_ind": "Assignment Ind",
        "assignment_late": "Assignment Late",
        "assignment_return": "Assignment Return",
        "assignment_returned": "Assignment Returned",
        "assignment_turned_in": "Assignment Turned In",
        "autorenew": "Autorenew",
        "backup": "Backup",
        "book": "Book",
        "bookmark": "Bookmark",
        "bookmark_border": "Bookmark Border",
        "bug_report": "Bug Report",
        "build": "Build",
        "cached": "Cached",
        "camera_enhance": "Camera Enhance",
        "card_giftcard": "Card Giftcard",
        "card_membership": "Card Membership",
        "card_travel": "Card Travel",
        "change_history": "Change History",
        "check_circle": "Check Circle",
        "chrome_reader_mode": "Chrome Reader Mode",
        "class": "Class",
        "code": "Code",
        "compare_arrows": "Compare Arrows",
        "contact_support": "Contact Support",
        "credit_card": "Credit Card",
        "dashboard": "Dashboard",
        "date_range": "Date Range",
        "delete": "Delete",
        "delete_forever": "Delete Forever",
        "description": "Description",
        "dns": "DNS",
        "done": "Done",
        "done_all": "Done All",
        "donut_large": "Donut Large",
        "donut_small": "Donut Small",
        "eject": "Eject",
        "euro_symbol": "Euro Symbol",
        "event": "Event",
        "event_seat": "Event Seat",
        "exit_to_app": "Exit To App",
        "explore": "Explore",
        "extension": "Extension",
        "face": "Face",
        "favorite": "Favorite",
        "favorite_border": "Favorite Border",
        "feedback": "Feedback",
        "find_in_page": "Find In Page",
        "find_replace": "Find Replace",
        "fingerprint": "Fingerprint",
        "flight_land": "Flight Land",
        "flight_takeoff": "Flight Takeoff",
        "flip_to_back": "Flip To Back",
        "flip_to_front": "Flip To Front",
        "g_translate": "G Translate",
        "gavel": "Gavel",
        "get_app": "Get App",
        "gif": "GIF",
        "grade": "Grade",
        "group_work": "Group Work",
        "help": "Help",
        "help_outline": "Help Outline",
        "highlight_off": "Highlight Off",
        "history": "History",
        "home": "Home",
        "hourglass_empty": "Hourglass Empty",
        "hourglass_full": "Hourglass Full",
        "http": "HTTP",
        "https": "HTTPS",
        "important_devices": "Important Devices",
        "info": "Info",
        "info_outline": "Info Outline",
        "input": "Input",
        "invert_colors": "Invert Colors",
        "label": "Label",
        "label_outline": "Label Outline",
        "language": "Language",
        "launch": "Launch",
        "lightbulb_outline": "Lightbulb Outline",
        "line_style": "Line Style",
        "line_weight": "Line Weight",
        "list": "List",
        "lock": "Lock",
        "lock_open": "Lock Open",
        "loyalty": "Loyalty",
        "markunread_mailbox": "Markunread Mailbox",
        "maximize": "Maximize",
        "minimize": "Minimize",
        "motorcycle": "Motorcycle",
        "note_add": "Note Add",
        "offline_bolt": "Offline Bolt",
        "offline_pin": "Offline Pin",
        "opacity": "Opacity",
        "open_in_browser": "Open In Browser",
        "open_in_new": "Open In New",
        "open_with": "Open With",
        "pageview": "Pageview",
        "pan_tool": "Pan Tool",
        "payment": "Payment",
        "perm_camera_mic": "Perm Camera Mic",
        "perm_contact_calendar": "Perm Contact Calendar",
        "perm_data_setting": "Perm Data Setting",
        "perm_device_information": "Perm Device Information",
        "perm_identity": "Perm Identity",
        "perm_media": "Perm Media",
        "perm_phone_msg": "Perm Phone Msg",
        "perm_scan_wifi": "Perm Scan Wifi",
        "pets": "Pets",
        "picture_in_picture": "Picture In Picture",
        "picture_in_picture_alt": "Picture In Picture Alt",
        "play_for_work": "Play For Work",
        "polymer": "Polymer",
        "power_settings_new": "Power Settings New",
        "pregnant_woman": "Pregnant Woman",
        "print": "Print",
        "query_builder": "Query Builder",
        "question_answer": "Question Answer",
        "receipt": "Receipt",
        "record_voice_over": "Record Voice Over",
        "redeem": "Redeem",
        "remove_shopping_cart": "Remove Shopping Cart",
        "reorder": "Reorder",
        "report_problem": "Report Problem",
        "restore": "Restore",
        "restore_from_trash": "Restore From Trash",
        "restore_page": "Restore Page",
        "room": "Room",
        "rounded_corner": "Rounded Corner",
        "rowing": "Rowing",
        "schedule": "Schedule",
        "search": "Search",
        "settings": "Settings",
        "settings_applications": "Settings Applications",
        "settings_backup_restore": "Settings Backup Restore",
        "settings_bluetooth": "Settings Bluetooth",
        "settings_brightness": "Settings Brightness",
        "settings_cell": "Settings Cell",
        "settings_ethernet": "Settings Ethernet",
        "settings_input_antenna": "Settings Input Antenna",
        "settings_input_component": "Settings Input Component",
        "settings_input_composite": "Settings Input Composite",
        "settings_input_hdmi": "Settings Input HDMI",
        "settings_input_svideo": "Settings Input S-Video",
        "settings_overscan": "Settings Overscan",
        "settings_phone": "Settings Phone",
        "settings_power": "Settings Power",
        "settings_remote": "Settings Remote",
        "settings_voice": "Settings Voice",
        "shop": "Shop",
        "shop_two": "Shop Two",
        "shopping_basket": "Shopping Basket",
        "shopping_cart": "Shopping Cart",
        "speaker_notes": "Speaker Notes",
        "speaker_notes_off": "Speaker Notes Off",
        "spellcheck": "Spellcheck",
        "stars": "Stars",
        "store": "Store",
        "subject": "Subject",
        "supervisor_account": "Supervisor Account",
        "swap_horiz": "Swap Horiz",
        "swap_vert": "Swap Vert",
        "swap_vertical_circle": "Swap Vertical Circle",
        "tab": "Tab",
        "tab_unselected": "Tab Unselected",
        "theaters": "Theaters",
        "thumb_down": "Thumb Down",
        "thumb_up": "Thumb Up",
        "thumbs_up_down": "Thumbs Up Down",
        "timeline": "Timeline",
        "toc": "Toc",
        "today": "Today",
        "toll": "Toll",
        "touch_app": "Touch App",
        "track_changes": "Track Changes",
        "translate": "Translate",
        "trending_down": "Trending Down",
        "trending_flat": "Trending Flat",
        "trending_up": "Trending Up",
        "turned_in": "Turned In",
        "turned_in_not": "Turned In Not",
        "update": "Update",
        "verified_user": "Verified User",
        "view_agenda": "View Agenda",
        "view_array": "View Array",
        "view_carousel": "View Carousel",
        "view_column": "View Column",
        "view_day": "View Day",
        "view_headline": "View Headline",
        "view_list": "View List",
        "view_module": "View Module",
        "view_quilt": "View Quilt",
        "view_stream": "View Stream",
        "view_week": "View Week",
        "visibility": "Visibility",
        "visibility_off": "Visibility Off",
        "watch_later": "Watch Later",
        "work": "Work",
        "youtube_searched_for": "YouTube Searched For",
        "zoom_in": "Zoom In",
        "zoom_out": "Zoom Out",
        "access_alarm": "Access Alarm",
  "access_alarms": "Access Alarms",
  "access_time": "Access Time",
  "add_alarm": "Add Alarm",
  "airplanemode_active": "Airplane Mode Active",
  "airplanemode_inactive": "Airplane Mode Inactive",
  "battery_alert": "Battery Alert",
  "battery_charging_full": "Battery Charging Full",
  "battery_full": "Battery Full",
  "battery_std": "Battery Standard",
  "battery_unknown": "Battery Unknown",
  "bluetooth": "Bluetooth",
  "bluetooth_connected": "Bluetooth Connected",
  "bluetooth_disabled": "Bluetooth Disabled",
  "bluetooth_searching": "Bluetooth Searching",
  "brightness_auto": "Brightness Auto",
  "brightness_high": "Brightness High",
  "brightness_low": "Brightness Low",
  "brightness_medium": "Brightness Medium",
  "data_usage": "Data Usage",
  "developer_mode": "Developer Mode",
  "devices": "Devices",
  "dvr": "DVR",
  "gps_fixed": "GPS Fixed",
  "gps_not_fixed": "GPS Not Fixed",
  "gps_off": "GPS Off",
  "location_disabled": "Location Disabled",
  "location_searching": "Location Searching",
  "mobile_friendly": "Mobile Friendly",
  "mobile_off": "Mobile Off",
  "network_cell": "Network Cell",
  "network_wifi": "Network Wifi",
  "nfc": "NFC",
  "screen_lock_landscape": "Screen Lock Landscape",
  "screen_lock_portrait": "Screen Lock Portrait",
  "screen_lock_rotation": "Screen Lock Rotation",
  "screen_rotation": "Screen Rotation",
  "sd_storage": "SD Storage",
  "settings_system_daydream": "Settings System Daydream",
  "signal_cellular_4_bar": "Signal Cellular 4 Bar",
  "signal_cellular_connected_no_internet_4_bar": "Signal Cellular Connected No Internet 4 Bar",
  "signal_cellular_no_sim": "Signal Cellular No SIM",
  "signal_cellular_null": "Signal Cellular Null",
  "signal_cellular_off": "Signal Cellular Off",
  "signal_wifi_4_bar": "Signal Wifi 4 Bar",
  "signal_wifi_4_bar_lock": "Signal Wifi 4 Bar Lock",
  "signal_wifi_off": "Signal Wifi Off",
  "storage": "Storage",
  "usb": "USB",
  "wifi_lock": "Wifi Lock",
  "wifi_tethering": "Wifi Tethering",
  "add_comment": "Add Comment",
  "attach_file": "Attach File",
  "attach_money": "Attach Money",
  "bar_chart": "Bar Chart",
  "border_all": "Border All",
  "border_bottom": "Border Bottom",
  "border_clear": "Border Clear",
  "border_color": "Border Color",
  "border_horizontal": "Border Horizontal",
  "border_inner": "Border Inner",
  "border_left": "Border Left",
  "border_outer": "Border Outer",
  "border_right": "Border Right",
  "border_style": "Border Style",
  "border_top": "Border Top",
  "border_vertical": "Border Vertical",
  "bubble_chart": "Bubble Chart",
  "drag_handle": "Drag Handle",
  "format_align_center": "Format Align Center",
  "format_align_justify": "Format Align Justify",
  "format_align_left": "Format Align Left",
  "format_align_right": "Format Align Right",
  "format_bold": "Format Bold",
  "format_clear": "Format Clear",
  "format_color_fill": "Format Color Fill",
  "format_color_reset": "Format Color Reset",
  "format_color_text": "Format Color Text",
  "format_indent_decrease": "Format Indent Decrease",
  "format_indent_increase": "Format Indent Increase",
  "format_italic": "Format Italic",
  "format_line_spacing": "Format Line Spacing",
  "format_list_bulleted": "Format List Bulleted",
  "format_list_numbered": "Format List Numbered",
  "format_paint": "Format Paint",
  "format_quote": "Format Quote",
  "format_shapes": "Format Shapes",
  "format_size": "Format Size",
  "format_strikethrough": "Format Strikethrough",
  "format_textdirection_l_to_r": "Format Textdirection L To R",
  "format_textdirection_r_to_l": "Format Textdirection R To L",
  "format_underlined": "Format Underlined",
  "functions": "Functions",
  "highlight": "Highlight",
  "insert_chart": "Insert Chart",
  "insert_chart_outlined": "Insert Chart Outlined",
  "insert_comment": "Insert Comment",
  "insert_drive_file": "Insert Drive File",
  "insert_emoticon": "Insert Emoticon",
  "insert_invitation": "Insert Invitation",
  "insert_link": "Insert Link",
  "insert_photo": "Insert Photo",
  "linear_scale": "Linear Scale",
  "merge_type": "Merge Type",
  "mode_comment": "Mode Comment",
  "mode_edit": "Mode Edit",
  "monetization_on": "Monetization On",
  "money_off": "Money Off",
  "multiline_chart": "Multiline Chart",
  "notes": "Notes",
  "pie_chart": "Pie Chart",
  "post_add": "Post Add",
  "publish": "Publish",
  "scatter_plot": "Scatter Plot",
  "score": "Score",
  "short_text": "Short Text",
  "show_chart": "Show Chart",
  "space_bar": "Space Bar",
  "strikethrough_s": "Strikethrough S",
  "subdirectory_arrow_left": "Subdirectory Arrow Left",
  "subdirectory_arrow_right": "Subdirectory Arrow Right",
  "table_chart": "Table Chart",
  "text_fields": "Text Fields",
  "title": "Title",
  "vertical_align_bottom": "Vertical Align Bottom",
  "vertical_align_center": "Vertical Align Center",
  "vertical_align_top": "Vertical Align Top",
  "wrap_text": "Wrap Text",
  "attachment": "Attachment",
  "cloud": "Cloud",
  "cloud_circle": "Cloud Circle",
  "cloud_done": "Cloud Done",
  "cloud_download": "Cloud Download",
  "cloud_off": "Cloud Off",
  "cloud_queue": "Cloud Queue",
  "cloud_upload": "Cloud Upload",
  "create_new_folder": "Create New Folder",
  "folder": "Folder",
  "folder_open": "Folder Open",
  "folder_shared": "Folder Shared",
  "cast": "Cast",
  "cast_connected": "Cast Connected",
  "computer": "Computer",
  "desktop_mac": "Desktop Mac",
  "desktop_windows": "Desktop Windows",
  "developer_board": "Developer Board",
  "dock": "Dock",
  "gamepad": "Gamepad",
  "headset": "Headset",
  "headset_mic": "Headset Mic",
  "keyboard": "Keyboard",
  "keyboard_arrow_down": "Keyboard Arrow Down",
  "keyboard_arrow_left": "Keyboard Arrow Left",
  "keyboard_arrow_right": "Keyboard Arrow Right",
  "keyboard_arrow_up": "Keyboard Arrow Up",
  "keyboard_backspace": "Keyboard Backspace",
  "keyboard_capslock": "Keyboard Capslock",
  "keyboard_hide": "Keyboard Hide",
  "keyboard_return": "Keyboard Return",
  "keyboard_tab": "Keyboard Tab",
  "keyboard_voice": "Keyboard Voice",
  "laptop": "Laptop",
  "laptop_chromebook": "Laptop Chromebook",
  "laptop_mac": "Laptop Mac",
  "laptop_windows": "Laptop Windows",
  "memory": "Memory",
  "mouse": "Mouse",
  "phone_android": "Phone Android",
  "phone_iphone": "Phone Iphone",
  "phonelink": "Phonelink",
  "phonelink_off": "Phonelink Off",
  "router": "Router",
  "scanner": "Scanner",
  "security": "Security",
  "sim_card": "Sim Card",
  "smartphone": "Smartphone",
  "speaker": "Speaker",
  "speaker_group": "Speaker Group",
  "tablet": "Tablet",
  "tablet_android": "Tablet Android",
  "tablet_mac": "Tablet Mac",
  "toys": "Toys",
  "tv": "TV",
  "videogame_asset": "Videogame Asset",
  "watch": "Watch",
  "add_a_photo": "Add A Photo",
  "add_photo_alternate": "Add Photo Alternate",
  "add_to_photos": "Add To Photos",
  "adjust": "Adjust",
  "assistant": "Assistant",
  "assistant_photo": "Assistant Photo",
  "audiotrack": "Audiotrack",
  "blur_circular": "Blur Circular",
  "blur_linear": "Blur Linear",
  "blur_off": "Blur Off",
  "blur_on": "Blur On",
  "brightness_1": "Brightness 1",
  "brightness_2": "Brightness 2",
  "brightness_3": "Brightness 3",
  "brightness_4": "Brightness 4",
  "brightness_5": "Brightness 5",
  "brightness_6": "Brightness 6",
  "brightness_7": "Brightness 7",
  "broken_image": "Broken Image",
  "brush": "Brush",
  "burst_mode": "Burst Mode",
  "camera": "Camera",
  "camera_alt": "Camera Alt",
  "camera_front": "Camera Front",
  "camera_rear": "Camera Rear",
  "camera_roll": "Camera Roll",
  "center_focus_strong": "Center Focus Strong",
  "center_focus_weak": "Center Focus Weak",
  "collections": "Collections",
  "collections_bookmark": "Collections Bookmark",
  "color_lens": "Color Lens",
  "colorize": "Colorize",
  "compare": "Compare",
  "control_point": "Control Point",
  "control_point_duplicate": "Control Point Duplicate",
  "crop": "Crop",
  "crop_16_9": "Crop 16 9",
  "crop_3_2": "Crop 3 2",
  "crop_5_4": "Crop 5 4",
  "crop_7_5": "Crop 7 5",
  "crop_din": "Crop Din",
  "crop_free": "Crop Free",
  "crop_landscape": "Crop Landscape",
  "crop_original": "Crop Original",
  "crop_portrait": "Crop Portrait",
  "crop_rotate": "Crop Rotate",
  "crop_square": "Crop Square",
  "dehaze": "Dehaze",
  "details": "Details",
  "edit": "Edit",
  "exposure": "Exposure",
  "exposure_neg_1": "Exposure Neg 1",
  "exposure_neg_2": "Exposure Neg 2",
  "exposure_plus_1": "Exposure Plus 1",
  "exposure_plus_2": "Exposure Plus 2",
  "exposure_zero": "Exposure Zero",
  "filter": "Filter",
  "filter_1": "Filter 1",
  "filter_2": "Filter 2",
  "filter_3": "Filter 3",
  "filter_4": "Filter 4",
  "filter_5": "Filter 5",
  "filter_6": "Filter 6",
  "filter_7": "Filter 7",
  "filter_8": "Filter 8",
  "filter_9": "Filter 9",
  "filter_9_plus": "Filter 9 Plus",
  "filter_b_and_w": "Filter B And W",
  "filter_center_focus": "Filter Center Focus",
  "filter_drama": "Filter Drama",
  "filter_frames": "Filter Frames",
  "filter_hdr": "Filter Hdr",
  "filter_none": "Filter None",
  "filter_tilt_shift": "Filter Tilt Shift",
  "filter_vintage": "Filter Vintage",
  "flare": "Flare",
  "flash_auto": "Flash Auto",
  "flash_off": "Flash Off",
  "flash_on": "Flash On",
  "flip": "Flip",
  "gradient": "Gradient",
  "grain": "Grain",
  "grid_off": "Grid Off",
  "grid_on": "Grid On",
  "hdr_off": "HDR Off",
  "hdr_on": "HDR On",
  "hdr_strong": "HDR Strong",
  "hdr_weak": "HDR Weak",
  "healing": "Healing",
  "image": "Image",
  "image_aspect_ratio": "Image Aspect Ratio",
  "image_search": "Image Search",
  "iso": "ISO",
  "landscape": "Landscape",
  "leak_add": "Leak Add",
  "leak_remove": "Leak Remove",
  "lens": "Lens",
  "linked_camera": "Linked Camera",
  "looks": "Looks",
  "looks_3": "Looks 3",
  "looks_4": "Looks 4",
  "looks_5": "Looks 5",
  "looks_6": "Looks 6",
  "looks_one": "Looks One",
  "looks_two": "Looks Two",
  "loupe": "Loupe",
  "monochrome_photos": "Monochrome Photos",
  "movie_creation": "Movie Creation",
  "movie_filter": "Movie Filter",
  "music_note": "Music Note",
  "music_off": "Music Off",
  "nature": "Nature",
  "nature_people": "Nature People",
  "navigate_before": "Navigate Before",
  "navigate_next": "Navigate Next",
  "palette": "Palette",
  "panorama": "Panorama",
  "panorama_fish_eye": "Panorama Fish Eye",
  "panorama_horizontal": "Panorama Horizontal",
  "panorama_vertical": "Panorama Vertical",
  "panorama_wide_angle": "Panorama Wide Angle",
  "photo": "Photo",
  "photo_album": "Photo Album",
  "photo_camera": "Photo Camera",
  "photo_filter": "Photo Filter",
  "photo_library": "Photo Library",
  "photo_size_select_actual": "Photo Size Select Actual",
  "photo_size_select_large": "Photo Size Select Large",
  "photo_size_select_small": "Photo Size Select Small",
  "picture_as_pdf": "Picture As PDF",
  "portrait": "Portrait",
  "remove_red_eye": "Remove Red Eye",
  "rotate_90_degrees_ccw": "Rotate 90 Degrees CCW",
  "rotate_left": "Rotate Left",
  "rotate_right": "Rotate Right",
  "shutter_speed": "Shutter Speed",
  "slideshow": "Slideshow",
  "straighten": "Straighten",
  "style": "Style",
  "switch_camera": "Switch Camera",
  "switch_video": "Switch Video",
  "tag_faces": "Tag Faces",
  "texture": "Texture",
  "timelapse": "Timelapse",
  "timer": "Timer",
  "timer_10": "Timer 10",
  "timer_3": "Timer 3",
  "timer_off": "Timer Off",
  "tonality": "Tonality",
  "transform": "Transform",
  "tune": "Tune",
  "view_comfy": "View Comfy",
  "view_compact": "View Compact",
  "vignette": "Vignette",
  "wb_auto": "WB Auto",
  "wb_cloudy": "WB Cloudy",
  "wb_incandescent": "WB Incandescent",
  "wb_iridescent": "WB Iridescent",
  "wb_sunny": "WB Sunny",
  "360": "360",
  "add_business": "Add Business",
  "add_location": "Add Location",
  "add_location_alt": "Add Location Alt",
  "add_road": "Add Road",
  "agriculture": "Agriculture",
  "alt_route": "Alt Route",
  "atm": "ATM",
  "beenhere": "Beenhere",
  "bike_scooter": "Bike Scooter",
  "breakfast_dining": "Breakfast Dining",
  "car_rental": "Car Rental",
  "car_repair": "Car Repair",
  "category": "Category",
  "cleaning_services": "Cleaning Services",
  "compass_calibration": "Compass Calibration",
  "departure_board": "Departure Board",
  "design_services": "Design Services",
  "directions": "Directions",
  "directions_bike": "Directions Bike",
  "directions_boat": "Directions Boat",
  "directions_bus": "Directions Bus",
  "directions_car": "Directions Car",
  "directions_ferry": "Directions Ferry",
  "directions_railway": "Directions Railway",
  "directions_run": "Directions Run",
  "directions_subway": "Directions Subway",
  "directions_train": "Directions Train",
  "directions_transit": "Directions Transit",
  "directions_walk": "Directions Walk",
  "edit_attributes": "Edit Attributes",
  "edit_location": "Edit Location",
  "edit_location_alt": "Edit Location Alt",
  "edit_road": "Edit Road",
  "electric_bike": "Electric Bike",
  "electric_car": "Electric Car",
  "electric_moped": "Electric Moped",
  "electric_rickshaw": "Electric Rickshaw",
  "electric_scooter": "Electric Scooter",
  "electric_vehicle": "Electric Vehicle",
  "enhanced_encryption": "Enhanced Encryption",
  "ev_station": "EV Station",
  "fastfood": "Fastfood",
  "flight": "Flight",
  "handyman": "Handyman",
  "home_repair_service": "Home Repair Service",
  "hotel": "Hotel",
  "hvac": "HVAC",
  "layers": "Layers",
  "layers_clear": "Layers Clear",
  "local_activity": "Local Activity",
  "local_airport": "Local Airport",
  "local_atm": "Local ATM",
  "local_bar": "Local Bar",
  "local_cafe": "Local Cafe",
  "local_car_wash": "Local Car Wash",
  "local_convenience_store": "Local Convenience Store",
  "local_dining": "Local Dining",
  "local_drink": "Local Drink",
  "local_fire_department": "Local Fire Department",
  "local_florist": "Local Florist",
  "local_gas_station": "Local Gas Station",
  "local_grocery_store": "Local Grocery Store",
  "local_hospital": "Local Hospital",
  "local_hotel": "Local Hotel",
  "local_laundry_service": "Local Laundry Service",
  "local_library": "Local Library",
  "local_mall": "Local Mall",
  "local_movies": "Local Movies",
  "local_offer": "Local Offer",
  "local_parking": "Local Parking",
  "local_pharmacy": "Local Pharmacy",
  "local_phone": "Local Phone",
  "local_pizza": "Local Pizza",
  "local_play": "Local Play",
  "local_police": "Local Police",
  "local_post_office": "Local Post Office",
  "local_printshop": "Local Printshop",
  "local_see": "Local See",
  "local_shipping": "Local Shipping",
  "local_taxi": "Local Taxi",
  "male": "Male",
  "manage_accounts": "Manage Accounts",
  "maps_home_work": "Maps Home Work",
  "maps_ugc": "Maps UGC",
  "medical_services": "Medical Services",
  "moped": "Moped",
  "moving": "Moving",
  "multiple_stop": "Multiple Stop",
  "museum": "Museum",
  "my_location": "My Location",
  "navigation": "Navigation",
  "near_me": "Near Me",
  "not_listed_location": "Not Listed Location",
  "pest_control": "Pest Control",
  "pest_control_rodent": "Pest Control Rodent",
  "pin_drop": "Pin Drop",
  "place": "Place",
  "plumbing": "Plumbing",
  "precision_manufacturing": "Precision Manufacturing",
  "railway_alert": "Railway Alert",
  "ramen_dining": "Ramen Dining",
  "rate_review": "Rate Review",
  "restaurant": "Restaurant",
  "roofing": "Roofing",
  "room_preferences": "Room Preferences",
  "run_circle": "Run Circle",
  "sailing": "Sailing",
  "satellite": "Satellite",
  "save": "Save",
  "save_alt": "Save Alt",
  "saved_search": "Saved Search",
  "sell": "Sell",
  "set_meal": "Set Meal",
  "shape": "Shape",
  "share": "Share",
  "shortcut": "Shortcut",
  "snowmobile": "Snowmobile",
  "store_mall_directory": "Store Mall Directory",
  "streetview": "Streetview",
  "subway": "Subway",
  "terrain": "Terrain",
  "traffic": "Traffic",
  "train": "Train",
  "tram": "Tram",
  "transfer_within_a_station": "Transfer Within A Station",
  "transit_enterexit": "Transit Enterexit",
  "trip_origin": "Trip Origin",
  "two_wheeler": "Two Wheeler",
  "umbrella": "Umbrella",
  "verified": "Verified",
  "voice_over_off": "Voice Over Off",
  "washing_machine": "Washing Machine",
  "water_damage": "Water Damage",
  "wheelchair_pickup": "Wheelchair Pickup",
  "wifi": "WiFi",
  "workspaces_filled": "Workspaces Filled",
  "workspaces_outline": "Workspaces Outline",
  "wrong_location": "Wrong Location",
  "warning": "Warning"
      }
    mongoPanelColumnSelect = new Subject();

    isDataConverted = {};

    typeOptions = [
        { value: 1, label: 'Anomaly' },
        { value: 2, label: 'Data Quality' },
        { value: 3, label: 'SCADA' },
        { value: 4, label: 'Diagnostic' },
        { value: 5, label: 'Threshold' },
    ]

    subtypeOptions = [{ value: 1, label: 'Outlier' },
    { value: 2, label: 'Out of Range' },
    { value: 4, label: 'Flatline' },
    { value: 8, label: 'Missing' },
    { value: 10, label: 'Paint Brushing' }, //unique
    { value: 1, label: 'Trip/Shutdown/Breakdown' },
    { value: 2, label: 'Underperformance' },
    { value: 7, label: 'Curtailment' }, //unique
    { value: 8, label: 'Out of Environmental Scope' },
    { value: 4, label: 'Inverter Clipping' },
    { value: 5, label: 'SMB Down' }, //unique
    { value: 6, label: 'SMB Underperformance' }, //unique
    { value: 1, label: 'Low' },
    { value: 2, label: 'High' }]

    statusValueOptions = [
        { value: 1, label: 'Unacknowledged' },
        { value: 2, label: 'In Process' },
        { value: 3, label: 'On Watch' },
        { value: 4, label: 'Pending - Defect Raised' },
        { value: 6, label: 'Closed - No Action Planned' },
        { value: 7, label: 'Closed - Resolved' },
        { value: 8, label: 'Rejected' }
    ]

    severityValueOptions = [
        { value: 1, label: 'High' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'Low' },
    ]

    stateValueOptions = [
        { value: 1, label: 'Tentative' },
        { value: 2, label: 'Active' },
        { value: 3, label: 'Returned to Normal' },
        { value: 4, label: 'Closed' },
        { value: 5, label: 'Noise/Discarded' },
    ]
    mongoObjectTypes = new Set(['facility','unit','system','asset', 'tag', 'user', 'owner', 'subscribers', 'reviewers'])
    targetCollectionList = [
        {
            name: 'alerts',
            displayName: 'Alert',
            fields: [
                { name: 'state', displayName: 'State', operators: ['in', 'not in', '='], values: [...this.stateValueOptions], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'severityValue', displayName: 'Severity Value', operators: ['in', 'not in', '='], values: [...this.severityValueOptions], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'statusValue', displayName: 'Status Value', operators: ['in', 'not in', '='], values: [...this.statusValueOptions], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'type', displayName: 'Type', operators: ['in', 'not in', '='], values: [...this.typeOptions], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'totalMonetaryValue', displayName: 'Total Monetary Value', operators: ['<', '<=', '=', '>=', '>'], subTargetOperators: ['count', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'totalReliability', displayName: 'Total Reliability Saving', operators: ['<', '<=', '=', '>=', '>'], subTargetOperators: ['count', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'totalPerformance', displayName: 'Total Performance Saving', operators: ['<', '<=', '=', '>=', '>'], subTargetOperators: ['count', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'totalMaintenance', displayName: 'Total Maintenance Saving', operators: ['<', '<=', '=', '>=', '>'], subTargetOperators: ['count', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'system', displayName: 'System', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'unit', displayName: 'Unit', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'facility', displayName: 'Facility', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'owner', displayName: 'Owner', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'createdTime', displayName: 'Created On', isDateField: true, subTargetOperators: ['count', 'uniqueCount'], values: [], displayBy: ["year", "quarter", "month", "week", "date"], operators: ['<', '=', '>', 'between'], categorical: false, numerical: true, slicer: false },
                { name: 'confirmedTime', displayName: 'Confirm Date', isDateField: true, subTargetOperators: ['count', 'uniqueCount'], values: [], displayBy: ["year", "quarter", "month", "week", "date"], operators: ['<', '=', '>', 'between'], categorical: false, numerical: true, slicer: false },
                { name: 'currentTime', displayName: 'Current Date', isDateField: true, categorical: false, slicer: false },
                { name: 'statusValueDuration.1', displayName: 'Unacknowledge Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.2', displayName: 'In Process Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.3', displayName: 'On Watch Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.4', displayName: 'Pending - Defect Raised Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.6', displayName: 'Closed - No Action Planned Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.7', displayName: 'Closed - Resolved Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], values: [], isDateField: false, storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusValueDuration.8', displayName: 'Rejected Duration', subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], isDateField: false, values: [], storedUnit: "minutes", displayUnit: "hours", operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'alertId', displayName: 'Alert ID', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [], categorical: false, numerical: false, slicer: false },
                { name: 'category', displayName: 'Category', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: true },
                { name: 'component', displayName: 'Component', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: true },
                { name: 'subtype', displayName: 'Sub Type - Diagnostic Alerts', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [...this.subtypeOptions], categorical: true, numerical: false, slicer: false }, // complicated and requires more discussion
                { name: 'ranges.subtype', displayName: 'Sub Type - DQ/Threshold Alerts', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [...this.subtypeOptions], categorical: true, numerical: false, slicer: false },// complicated and requires more discussion
                { name: 'failures.mode', displayName: 'Failure Mode', graphTye:[20], isDateField: false, subTargetOperators: ['count'], operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: false, attributeOnly: true, prePopulateBaseFilter: [{ key: 'failures.display', operator: 'in', values: [true] as any, msg: 'Failure Mode Attribute depends on Failure Display filter' }, { key: 'type', operator: 'in', values: [1] as any, msg: 'Failure Mode Attribute depends on Type filter' }] }, // Attribute only - not a filter column; classify via Failure Approval Status
                { name: 'failures.approved', displayName: 'Failure Status', isDateField: false, subTargetOperators: ['count'], operators: ['in', 'not in', '='], values: [{ value: true, label: 'Approved' }, { value: false, label: 'Relearned' }], categorical: true, numerical: false, slicer: true, filterOnly: true, scopedToAttribute: 'failures.mode' }, // filter column only - not a selectable Attribute; visible only when Attribute = Failure Mode
                { name: 'failures.display', displayName: 'Is Failure Displayed', isDateField: false, subTargetOperators: ['count'], operators: ['in', 'not in', '='], values: [{ value: true, label: 'True' }, { value: false, label: 'False' }], categorical: true, numerical: false, slicer: false, filterOnly: true, scopedToAttribute: 'failures.mode' }, // filter column only - not a selectable Attribute; visible only when Attribute = Failure Mode; auto-applied as a locked base filter
                // { name: 'asset', displayName: 'Asset', isDateField: false, subTargetOperators: ['count', 'uniqueCount'], operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: true },
                { name: 'closedOn', subTargetOperators: ['count', 'uniqueCount'], displayBy: ["year", "quarter", "month", "week", "date"], prePopulateBaseFilter: [{ key: 'statusValue', operator: 'in', values: [6, 7, 8], msg: 'Closed On Attribute depends on Status Value filter' }], displayName: 'Closed On', isDateField: true, operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'statusStartTime', subTargetOperators: ['count', 'uniqueCount'], values: [], displayName: 'Status Start Time ', isDateField: true, operators: ['<', '=', '>', 'between'], categorical: false, numerical: false, slicer: false },
                { name: 'lastSystemUpdate', subTargetOperators: ['count', 'uniqueCount'], values: [], displayName: 'Last System Update', isDateField: true, operators: ['<', '=', '>', 'between'], categorical: false, numerical: true, slicer: false },
                { name: 'lastUserUpdate', subTargetOperators: ['count', 'uniqueCount'], values: [], displayName: 'Last User Update', isDateField: true, operators: ['<', '=', '>', 'between'], categorical: false, numerical: true, slicer: false },
                { name: 'lastUpdatedBy', displayName: 'Updated By', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                // { name: 'errorCode.Code', subTargetOperators: ['count', 'uniqueCount'], displayName: 'Event Code', isDateField: false, operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: false },
                // { name: 'errorCode.Description', subTargetOperators: ['count', 'uniqueCount'], displayName: 'Event Description', isDateField: false, operators: ['in', 'not in', '='], values: [], categorical: true, numerical: false, slicer: false },
                { name: 'loss', displayName: 'Loss', subTargetOperators: ['count', 'uniqueCount'], values: [], isDateField: false, operators: ['<', '<=', '=', '>=', '>'], categorical: false, numerical: true, slicer: false },
                { name: 'bookmark', subTargetOperators: ['count', 'uniqueCount'], displayName: 'Bookmarked', isDateField: false, operators: ['in', 'not in', '='], values: [{ value: true, label: "True" }, { value: false, label: 'False' }], categorical: true, numerical: false, slicer: true },
                { name: 'catch', subTargetOperators: ['count', 'uniqueCount'], displayName: 'Catch', isDateField: false, operators: ['in', 'not in', '='], values: [{ value: true, label: "True" }, { value: false, label: 'False' }], categorical: true, numerical: false, slicer: true },
                { name: 'tag', subTargetOperators: ['count', 'uniqueCount'], displayName: 'Tag', isDateField: false, operators: ['in', 'not in', '='], values: [], categorical: true, slicer: false },
            ],
            groupBy: [
                { name: 'state', displayName: 'State'},
                { name: 'severityValue', displayName: 'Severity'},
                { name: 'statusValue', displayName: 'Status'},
                { name: 'type', displayName: 'Alert Type'},
                { name: 'system', displayName: 'System'},
                { name: 'unit', displayName: 'Unit'},
                { name: 'facility', displayName: 'Facility' },
                { name: 'owner', displayName: 'Owner'},
                { name: 'createdTime', displayName: 'Created On', isDateField: true, category: ["year", "quarter", "month", "week", "date"]},
            ]
        },
        {
            name: 'sapnotifications',
            displayName: 'Maintenance',
            fields: [
                { name: 'WarehouseCost', displayName: 'Warehouse Cost', operators: ['<', '<=', '=', '>=', '>'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: true, slicer: false },
                { name: 'InternalLabourCost', displayName: 'Internal Labour Cost', operators: ['<', '<=', '=', '>=', '>'], values: [], subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'ExternalLabourCost', displayName: 'External Labour Cost', operators: ['<', '<=', '=', '>=', '>'], values: [], subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'ExternalServiceCost', displayName: 'External Service Cost', operators: ['<', '<=', '=', '>=', '>'], values: [], subTargetOperators: ['count', 'uniqueCount', 'sum', 'avg'], categorical: false, numerical: true, slicer: false },
                { name: 'CreatedOn', displayName: 'Created On', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'ChangedOn', displayName: 'Modified On', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'CompletedOn', displayName: 'Completed On', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'Notifdate', displayName: 'Notification Date', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'MalfunctStart', displayName: 'Malfunction Start Date', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'MalfunctEnd', displayName: 'Malfunction End Date', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'REQUIRED_START_DATETIME', displayName: 'REQUIRED START DATE & TIME', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: true, slicer: false },
                { name: ' REQUIRED_END_DATETIME', displayName: ' REQUIRED END DATE & TIME', isDateField: true, operators: ['<', '=', '>', 'between'], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: true, slicer: false },
                { name: 'FunctionalLoc', displayName: 'FunctionalLoc', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'unit', displayName: 'Unit', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'system', displayName: 'System', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'facility', displayName: 'Facility', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'alert', displayName: 'Alert', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'user', displayName: 'User', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'failure', displayName: 'Failure', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'dataPopulated', displayName: 'Data Populated', isDateField: false, operators: ['in', 'not in', '='], values: [{ value: true, label: "True" }, { value: false, label: 'False' }], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'NotificationNo', displayName: 'Notification No', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'Equipment', displayName: 'Equipment', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'BreakdownDuration', displayName: 'Breakdown Duration', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count'], categorical: false, numerical: true, slicer: false },
                { name: 'NotificationType', displayName: 'Notification Type', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                // { name: 'Description', displayName: 'Description', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true  },
                { name: 'Priority', displayName: 'Priority', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'CreatedByName', displayName: 'Created By', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'ChangedBy', displayName: 'Changed By', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'Order', displayName: 'Order', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'Material', displayName: 'Material', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'ObjectNo', displayName: 'Object No', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: false, numerical: false, slicer: false },
                { name: 'Status', displayName: 'Status', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'UserStatus', displayName: 'User Status', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'MaintenancePlant', displayName: 'Maintenance Plant', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'PlanGroup', displayName: 'Plan Group', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'CompletedByName', displayName: 'Completed By', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'Flocaffected', displayName: 'Floc Affected', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
                { name: 'PlantSection', displayName: 'Plant Section', isDateField: false, operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: true },
            ],
            groupBy: [
                { name: 'system', displayName: 'System' },
                { name: 'facility', displayName: 'Facility' },
            ]
        },
        {
            name: 'tags',
            displayName: 'Tag',
            fields: [
                { name: 'calculated', displayName: 'Calculated', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name: 'type', displayName: 'Type', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name: 'isclientTag', displayName: 'Is Client Tag', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name: 'system', displayName: 'System', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name: 'facility', displayName: 'Facility', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name: 'tagId', displayName: 'Tag ID', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name:'_id', displayName: 'Tag', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
                { name:'modelling', displayName: 'Used In Model', operators: ['in', 'not in', '='], values: [], subTargetOperators: ['count', 'uniqueCount'], categorical: true, numerical: false, slicer: false },
            ],
            groupBy: [
                { name: 'calculated', displayName: 'Calculated'},
                { name: 'type', displayName: 'Type'},
                { name: 'isclientTag', displayName: 'Is Client Tag'},
                { name: 'system', displayName: 'System'},
                { name: 'facility', displayName: 'Facility'},
            ]
        },
    ]

    clearConvertedDataMapForWidget(id) {
        for (const key in this.isDataConverted) {
            if (key.startsWith(`${id}`)) {
                delete this.isDataConverted[key];
            }
        }
    }

    /**
     * @param {string} name - form control name
     * @param {string} displayName - Title of the table name
     * @param {string} localField - name of the field in the collection data object
     * @param {string} collectionName - name of the collection to which the field belongs
     * @param {string} foreignField - name of the field which we need, in the collection to which the field belongs
     * @param {string} dataObjectKey - name of the field in the collection data object when object is flattened for table
     * @param {boolean} slicer - true if the field is to be included in slicer
     * @param {boolean} includeInDrilldown - true if the field is to be included in drilldown
     * @param {boolean} isDateField - true if the field is a date field
     * @param {boolean} resolveName - true if the field is to be resolved to its name in the collection
     * @param {string} dependsOn - name of the field on which this field depends
     */
    drilldownOptions = {
        alerts: [
            { name: 'alertId', displayName: 'Alert ID', includeInDrilldown: true },
            { name: 'type', displayName: 'Alert Type', includeInDrilldown: true, resolveName: true }, // enum
            { name: 'subtype', displayName: 'Alert Sub Type', includeInDrilldown: true, resolveName: true, dependsOn: 'type' }, // enum
            { name: 'category', displayName: 'Alert Category', includeInDrilldown: true, resolveName: true }, // enum
            { name: 'component', displayName: 'Alert Component', includeInDrilldown: true },
            { name: 'tagId', localField: 'tag', collectionName: 'tags', foreignField: 'tagId', displayName: 'Tag Id', dataObjectKey: 'tag_tagId', includeInDrilldown: true },
            { name: 'tag', localField: 'tag', collectionName: 'tags', foreignField: 'name', displayName: 'Tag Name', dataObjectKey: 'tag_name', includeInDrilldown: true },
            { name: 'tagDescription', localField: 'tag', collectionName: 'tags', foreignField: 'description', displayName: 'Tag Description', dataObjectKey: 'tag_description', includeInDrilldown: true },
            { name: 'facility', localField: 'facility', collectionName: 'facilities', foreignField: 'name', displayName: 'Facility', dataObjectKey: 'facility_name', includeInDrilldown: true },
            { name: 'unit', localField: 'unit', collectionName: 'units', foreignField: 'name', displayName: 'Unit', dataObjectKey: 'unit_name', includeInDrilldown: true },
            { name: 'system', localField: 'system', collectionName: 'systems', foreignField: 'name', displayName: 'System', dataObjectKey: 'system_name', includeInDrilldown: true },
            { name: 'asset', localField: 'asset', collectionName: 'assets', foreignField: 'name', displayName: 'Asset', dataObjectKey: 'asset_name', includeInDrilldown: true },
            { name: 'statusValue', displayName: 'Status', includeInDrilldown: true, resolveName: true }, // enum
            { name: 'state', displayName: 'State', includeInDrilldown: true, resolveName: true }, // enum
            { name: 'severityValue', displayName: 'Severity', includeInDrilldown: true, resolveName: true }, // enum
            { name: 'owner', localField: 'owner', collectionName: 'users', foreignField: 'name', displayName: 'Alert Owner', dataObjectKey: 'owner_name', includeInDrilldown: true },
            { name: 'alertWatcher', displayName: 'Alert Watcher', includeInDrilldown: false }, // multiple watchers are possible? how to accomodate?
            { name: 'lastUpdatedBy', localField: 'lastUpdatedBy', collectionName: 'users', foreignField: 'name', dataObjectKey: 'lastUpdatedBy_name', displayName: 'Updated By', includeInDrilldown: true },
            { name: 'totalFMs', displayName: 'Total FMs', includeInDrilldown: false }, //Accepted + Relearned + No Action
            { name: 'approvedFMs', displayName: 'Approved FMs', includeInDrilldown: false }, // Accepted Failure
            { name: 'relearnedFMs', displayName: 'Relearned FMs', includeInDrilldown: false }, // Relearned Failures
            { name: 'totalMonetaryValue', displayName: 'Total Value', includeInDrilldown: true }, // non categorical value
            { name: 'createdTime', displayName: 'Created On', includeInDrilldown: true, isDateField: true }, // need more clarity and is not simple
            { name: 'statusStartTime', displayName: 'Closed On', includeInDrilldown: false, isDateField: true }, // need more clarity not simple
            { name: 'statusValueDuration.1', displayName: 'Unacknowledged Duration', dataObjectKey: 'statusValueDuration_1', includeInDrilldown: true },
            { name: 'statusValueDuration.2', displayName: 'In Process Duration', dataObjectKey: 'statusValueDuration_2', includeInDrilldown: true },
            { name: 'statusValueDuration.3', displayName: 'On Watch Duration', dataObjectKey: 'statusValueDuration_3', includeInDrilldown: true },
            { name: 'statusValueDuration.4', displayName: 'Pending Defect Raised Duration', dataObjectKey: 'statusValueDuration_4', includeInDrilldown: true },
            { name: 'statusValueDuration.7', displayName: 'Closed Resolved Duration', dataObjectKey: 'statusValueDuration_7', includeInDrilldown: true },
            { name: 'statusValueDuration.6', displayName: 'Closed NAP Duration', dataObjectKey: 'statusValueDuration_6', includeInDrilldown: true },
            { name: 'statusValueDuration.8', displayName: 'Rejected Duration', dataObjectKey: 'statusValueDuration_8', includeInDrilldown: true },
            { name: 'lastSystemUpdate', displayName: 'Last System Update', includeInDrilldown: true, isDateField: true },
            { name: 'lastUserUpdate', displayName: 'Last User Update', includeInDrilldown: true, isDateField: true },
            { name: 'criticality', displayName: 'Criticality', includeInDrilldown: false }, // projected field
            { name: 'thresholdDirection', displayName: 'Threshold Direction', includeInDrilldown: false },
            { name: 'eventCode', displayName: 'Event Code', includeInDrilldown: false }, // is this error code?
            { name: 'eventDescription', displayName: 'Event Description', includeInDrilldown: false }, // is this error code description
            { name: 'scadaDeviceId', displayName: 'SCADA Device ID', includeInDrilldown: false }, // projected field
            { name: 'loss', displayName: 'LOSS', includeInDrilldown: true }, // non categorical field
            { name: 'bookmark', displayName: 'Bookmarked', includeInDrilldown: true },
            { name: 'catch', displayName: 'Catch', includeInDrilldown: true }
        ],
        sapnotifications: [
            { name: 'NotificationNo', displayName: 'Notification No', includeInDrilldown: true },
            { name: 'Notifdate', displayName: 'Notification Date', isDateField: true, includeInDrilldown: true },
            { name: 'FunctionalLocDesc', displayName: 'Functional Location', includeInDrilldown: true },
            { name: 'EquipmentDesc', displayName: 'Equipment', includeInDrilldown: true },
            { name: 'BreakdownDuration', displayName: 'Breakdown Duration', includeInDrilldown: true },
            { name: 'Effect', displayName: 'Effect', includeInDrilldown: true },
            { name: 'Description', displayName: 'Description', includeInDrilldown: true },
            { name: 'Priority', displayName: 'Priority', includeInDrilldown: true },
            { name: 'NotificationType', displayName: 'Notification Type', includeInDrilldown: true },
            { name: 'Status', displayName: 'Status', includeInDrilldown: true },
            { name: 'UserStatus', displayName: 'User Status', includeInDrilldown: true },
            { name: 'CreatedByName', displayName: 'Created By', includeInDrilldown: true },
            { name: 'ChangedBy', displayName: 'Changed By', includeInDrilldown: true },
            { name: 'CompletedByName', displayName: 'Completed By', includeInDrilldown: true },
            { name: 'CreatedOn', displayName: 'Created On', isDateField: true, includeInDrilldown: true },
            { name: 'ChangedOn', displayName: 'Changed On', isDateField: true, includeInDrilldown: true },
            { name: 'CompletedOn', displayName: 'Completed On', isDateField: true, includeInDrilldown: true },
            { name: 'MalfunctStart', displayName: 'Malfunction Start Date/Time', isDateField: true, includeInDrilldown: true },
            { name: 'MalfunctEnd', displayName: 'Malfunction End Date/Time', isDateField: true, includeInDrilldown: true },
            { name: 'REQUIRED_START_DATETIME', displayName: 'Required Start Date/Time', isDateField: true, includeInDrilldown: true },
            { name: 'REQUIRED_END_DATETIME', displayName: 'Required End Date/Time', isDateField: true, includeInDrilldown: true },
            { name: 'InternalLabourCost', displayName: 'Internal Labour Cost', includeInDrilldown: true },
            { name: 'ExternalLabourCost', displayName: 'External Labour Cost', includeInDrilldown: true },
            { name: 'ExternalServiceCost', displayName: 'External Service Cost', includeInDrilldown: true },
            { name: 'WarehouseCost', displayName: 'Warehouse Cost', includeInDrilldown: true },
            { name: 'DamageDesc', displayName: 'Damage', includeInDrilldown: true },
            { name: 'Loss', displayName: 'Loss', includeInDrilldown: true },
            { name: 'dataPopulated', displayName: 'Data Populated', includeInDrilldown: true },
            { name: 'unit', localField: 'unit', collectionName: 'units', foreignField: 'name', displayName: 'Unit', dataObjectKey: 'unit_name', includeInDrilldown: true },
            { name: 'system', localField: 'system', collectionName: 'systems', foreignField: 'name', displayName: 'System', dataObjectKey: 'system_name', includeInDrilldown: true },
            { name: 'facility', localField: 'facility', collectionName: 'facilities', foreignField: 'name', displayName: 'Facility', dataObjectKey: 'facility_name', includeInDrilldown: true },
            { name: 'alert', localField: 'alert', collectionName: 'alerts', foreignField: 'alertId', displayName: 'Alert', dataObjectKey: 'alert_alertId', includeInDrilldown: true },
            { name: 'user', localField: 'user', collectionName: 'users', foreignField: 'name', displayName: 'User', dataObjectKey: 'user_name', includeInDrilldown: true },
            { name: 'failure', localField: 'failure', collectionName: 'failures', foreignField: 'name', displayName: 'Failure', dataObjectKey: 'failure_name', includeInDrilldown: true },
            { name: 'Order', displayName: 'Order', includeInDrilldown: true },
            { name: 'Material', displayName: 'Material', includeInDrilldown: true },
            { name: 'ObjectNo', displayName: 'Object No', includeInDrilldown: true },
            { name: 'MaintenancePlantDesc', displayName: 'Maintenance Plant', includeInDrilldown: true },
            { name: 'PlanGroupDesc', displayName: 'Plan Group', includeInDrilldown: true },
            { name: 'Flocaffected', displayName: 'Floc Affected', includeInDrilldown: true },
            { name: 'PlantSection', displayName: 'Plant Section', includeInDrilldown: true },
        ]
    };

    private getDisplayValueForDependsOn(dependsOn: string, dependsOnValue: any, value: any): string {
        const config = {
          type: {
            2: {
              1: 'Outlier',
              2: 'Out of Range',
              4: 'Flatline',
              8: 'Missing',
              10: 'Paint Brushing',
            },
            4: {
              1: 'Trip/Shutdown/Breakdown',
              2: 'Underperformance',
              7: 'Curtailment',
              8: 'Out of Environmental Scope',
              4: 'Inverter Clipping',
              5: 'SMB Down',
              6: 'SMB Underperformance',
            },
            5: {
              1: 'Low',
              2: 'High',
            },
          },
        };

        // Check if the dependsOn field exists in the configuration
        if (config[dependsOn] && config[dependsOn][dependsOnValue]) {
          return config[dependsOn][dependsOnValue][value] || 'NA';
        }

        // Fallback to the raw value if no mapping is found
        return value || 'NA';
      }


    getFieldDistinctValues(collectionName, field) {
        let p = {
            collectionName,
            fields: [field]
        };
        if (field === 'tag' || field === 'tagId') {
            return this.getTagList().pipe(
                switchMap(() => this.widgetDashboardService.getFieldDistinctValues(p))
            );
        }
        return this.widgetDashboardService.getFieldDistinctValues(p);
    }

    validateGroupOperatorForCorrectValues(): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const check = group.get('check')?.value;
            const values = group.get('values')?.value;

            // Validation logic
            if (['in', 'not in'].includes(check) && !Array.isArray(values)) {
                return { invalidOperatorValues: `Values must be an array for operator ${check}` };
            }
            if (!['in', 'not in'].includes(check) && Array.isArray(values)) {
                return { invalidOperatorValues: `Values must not be an array for operator ${check}` };
            }
            return null; // Return null if valid
        };
    }

    isMongoId(id: string) {
        const regex = /^[0-9a-fA-F]{24}$/;
        return regex.test(id);
      }

      getFacilityName(id: string) {
        return this.facilityService.facilityList.find(fac => fac._id === id)?.name;
      }

      getUnitName(id: string) {
        return this.unitService.unitList.find(unit => unit._id === id)?.name;
      }

      getSystemName(id: string) {
        return this.systemService.systemList.find(system => system._id === id)?.name;
      }

      getUserName(id: string) {
        return this.userService.userList.find(user => user._id === id)?.name;
      }

      getAssetName(id: string) {
        return this.completeAssetList.find(asset => asset._id === id)?.name;
      }

      getTagName(id: string) {
        return this.completeTagList?.find(tag => tag._id === id)?.name;
      }

      getName(id: string) {
        if (this.isMongoId(id))
          return this.getFacilityName(id) || this.getUnitName(id) || this.getSystemName(id) || this.getUserName(id) || this.getAssetName(id) || this.getTagName(id) || 'Other' ;
        return "Other";
      }

      resolveGroupNames(data) {
        let newData = {};
        for (let key in data) {
          newData[key] = data[key].map(i => {
              i.group = { name: this.getName(i.group), id: i.group }
            return i;
          })
        }
        return newData;
      }


    formatExtraInputsData(data, graphType, objToArray) {
        let res;
        if (objToArray) {
          res = Object.keys(data).map(k => {
            let configObj = this.widgetDashboardService.chartMetaData[graphType]?.extraInputs.filter(c => c.key == k)
            configObj = configObj?.length ? configObj[0] : null
            if (data[k]?._id) {
                return { ...configObj, 'tag': data[k] }
            }
            return { ...configObj, 'value': data[k] }
          })
        } else {
          //array to object
          res = {}
          data.forEach(obj => {
            res[obj.key] = obj.value
          })
        }
        return res
      }

      insertEmptyDataMsgInPlotly(graphOptions, msg='') {
        graphOptions.data = []
        graphOptions.layout = {
          "xaxis": {
            "visible": false
          },
          "yaxis": {
            "visible": false
          },
          "annotations": [
            {
                  "text": msg ? msg : "No matching data found",
              "xref": "paper",
              "yref": "paper",
              "showarrow": false,
              "font": {
                ...FONT_FAMILY,
                "size": 16
              }
            }
          ],
        }
        return graphOptions;
      }
    testFormula(formula, numOfTagsSelected) {
        if (formula.includes('_')) {
            return { 'invalidSyntax': true };
        }
        let testFormulaExpression = []
        for (let fr of formula) {
            let ch = fr.toUpperCase(), asci = ch.charCodeAt(0)
            if ((/[a-zA-Z]/).test(ch)) {
                if (asci - 64 > numOfTagsSelected) {
                    //i.e ch is lies between A-Z
                    //Invalid var means, let's say numOfTagsSelected = 3. Then allowed variables in formula are A, B, C.
                    //if any other alphabates other then A, B, C is found, throw invalid expression error
                    return { 'invalidFormula': true }
                }
                testFormulaExpression.push(1)
            } else {
                testFormulaExpression.push(ch)
            }

        }
        let testFormulaExpressionStr = testFormulaExpression.join("")
        try {
            evaluate(testFormulaExpressionStr)
            return null
        } catch (e) {
            return { 'invalidSyntax': true }
        }
    }

    formulaValidator = (control: AbstractControl): { [key: string]: boolean } | null => {
        let formula = control.value // (A+B)*C/D
        let numOfTagsSelected = control.parent?.value?.tag?.length
        return this.testFormula(formula, numOfTagsSelected)
    }

    prepareTreeMapData(treeMetaData, type, colors, decimalPlaces) {
        colors.unshift('rgba(0,0,0,0)');
        let dataObject: any;
        let ids = treeMetaData.map((md) => md.id);
        let labels = treeMetaData.map((md) => md.title);
        let text = treeMetaData.map((md) => md.title);
        let values = treeMetaData.map((md) => {
            let processedValue;
            if (!md.value) {
                processedValue = 0;
            } else if (md.value < 0) {
                processedValue = -1 * md.value;
            } else {
                processedValue = md.value;
            }

            return processedValue;
        });
        let parentValue = values?.[0]?.toFixed(decimalPlaces)
        let max = Math.max(...values)
        values = values.map(val => {
            return val == max ? (((parseFloat(val) / max))) : (((parseFloat(val) / max) * 100))
        })
        let customdataList = treeMetaData.map(md => {
            if (md.id == "0") return ''
            return `${this._getValue(md.value, decimalPlaces)}`
        })

        let parentIds = treeMetaData.map((md) => {
            return this.getParentId(md.id);
        })

        dataObject = [
            {
                type: type,
                textposition: "bottom left",
                labels: labels,
                ids: ids,
                customdata: customdataList,
                parents: parentIds,
                text: text,
                values: values,
                parentValue: parentValue,
                texttemplate: "%{customdata}<br><b>%{text}</b>",
                hovertemplate: "%{customdata}<br><b>%{text}</b>",
                tiling: { pad: 6 },
                hovelabel: { bgcolor: "white" },
                pathbar: { "visible": false },
                maxdepth: type == 'treemap' ? 2 : 3,
                outsidetextfont: { "size": 37, "color": "#fff" },
                marker: {
                    "line": { "width": 1 },
                    colors: type == 'treemap' ? colors : []
                },
                name: "",
                insidetextorientation: 'radial',
                extendsunburstcolorway: true
            }
        ]
        return dataObject;
    }

    getParentId(id) {
        let parent = id.split(".")
        parent.pop()
        parent = parent.join(".")

        return parent;
    }

    _getValue(value, decimalPlaces) {
        let v = value == null || value == undefined || value == '' ? "NA" : value?.toFixed(decimalPlaces)
        return `<span style="font-weight: 400">${v}</span>`
    }
    refreshCustomFormulaComponent(data) {
        this.customFormulaComponentRefresher.next(data)
    }

    // Modifying graph layout

    setEchartOptions(widget, echartOptions, selectedGraphId) {
        echartOptions.legend["show"] = widget.graphOptions.miscSettings.showlegend;
        delete echartOptions.legend.left;
        delete echartOptions.legend.right;
        echartOptions.legend.itemGap = 10;
        echartOptions.legend.itemHeight = 4;
        echartOptions.legend.itemWidth = 4;
        echartOptions.legend.formatter = {};
        echartOptions.legend.textStyle = {
            color: '#252525',
            fontSize: 10,
        };
        echartOptions.legend = { ...echartOptions.legend, ...this.widgetDashboardService.getLegendPositioneCharts(widget.legend.position, widget.legend.orientation) }
        if (selectedGraphId === 4) {
            if (!widget.hole) {
                echartOptions.series[0]["padAngle"] = 0;
                echartOptions.series[0].itemStyle = {
                    borderRadius: 0,
                }
            }
            if (widget.hole) {
                if (widget.hole === 0.85) {
                    echartOptions.series[0]["padAngle"] = 3;
                    echartOptions.series[0].itemStyle = {
                        borderRadius: 10,
                    }
                } else {
                    echartOptions.series[0]["padAngle"] = 0;
                    echartOptions.series[0].itemStyle = {
                        borderRadius: 5,
                    }
                }
            }
            if (!widget.graphOptions.miscSettings.overlapLegends && echartOptions?.legend?.show) {
                echartOptions.series[0].center = this.getPieChartPosition(widget.legend);
                let hole = `${widget.hole * 80}%`
                echartOptions.series[0].radius = [hole, '80%'];
            }
            if (widget.graphOptions.miscSettings.overlapLegends || !echartOptions?.legend?.show) {
                echartOptions.series[0].center = ['50%', '50%'];
                let hole = `${widget.hole * 95}%`
                echartOptions.series[0].radius = [hole, '95%'];
            }
            if (widget.graphOptions.miscSettings?.semiPieChart) {
                echartOptions.series[0]["startAngle"] = 180
                echartOptions.series[0]["endAngle"] = 360
                let hole = `${widget.hole * 100}%`
                echartOptions.series[0].radius = [hole, '100%'];
                if (echartOptions?.legend?.show) {
                    echartOptions.series[0].center = this.getSemiPieChartPosition(widget.legend);
                }
                else echartOptions.series[0].center = ['50%', '70%'];
            }
            if (!widget.graphOptions.miscSettings?.semiPieChart) {
                echartOptions.series[0]["startAngle"] = 90
                echartOptions.series[0]["endAngle"] = 450
            }
            if (widget.graphOptions.miscSettings?.legendType === 'b') {
                this.setGraphOptionsforSidePieChart(echartOptions, widget)
            }
        }
        return echartOptions;
    }
    setGraphOptionsforSidePieChart(echartOptions, widget) {
        delete echartOptions.legend.left;
        delete echartOptions.legend.right;
        echartOptions.legend = { ...echartOptions.legend, ...this.legendOptionsforSidePieChart(echartOptions, widget) };
        if (widget.graphOptions.miscSettings?.semiPieChart) {
            echartOptions.series[0].center = !this.dynamicPiechartWidth || this.dynamicPiechartWidth > 400 && this.dynamicPiechartWidth > 1.6 * this.dynamicPiechartHeight ? ['25%', '65%'] : ['45%', '65%'];
        } else {
            echartOptions.series[0].center = !this.dynamicPiechartWidth || this.dynamicPiechartWidth > 400 && this.dynamicPiechartWidth > 1.6 * this.dynamicPiechartHeight ? ['25%', '50%'] : ['45%', '50%'];
        }
        let hole = `${widget.hole * 80}%`
        echartOptions.series[0].radius = [hole, '80%'];
    }

    legendOptionsforSidePieChart(echartOptions, widget) {
        if (echartOptions.series[0].data.length === 0) return {};
        let maxWidth = 0;
        let data = echartOptions.series[0].data;
        let total = 0;
        let valueObj = {}
        data.forEach(function (item) {
            total += parseFloat(item.value);
            maxWidth = Math.max(maxWidth, item.name.length);
            valueObj[item.name] = parseFloat(item.value);
        });
        if (!widget.graphOptions.miscSettings.overlapLegends) {
            maxWidth = Math.min(maxWidth, 35);
        }
        return {
            orient: 'vertical',
            top: 'middle',
            right: '3%',
            itemGap: 13,
            formatter: function (name) {
                var percent = ((valueObj[name] / total) * 100).toFixed(2);
                if (name.length > 35 && !widget.graphOptions.miscSettings.overlapLegends) {
                    name = name.slice(0, 35) + ' ...';
                }
                return ` {name|${name}}    {percentage|${percent} %}`;
            },
            textStyle: {
                rich: {
                    name: {
                        padding: [3, 0, 0, 0],
                        width: maxWidth * 6,
                        fontSize: 10,
                        color: '#252525',
                        fontWeight: 500
                    },
                    percentage: {
                        padding: [3, 0, 0, 0],
                        fontSize: 10,
                        color: '#615E83',
                        fontWeight: 400
                    }
                }
            },
            tooltip: {
                show: !widget.graphOptions.miscSettings.overlapLegends,
                borderRadius: 2,
                textStyle: {
                    color: '#fff',
                    fontSize: 10,
                },
            }
        }
    }
    setGraphLayout(widget, graphOptions, selectedGraphId, hovermode = false) {
        if (widget?.orientation == 'h' && [13, 14, 16].includes(selectedGraphId)) {
            graphOptions.layout['xaxis']["ticksuffix"] = ` ${widget?.axis?.yaxis?.unit || ''}`
            graphOptions.layout['yaxis']["showticklabels"] = widget?.axis?.xaxis?.showLabel;
            delete graphOptions.layout['yaxis']["ticksuffix"]
            delete graphOptions.layout['xaxis']["showticklabels"]
            graphOptions.layout['yaxis']['type'] = 'category'
            graphOptions.layout['xaxis']['type'] = widget?.graphOptions?.miscSettings?.axisType
            graphOptions.layout['yaxis']['tickangle'] = -45
            graphOptions.layout['yaxis']['automargin'] = true;
            graphOptions = this.widgetDashboardService.setAxisRange(widget,'x',graphOptions)
        }
        else if (widget?.orientation == 'v' && [13, 14, 16].includes(selectedGraphId)) {
            graphOptions.layout['yaxis']["ticksuffix"] = ` ${widget?.axis?.yaxis?.unit || ''}`
            graphOptions.layout['xaxis']["showticklabels"] = widget?.axis?.xaxis?.showLabel;
            delete graphOptions.layout['xaxis']["ticksuffix"]
            delete graphOptions.layout['yaxis']["showticklabels"]
            graphOptions.layout['xaxis']['type'] = 'category'
            graphOptions.layout['yaxis']['type'] = widget?.graphOptions?.miscSettings?.axisType
            graphOptions = this.widgetDashboardService.setAxisRange(widget,'y',graphOptions)
        }
        if (hovermode && !this.widgetDashboardService.isTimeSeriesGraph(selectedGraphId)) {
            graphOptions.layout['hovermode'] = 'closest'
        }
        if (this.widgetDashboardService.isTimeSeriesGraph(selectedGraphId) || selectedGraphId == 15) {
            graphOptions.layout['yaxis']['type'] = widget?.graphOptions?.miscSettings?.axisType;
        }
        if (selectedGraphId == 6 || selectedGraphId == 18) {
            graphOptions.layout['yaxis']['type'] = widget?.graphOptions?.miscSettings?.axisType;
            graphOptions.layout['xaxis']['type'] = widget?.graphOptions?.miscSettings?.axisType;
            if(selectedGraphId == 18) graphOptions.layout['xaxis']["ticksuffix"] = ` ${widget?.axis?.xaxis?.unit || ''}`
        }
        if (![13, 14, 16].includes(selectedGraphId)) {
            graphOptions.layout['yaxis']["ticksuffix"] = ` ${widget?.axis?.yaxis?.unit || ''}`
            graphOptions = this.widgetDashboardService.setAxisRange(widget,'y',graphOptions)
        }
        if (selectedGraphId === 15) {
            graphOptions.layout['xaxis']['type'] = 'category'
        }
        if(![16,18,19].includes(selectedGraphId)) {
            graphOptions.layout['hoverlabel'] = {
                bgcolor: '#1E1B39',
                bordercolor: '#1E1B39',
                font: {
                    size: 14,
                    color: '#FFFFFF'
                }
            }
        }
        graphOptions.layout['autosize'] = true
        graphOptions.layout['xaxis']["showticklabels"] = widget?.axis?.xaxis?.showLabel;
        if (widget.graphOptions.miscSettings?.showlegend != false) {
            graphOptions.layout["showlegend"] = true
            graphOptions.layout["legend"] = {
                ...graphOptions.layout["legend"],
                ...this.widgetDashboardService.legendPosition(widget?.legend?.position, widget?.graphOptions?.miscSettings?.overlapLegends, widget?.legend?.orientation),
                ...{ orientation: widget?.legend?.orientation ? widget?.legend?.orientation : 'h' }
            }
        } else {
            graphOptions.layout["showlegend"] = false
        }
        return graphOptions;
    }
    // Waterfall chart data preparation start
    createWaterfallOption(valueDirection, widget) {
        let options = {
            type: 'waterfall',
            name: "",
            textposition: "auto",
            showlegend: false,
            x: [],
            y: [],
            measure: [],
            orientation: widget.orientation,
            text: [],
            hovertemplate: widget?.orientation == 'h' ? `%{y}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>` : `%{x}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`,
            texttemplate: '%{text}',
            customdata: [],
            totals: {
                marker: {
                    color: "#7200FF"
                }
            },
            base: 0,
            increasing: { marker: { color: "#00B16E" } },
            decreasing: { marker: { color: "#DF1E5A" } },

        }

        if (valueDirection == 2) {//reverse
            options.increasing = { marker: { color: "#db4437" } };
            options.decreasing = { marker: { color: "#0f9d58" } };
        }
        return options;
    }
    relativeMeasureWaterFall(widget, options, total, minTotal, maxTotal, element) {
        if (widget?.orientation == 'h') {
            options.x.push(((element?.a) * -1)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
        }
        else {
            options.y.push(((element?.a) * -1)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
        }
        options.text.push(((element?.a) * -1)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
        total += ((element?.a) * -1)
        minTotal = Math.min(minTotal, total)
        maxTotal = Math.max(maxTotal, total)

        return { options, maxTotal, minTotal, total }
    }
    otherMeasureWaterfall(widget, options, total, minTotal, maxTotal, element, measure) {
        if (widget?.orientation == 'h') {
            options.x.push((element?.a)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces));
        }
        else {
            options.y.push((element?.a)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces));
        }
        if (measure?.value == 'total') {
            options.text.push(total?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
        }
        else {
            options.text.push((element?.a)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
            total += element?.a
            maxTotal = Math.max(maxTotal, total)
            minTotal = Math.min(minTotal, total)
        }

        return { options, maxTotal, minTotal, total }
    }

    waterFallData(data, widget, graphOptions, type = "prod") {
        let total = 0;
        let t = 0
        let valueDirection = widget.valueDirection ? widget.valueDirection : 1
        let color = [];
        let minTotal;
        let maxTotal;
        let options = this.createWaterfallOption(valueDirection, widget)
        let i = 0;
        for (const d in data) {
            let base = 0
            let element = type == 'prod' ? data[d][0] : data[d];
            if (element?.a && widget?.panelDataNew[i]?.label) {
                if (i == 0) {
                    base = parseFloat(element?.a) || 0
                    minTotal = base
                    maxTotal = base
                }
                t++
                let measure = widget?.panelDataNew[i]?.extraInputs?.find(extra => extra.key == 'measure')
                if (widget?.orientation == 'h') {
                    options.y.push(widget?.panelDataNew[i]?.label)
                }
                else {
                    options.x.push(widget?.panelDataNew[i]?.label)
                }
                if (valueDirection == 2 && i > 0 && measure?.value == 'relative') {

                    ({ options, maxTotal, minTotal, total } = this.relativeMeasureWaterFall(widget, options, total, minTotal, maxTotal, element))
                }
                else {
                    ({ options, maxTotal, minTotal, total } = this.otherMeasureWaterfall(widget, options, total, minTotal, maxTotal, element, measure))
                }
                options.measure.push(measure?.value);
                options.customdata.push(moment(new Date(element.t)).format('MMM DD, YYYY, hh:mm:ss'))
            }
            i++;
        }
        minTotal = minTotal * 0.95
        maxTotal = maxTotal + maxTotal * 0.05
        options['marker'] = { color: color }
        if(!widget.graphOptions.miscSettings?.range?.min && !widget.graphOptions.miscSettings?.range?.max) {
            if(widget?.orientation == 'h') {
                graphOptions.layout.xaxis['range'] = [minTotal, maxTotal]
                graphOptions.layout['xaxis']['autorange'] = false
              delete graphOptions.layout.yaxis['range']
            } else {
              graphOptions.layout.yaxis['range'] = [minTotal, maxTotal]
              graphOptions.layout['yaxis']['autorange'] = false
              delete graphOptions.layout.xaxis['range']
            }
          }
        graphOptions.data.push(options)
        return { graphOptions, isData: t != 0 }
    }
    // waterfall chart data preparation end
    // Demo Charts Handling
    // Demo Non Time Series Bar Chart
    BarChartDemo(data, widget, graphOptions, colors) {
        let groupFor = widget.groupFor
        let t = 0;
        let pivot = groupFor == 'unit' ? 'label' : 'unit';
        let graphData = []
        if (!groupFor) {
            let trace = {
                orientation: widget.orientation,
                x: [], y: [], text: [],
                textposition: 'auto',
                hovertemplate: widget?.orientation == 'h' ? `%{y}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>` : `%{x}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`,
                type: 'bar',
                marker: {
                    color: colors[0]
                }
            }
            data.forEach(d => {
                if (d.value) {
                    trace['text'].push(d['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    if (widget.orientation == 'h') {
                        trace['y'].push(d.label);
                        trace['x'].push(d['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    else {
                        trace['x'].push(d['label'])
                        trace['y'].push(d['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    t++;
                }
            })
            trace.marker.color = colors.slice(0, trace.x.length);
            graphData.push(trace);
        }
        else {
            let groupGraphdata = this.handleGroupedBarDemo(data, widget, pivot, colors);
            graphData = groupGraphdata.graphData;
            t = groupGraphdata.isData;
        }
        graphOptions.data = graphData;
        graphOptions.layout.barmode = widget.graphOptions.miscSettings.barmode;
        return { graphOptions, isData: t != 0 }
    }
    handleGroupedBarDemo(data, widget, pivot, colors) {
        let graphData = [], t = 0;
        let groupFor = widget.groupFor;
        let groupedData = _.groupBy(data, d => d[pivot]);
        Object.keys(groupedData).forEach((grp, i) => {
            let trace = {
                name: grp.toUpperCase(), orientation: widget.orientation, x: [], y: [], text: [],
                hovertemplate: widget?.orientation == 'h' ? `%{y}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>` : `%{x}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`,
                type: 'bar'
            }
            groupedData[grp].forEach(elem => {
                if (elem.value) {
                    trace['text'].push(elem['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    if (widget.orientation == 'h') {
                        trace['y'].push(elem[groupFor])
                        trace['x'].push(elem['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    else {
                        trace['x'].push(elem[groupFor])
                        trace['y'].push(elem['value']?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    trace['marker'] = {
                        color: colors[i]
                    }
                    t++
                }
            })
            graphData.push(trace)
        })
        return { graphData, isData: t }
    }
    // survay chart data preparation start
    demoSurveyChartHandler(panelDataNew) {
        let data = {
            customValue: []
        };

        panelDataNew.forEach(pd => {
            data.customValue.push({
                legend: 'Actual',
                label: pd.label,
                value: pd.customValue
            });

            pd.extraInputs.forEach(ei => {
                if (ei?.key === 'dv_value') {
                    if (!data[ei.key]) {
                        data[ei.key] = [];
                    }

                    data[ei.key].push({
                        legend: ei.label,
                        key: ei.key,
                        label: pd.label,
                        value: ei.value
                    });
                }
            });
        });
        return data;
    }
    getLineStyle(graphId: number, extraKey) {
        let { extraInputs } = this.widgetDashboardService.chartMetaData[graphId];
        let target = extraInputs.find(inp => inp.key == extraKey)
        return target?.line;
    }
    survayChartData(graphOptions, processedData, selectedGraphId, decimalPlaces) {
        let t = 0
        for (const key in processedData) {
            let data = processedData[key];
            let options = {
                type: 'line+markers',
                name: data[0]?.legend,
                line: this.getLineStyle(selectedGraphId, key),
                x: [],
                y: [],
                marker: {
                    color: 'white',
                    size: 14,
                    symbol: 'circle',
                    line: {
                        width: 2,
                        color: key === 'dv_value' ? '#C996FF' : '#2E60FF'
                    },
                },
            }
            options['hovertemplate'] = `(%{x},%{y: .${decimalPlaces}f})<extra></extra>`
            data.forEach(element => {
                if (element?.value || element?.value === 0) {
                    options.x.push(element?.label)
                    options.y.push(parseFloat(element?.value)?.toFixed(decimalPlaces))
                    t++;
                }
            })
            graphOptions.data.push(options)
        }
        return { graphOptions, isData: t != 0 }
    }

    checkMongoIdEnabled(system) {
        return system?.enableTagMongoIdFormat == true;
    }

    // prepare panel Data

    preparePanelData(widget, selectedGraphId, timeRangeObj, systemNameForQuickPlot) {
        let panelData = []
        let row = widget?.panelDataNew[0]
        widget?.panelDataNew.map(pData => {
            if (pData.tag?.length) {
                let isTimeSeriesGraph = this.widgetDashboardService.isTimeSeriesGraph(selectedGraphId)
                let filter = (selectedGraphId === 18) || (!isTimeSeriesGraph && pData?.calcType === 1) || (isTimeSeriesGraph && pData?.calcType === 9) ? {} : pData?.filterOptions[0]
                pData.tag = pData?.tag.map(tag => ({ ...tag, filterOption: filter || {} }));
                let data: any = {};
                if (!this.widgetDashboardService.isTimeSeriesGraph(widget.graphOptions?.graphType) && ![18,16].includes(widget.graphOptions?.graphType) && pData.calcType != 1) {
                    let aggregateWindow = timeRangeObj.window
                    let aggregateWindowUnit = timeRangeObj.unit
                    if ((!pData?.hasOwnProperty('enableAggregateWindow') && pData?.aggregateWindow && pData?.aggregateWindowUnit) || (pData?.enableAggregateWindow && pData?.aggregateWindow)) {
                        aggregateWindow = pData?.aggregateWindow
                        aggregateWindowUnit = pData?.aggregateWindowUnit
                    }
                    data = {
                        "calcType": pData.calcType,
                        windowSize: pData.windowSize,
                        "enableAggregateWindow": pData?.enableAggregateWindow,
                        "aggregateWindow": aggregateWindow,
                        "aggregateWindowUnit": aggregateWindowUnit,
                        "raw": pData.tag[0]?.calculated ? 0 : 1,
                        "_id": pData.tag[0]?._id,
                        "tagId": this.checkMongoIdEnabled(pData.tag[0]?.system[0]) ? pData.tag[0]?._id : pData.tag[0]?.tagId,
                        "tsTable": this.widgetDashboardService.getTsTable(pData.tag[0]?.system, pData.tag[0]?.calculated),
                        "tags": this.checkMongoIdEnabled(pData.tag[0]?.system[0]) ? pData?.tag.map(tag => ({ ...tag, tagId: tag._id })) : pData?.tag,
                        "formula": pData?.customFormula,
                        "id": pData?.id,
                        "miscTags": []
                    }
                } else {
                    data = {
                        windowSize: pData.windowSize,
                        "calcType": pData.calcType,
                        "enableAggregateWindow": pData?.enableAggregateWindow,
                        "aggregateWindow": pData?.aggregateWindow,
                        "aggregateWindowUnit": pData?.aggregateWindowUnit,
                        "raw": pData.tag[0]?.calculated ? 0 : 1,
                        "_id": pData.tag[0]?._id,
                        "tagId": this.checkMongoIdEnabled(pData.tag[0]?.system[0]) ? pData.tag[0]?._id : pData.tag[0]?.tagId,
                        "tsTable": this.widgetDashboardService.getTsTable(pData.tag[0]?.system, pData.tag[0]?.calculated),
                        "tags": this.checkMongoIdEnabled(pData.tag[0]?.system[0]) ? pData?.tag.map(tag => ({ ...tag, tagId: tag._id })) : pData?.tag,
                        "formula": pData?.customFormula,
                        "id": pData?.id,
                        "miscTags": []
                    }
                }
                if (pData.calcType == 9) {
                    data["lessThan"] = pData?.lessThan;
                    data["greaterThan"] = pData?.greaterThan;
                }
                if (widget.graphOptions?.graphType == 18 && pData.yTag) {
                    data.miscTags.push({
                        valueAs: "y",
                        "raw": pData.yTag?.calculated ? 0 : 1,
                        "_id": pData.yTag?._id,
                        "tsTable": this.widgetDashboardService.getTsTable(pData.yTag?.system, pData.yTag?.calculated),
                        "tagId": this.checkMongoIdEnabled(pData.yTag?.system[0]) ? pData.yTag?._id : pData.yTag?.tagId
                    })
                }
                if (widget.graphOptions?.graphType == 18 && pData.zTag) {
                    data.miscTags.push({
                        valueAs: "z",
                        "raw": pData.zTag?.calculated ? 0 : 1,
                        "_id": pData.zTag?._id,
                        "tsTable": this.widgetDashboardService.getTsTable(pData.zTag?.system, pData.zTag?.calculated),
                        "tagId": this.checkMongoIdEnabled(pData.zTag?.system[0]) ? pData.zTag?._id : pData.zTag?.tagId
                    })
                }
                if (widget.graphOptions?.graphType == 15 && pData.extraInputs[0]?.tag) {
                    data["miscTags"].push({
                        "raw": pData.extraInputs[0]?.tag?.calculated ? 0 : 1,
                        "valueAs": 'calctag',
                        "_id": pData.extraInputs[0]?.tag?.value?._id,
                        "tsTable": this.widgetDashboardService.getTsTable(pData.extraInputs[0]?.tag?.system, pData.extraInputs[0]?.tag?.calculated),
                        "tagId": this.checkMongoIdEnabled(pData.extraInputs[0]?.tag?.system[0]) ? pData.extraInputs[0]?.tag?._id : pData.extraInputs[0]?.tag?.tagId
                    })
                }
                if (widget?.graphOptions?.miscSettings?.quickPlot) {
                    if (row.calcType == 9) {
                        data["lessThan"] = row?.lessThan;
                        data["greaterThan"] = row?.greaterThan;
                    }
                    let filterOption = (selectedGraphId === 18) || (!isTimeSeriesGraph && row?.calcType === 1) || (isTimeSeriesGraph && row?.calcType === 9) ? {} : row?.filterOptions[0]
                    if (filterOption?.tag) {
                        filterOption['tag'] = pData.tag[0]?._id
                    }
                    data["windowSize"] = row.windowSize
                    data["calcType"] = row.calcType
                    data["enableAggregateWindow"] = row.enableAggregateWindow
                    data["aggregateWindow"] = row.aggregateWindow
                    data["aggregateWindowUnit"] = row.aggregateWindowUnit
                    data["tags"] = pData?.tag.map(tag => ({ ...tag, filterOption: filterOption || {} }));
                }
                panelData.push(data)
                systemNameForQuickPlot[pData?.id] = this.widgetDashboardService.getSystemName(pData.tag[0]?.system)
            }
        })
        return { panelData, systemNameForQuickPlot };
    }

    prepareTreeConfigData(treeConfig, treeNodeDetailsMap, treeNodeIdToVal) {
        let treeMetaData = treeConfig.length ? prepareTreeMetaData(treeConfig[0], [], ['graphdata', 'value']) : []
        treeMetaData.forEach(md => {
            treeNodeDetailsMap[md.id] = { tagId: this.checkMongoIdEnabled(md.tag?.system[0]) ? md.tag._id : md.tagId, title: md.title }
            treeNodeIdToVal[md.id] = ''
        })
        treeMetaData = treeMetaData.filter(data => !data?.useChildrenVal);
        treeMetaData.forEach(item => {
            let filter = item?.calcType === 1 ? {} : item?.filterOptions
            if(this.checkMongoIdEnabled(item.tag?.system[0])){
                item.tagId = item.tag._id;
                item.tag.tagId = item.tag._id;
            }
            item['tags'] = [item.tag]
            if (item.tags[0]) {
                item.tags[0]['filterOption'] = filter || {};
            }
            delete item.tag
            delete item.filterOptions;
        });
        return { treeMetaData, treeNodeDetailsMap, treeNodeIdToVal }
    }
    // scatter chart data preparation start
    scatterChart(data, graphOptions, widget) {
        let colors = this.widgetDashboardService.colors;
        let panelDataNew = widget.panelDataNew;
        let t = 0
        let customCurves = widget.customCurves;
        panelDataNew.forEach((pData, index) => {
            let trace1 = {
                x: [],
                y: [],
                name: pData?.label,
                hovertemplate: 'X:Tag1: %{x}<br>Y:Tag2: %{y}<br>Z:Tag3: %{text.z}<br>Date: %{customdata}<extra></extra>',
                mode: 'markers',
                type: 'scatter',
                text: [],
                marker: {
                    color: colors[index]
                }
            };
            let trace2 = {
                name: pData?.label,
                mode: 'markers',
                type: 'scatter',
                marker: {
                    size: 15,
                    symbol: 'star',
                    color: colors[index],
                    line: {
                        width: 2,
                        color: 'red'
                    }
                },
                showlegend: false,
            }
            let p = 0;
            if (pData.tag?.length > 0) {
                let tempData = data[pData?.id];
                tempData?.forEach(td => {
                    if (td.a) {
                        p++;
                        trace1.x.push(td.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                        trace1.y.push((td.y)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                        trace1.text.push({
                            z: td.z?.toFixed(widget.graphOptions.miscSettings.decimalPlaces),
                            date: moment(new Date(td?.t)).format('MMM DD, YYYY, hh:mm:ss')
                        })
                        trace2['x'] = [(td.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))]
                        trace2['y'] = [(td.y)?.toFixed(widget.graphOptions.miscSettings.decimalPlaces)]
                        trace2['text'] = [(moment(new Date(td?.t)).format('MMM DD, YYYY, hh:mm:ss'))]
                    }
                })
            }
            if (p != 0) {
                trace1.hovertemplate = `X: ${pData?.tag[0]?.description}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<br>Y: ${pData?.yTag?.description}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}${pData?.zTag?.description ? '<br>Z: '+pData?.zTag?.description+`: %{text.z: .${widget.graphOptions.miscSettings.decimalPlaces}f}`:''}<br>Date: %{text.date}<extra></extra>`
                trace2['hovertemplate'] = `X: ${pData?.tag[0]?.description}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<br>Y: ${pData?.yTag?.description}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<br>Date: %{text}<extra></extra>`
                t++;
                graphOptions.data.push(trace1,trace2)
            }
        })
        if (customCurves) {
            graphOptions.data = [...graphOptions.data, ...customCurves]
        }
        return { graphOptions, isData: t != 0 }
    }

    // scatter chart data preparation end
    // handleTreeData
    handleTreeData(resDataKeys, resData, treeNodeIdToVal, treeConfig) {
        resDataKeys.forEach(nodeId => {
            let nodeVal = resData[nodeId]
            let length = nodeVal?.length
            treeNodeIdToVal[nodeId] = length ? nodeVal[length - 1].a : ''
        })
        treeNodeIdToVal = evaluateCustomFormulaNodes(treeConfig, { ...treeNodeIdToVal })
        return treeNodeIdToVal
    }
    // treeMap and sunburst data preparation start
    treeMapOrSunburstData(widget, treeNodeIdToVal, graphOptions, graphType, colors) {
        graphOptions.layout["sunburstcolorway"] = [...colors];
        let treeToList = treeToArray(widget.treeConfig[0])
        treeToList.forEach(node => {
            node["value"] = treeNodeIdToVal[node.id]
        })
        graphOptions.data = this.prepareTreeMapData(treeToList, graphType, colors, widget.graphOptions.miscSettings.decimalPlaces)[0]
        let i = 0;
        graphOptions.data?.values?.forEach(val => {
            if (val) {
                i++;
            }
        })
        return { graphOptions, isData: i != 0 }
    }

    // Survay chart data handling
    survayChartDataHandler(resData, widget) {
        let panelDataNew = widget.panelDataNew;
        let data = {};

        panelDataNew.forEach(pd => {
            data["value"] = data["value"] || [];
            data["value"].push({
                legend: 'Actual',
                label: pd.label,
                value: resData[pd.id]?.[0]?.a // Simplified accessing 'a'
            });

            pd.extraInputs.forEach(ei => {
                data[ei?.key] = data[ei?.key] || [];
                data[ei?.key].push({
                    legend: ei.label,
                    key: ei.key,
                    label: pd.label,
                    value: resData[pd.id]?.[0]?.calctag ?? ei.value
                });
            });
        });
        return data;
    }

    // HeatMap Chart Data Preparation

    heatMapChartData(data, widget, graphOptions, systemNameForQuickPlot) {
        let panelDataNew = widget.panelDataNew
        let t = 0
        let resDataKeys = Object.keys(data)
        graphOptions.data = [{}]
        let labelForQuickPlot = ''
        graphOptions.data[0]['type'] = 'heatmap'
        graphOptions.data[0]['hoverongaps'] = false
        graphOptions.data[0]['y'] = []
        graphOptions.data[0]['x'] = []
        graphOptions.data[0]['z'] = []
        resDataKeys.forEach((id, index) => {
            let name;
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                if (index == 0) {
                    labelForQuickPlot = this.widgetDashboardService.getLabel(panelDataNew, id)
                }
                name = `${systemNameForQuickPlot[id]}_${labelForQuickPlot}`
            }
            else {
                name = this.widgetDashboardService.getLabel(panelDataNew, id)
            }
            graphOptions.data[0]['y'].push(name)
        })
        let uniqueTime = new Set()
        resDataKeys.forEach((tagId, j) => {
            if (data[tagId]?.length) {
                t++;
            }
            let a = []
            data[tagId].forEach((t, i) => {
                let time = new Date(t.t)
                const timestamp = time.getTime();
                if(!uniqueTime.has(timestamp)) {
                    uniqueTime.add(timestamp);
                    graphOptions.data[0]['x'].push(time)
                }
                a.push(data[resDataKeys[j]][i]?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
            });
            graphOptions.data[0]['z'].push(a)
        })
        graphOptions.data[0]['hovertemplate'] = `x:%{x}<br>y:%{y}<br>z:%{z: .${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`
        graphOptions.layout = JSON.parse(JSON.stringify(heatmapLayout))
        return { graphOptions, isData: (t != 0 && this.isQuickPlotValid(widget, labelForQuickPlot)) }
    }

    isQuickPlotValid(widget, labelForQuickPlot) {
        //This function is written to know whether quick plot is valid or not.
        return !!(!widget.graphOptions?.miscSettings?.quickPlot || labelForQuickPlot);
    }

    // Bar Chart Data preparation starts here
    createTraceForBarChart(widget, hasGroup) {
        let hovertemplate;
        if (hasGroup) {
            hovertemplate = widget?.orientation == 'h' ? `%{fullData.name}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>'` : `'%{fullData.name}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`
        }
        else {
            hovertemplate = widget?.orientation == 'h' ? `%{y}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>` : `%{x}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`
        }
        return {
            x: [], y: [], text: [],
            orientation: widget.orientation,
            textposition: 'auto',
            hovertemplate: hovertemplate,
            type: 'bar',
            showlegend: !hasGroup ? false : true,
            marker: {}
        }
    }
    handleNonGroupBarData(data, widget, systemNameForQuickPlot) {
        let labelForQuickPlot = "", t = 0;
        let objectKeys = Object.keys(data)
        let trace = this.createTraceForBarChart(widget, false)
        objectKeys.forEach((obj, index) => {
            let objData = data[obj]
            if (objData?.length) {
                let element = objData[0]
                let name = '';
                if (widget.graphOptions?.miscSettings?.quickPlot) {
                    if (index == 0) {
                        labelForQuickPlot = this.widgetDashboardService.getLabel(widget.panelDataNew, obj)
                    }
                    if (labelForQuickPlot) {
                        name = `${systemNameForQuickPlot[obj]}_${labelForQuickPlot}`
                    }
                }
                else {
                    name = this.widgetDashboardService.getLabel(widget.panelDataNew, obj)
                }
                if (element.a && name) {
                    t++
                    trace['text'].push(element?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    if (widget.orientation == 'h') {
                        trace['y'].push(name)
                        trace['x'].push(element?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    else {
                        trace['x'].push(name)
                        trace['y'].push(element?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                }
            }
        })
        return { trace, isData: t }
    }
    handleGroupBarData(widget, groupedData, data, units, hasDuplicateUnits, colors) {
        let groupFor = widget.groupFor, t = 0;
        let pivot = groupFor == 'unit' ? 'label' : 'unit';
        let graphData = []
        Object.keys(groupedData).forEach((grp, i) => {
            let val;
            if (!hasDuplicateUnits) {
                val = pivot == 'label' ? grp : units.find(u => u._id == grp)?.name
            }
            let trace = {
                name: hasDuplicateUnits ? grp : val,
                x: [], y: [], text: [],
                hovertemplate: widget?.orientation == 'h' ? `%{fullData.name}: %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>` : `%{fullData.name}: %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}<extra></extra>`,
                orientation: widget.orientation,
                textposition: 'auto',
                type: 'bar'
            }
            groupedData[grp].forEach(elem => {
                if (elem.tag && data[elem.id] && data[elem.id][0] && data[elem.id][0].a) {
                    trace['text'].push(data[elem.id][0].a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    if (widget.orientation == 'h') {
                        trace['y'].push(groupFor == 'label' ? elem[groupFor] : units.find(u => u._id == elem[groupFor])?.name)
                        trace['x'].push(data[elem.id][0].a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    else {
                        trace['x'].push(groupFor == 'label' ? elem[groupFor] : units.find(u => u._id == elem[groupFor])?.name)
                        trace['y'].push(data[elem.id][0].a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    }
                    trace['marker'] = {
                        color: colors[i]
                    }
                    t++
                }
            })
            graphData.push(trace)
        })
        return { graphData, isData: t }
    }
    createGroupForBarChart(panelDataNew, units, pivot, hasDuplicateUnits) {
        let groupedData;
        if (hasDuplicateUnits) {
            if (pivot == 'label') {
                groupedData = panelDataNew.reduce((result, item) => {
                    const key = `${item.label}_${item.tag[0].description}`;
                    result[key] = result[key] || [];
                    result[key].push(item);
                    return result;
                }, {});
            } else {
                groupedData = panelDataNew.reduce((result, item) => {
                    let name = units.find(u => u._id == item.unit[0])?.name
                    const key = `${name}_${item.tag[0].description}`;
                    result[key] = result[key] || [];
                    result[key].push(item);
                    return result;
                }, {});
            }
        } else {
            groupedData = _.groupBy(panelDataNew, d => pivot == 'label' ? d[pivot] : d[pivot][0]);
        }
        return groupedData;
    }

    hasDuplicateUnits(panelDataNew) {
        const unitSet = new Set();
        let hasDuplicateUnits = false;
        for (const item of panelDataNew) {
            const unit = `${item.label}_${item.unit[0]}`;
            if (unitSet.has(unit)) {
                hasDuplicateUnits = true;
                break;
            }
            unitSet.add(unit);
        }
        return hasDuplicateUnits
    }
    BarChart(widget, data, colors, units, graphOptions, systemNameForQuickPlot) {
        let groupFor = widget.groupFor, barmode = widget?.graphOptions?.miscSettings?.barmode
        widget.panelDataNew = widget.panelDataNew.filter(item => (item.tag?.length && item.unit?.length))
        let t = 0
        let graphData = []
        if (!groupFor) {
            let { trace, isData } = this.handleNonGroupBarData(data, widget, systemNameForQuickPlot)
            trace.marker["color"] = colors.slice(0, trace.x.length);
            t = isData
            graphData.push(trace)
        } else {
            let pivot = groupFor == 'unit' ? 'label' : 'unit';
            let name = ''
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                widget.panelDataNew.forEach((element, i) => {
                    if (i == 0) {
                        name = element.label
                    }
                    element.label = `${systemNameForQuickPlot[element?.id]}_${name}`
                });
            }
            let hasDuplicateUnits = this.hasDuplicateUnits(widget.panelDataNew)
            let groupedData = this.createGroupForBarChart(widget.panelDataNew, units, pivot, hasDuplicateUnits);
            let groupGraphdata = this.handleGroupBarData(widget, groupedData, data, units, hasDuplicateUnits, colors);
            graphData = groupGraphdata.graphData;
            t = (this.isQuickPlotValid(widget, name)) ? groupGraphdata.isData : 0;
            graphOptions.layout.barmode = barmode;
        }
        graphOptions.data = graphData;
        return { graphOptions, isData: t != 0 }
    }

    BarChartEcharts(widget, data, colors, units, echartOptions, systemNameForQuickPlot) {
        let groupFor = widget.groupFor, barmode = widget?.graphOptions?.miscSettings?.barmode
        widget.panelDataNew = widget.panelDataNew.filter(item => (item.tag?.length && item.unit?.length))
        let t = 0
        if (!groupFor) {
            let { echartOptions: chartOption, isData } = this.handleNonGroupBarDataECharts(data, widget, systemNameForQuickPlot)
            t = isData;
            (chartOption.series[0] as any).itemStyle = {
                color: (params: any) => colors[params.dataIndex % colors.length],
                borderRadius: widget.orientation === 'h' ? [0, 5, 5, 0] : [5, 5, 0, 0]
            };
            echartOptions = { ...chartOption };
        } else {
            let pivot = groupFor == 'unit' ? 'label' : 'unit';
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                let name = ''
                widget.panelDataNew.forEach((element, i) => {
                    if (i == 0) {
                        name = element.label
                    }
                    element.label = `${systemNameForQuickPlot[element?.id]}_${name}`
                });
            }
            let hasDuplicateUnits = this.hasDuplicateUnits(widget.panelDataNew)
            let groupedData = this.createGroupForBarChart(widget.panelDataNew, units, pivot, hasDuplicateUnits);
            let groupGraphData = this.handleGroupBarDataECharts(widget, groupedData, data, units, hasDuplicateUnits, colors);
            echartOptions = groupGraphData.option;
            t = groupGraphData.isData;
            // Apply colors to the grouped bars
            echartOptions.series.forEach((seriesItem: any, idx: number) => {
                (seriesItem as any).itemStyle = {
                    color: colors[idx % colors.length],
                    borderRadius: widget.orientation === 'h' ? [0, 5, 5, 0] : [5, 5, 0, 0]
                };
            });
            if (echartOptions.series[0])
                echartOptions.series[0].barCategoryGap = '20%';
        }

        echartOptions.xAxis.axisLabel = {
            rotate: 30,
            interval: 0
        };

        echartOptions.yAxis.axisLabel = {
            rotate: 30,
            // interval: 0,
        }

        echartOptions.legend = this.getLegendConfig(widget?.legend?.orientation, widget?.legend?.position)
        echartOptions.legend.show = widget?.graphOptions?.miscSettings?.showlegend
        echartOptions.legend.overlapLegend = widget?.graphOptions?.miscSettings?.overlapLegends
        const legendName = echartOptions.series.map(ele => ele.name) || [];
        function calculateLegendWidth(legendName: string[], font: string = "11px Arial"): number {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            context.font = font;
            let maxLegendWidth = 0;
            for (const legend of legendName) {
                const measuredWidth = context.measureText(legend?.length > 25 ? legend.substring(0, 25) : legend)?.width;
                maxLegendWidth = Math.max(maxLegendWidth, measuredWidth);
            }
            return maxLegendWidth + 60;
        }
        const width = calculateLegendWidth(legendName);
        echartOptions.grid = {
            left: (widget?.legend?.position === 1 || widget?.legend?.position === 3) && (widget?.legend?.orientation == 'v') && widget?.groupFor && widget?.graphOptions?.miscSettings?.showlegend && !widget?.graphOptions?.miscSettings?.overlapLegends ? `${width}px` : 25,
            right: (widget?.legend?.position === 2 || widget?.legend?.position === 4) && (widget?.legend?.orientation == 'v') && widget?.groupFor && widget?.graphOptions?.miscSettings?.showlegend && !widget?.graphOptions?.miscSettings?.overlapLegends ? `${width}px` : 25,
            top: (widget?.legend?.position === 1 || widget?.legend?.position === 2) && (widget?.legend?.orientation == 'h') && widget?.groupFor && widget?.graphOptions?.miscSettings?.showlegend && !widget?.graphOptions?.miscSettings?.overlapLegends ? 30 : 15,
            bottom: (widget?.legend?.position === 3 || widget?.legend?.position === 4) && (widget?.legend?.orientation == 'h') && widget?.groupFor && widget?.graphOptions?.miscSettings?.showlegend && !widget?.graphOptions?.miscSettings?.overlapLegends ? 30 : 15,
            containLabel: true
        };


        const isHorizontal = widget.orientation === 'h';
        const axisKey = isHorizontal ? 'yAxis' : 'xAxis';
        const valueAxisKey = isHorizontal ? 'xAxis' : 'yAxis';

        // Axis showing category
        echartOptions[axisKey].axisLabel = {
            ...echartOptions[axisKey]?.axisLabel,
            rotate: 30,
            interval: 0,
            show: widget.axis.xaxis.showLabel
        };

        // Axis showing value
        const range = widget.graphOptions?.miscSettings?.range || {};
        const valueAxis: any = {
            ...echartOptions[valueAxisKey],
            type: widget.graphOptions.miscSettings.axisType === 'log' ? 'log' : 'value',
            axisLabel: {
                rotate: 30,
                formatter: (val) => `${val} ${widget.axis.yaxis.unit}`
            }
        };
        if (range.min) valueAxis.min = range.min;
        if (range.max) valueAxis.max = range.max;
        echartOptions[valueAxisKey] = valueAxis;

        return { echartOptions, isData: t !== 0 };
    }

    handleNonGroupBarDataECharts(data, widget, systemNameForQuickPlot) {
        let labelForQuickPlot = "", t = 0;
        let objectKeys = Object.keys(data);
        let seriesData = [];
        let xAxisData = [];

        objectKeys.forEach((obj, index) => {
            let objData = data[obj];
            if (objData?.length) {
                let element = objData[0];
                let name = '';
                if (widget.graphOptions?.miscSettings?.quickPlot) {
                    if (index == 0) {
                        labelForQuickPlot = this.widgetDashboardService.getLabel(widget.panelDataNew, obj);
                    }
                    if (labelForQuickPlot) {
                        name = `${systemNameForQuickPlot[obj]}_${labelForQuickPlot}`;
                    }
                } else {
                    name = this.widgetDashboardService.getLabel(widget.panelDataNew, obj);
                }

                if (element.a && name) {
                    t++;

                    if (widget.orientation == 'h') {
                        xAxisData.push(element?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces));
                        seriesData.push(name);
                    } else {
                        xAxisData.push(name);
                        seriesData.push(element?.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces));
                    }
                }
            }
        });

        let series = {
            type: 'bar',
            data: widget.orientation == 'h' ? xAxisData : seriesData,
            barWidth: 'auto',
            label: {
                show: true,
                position: 'inside',
                formatter: '{c}'
            }
        };

        let echartOptions = {
            xAxis: {
                type: widget.orientation == 'h' ? 'value' : 'category',
                data: xAxisData,
            },
            yAxis: {
                type: widget.orientation == 'h' ? 'category' : 'value',
                data: seriesData,
            },
            series: [series],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            }
        };

        return { echartOptions, isData: t };
    }

    handleGroupBarDataECharts(widget, groupedData, data, units, hasDuplicateUnits, colors) {
        let groupFor = widget.groupFor, t = 0;
        let barmode = widget?.graphOptions?.miscSettings?.barmode;
        let pivot = groupFor == 'unit' ? 'label' : 'unit';
        let series = [];
        let xAxisData = new Set();

        Object.keys(groupedData).forEach((grp, i) => {
            let val;
            if (!hasDuplicateUnits) {
                val = pivot == 'label' ? grp : units.find(u => u._id == grp)?.name;
            }

            // Create a series for each group
            let seriesItem = {
                name: hasDuplicateUnits ? grp : val,
                type: 'bar',
                stack: '',
                data: [],
                barWidth: 'auto',
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (params) => widget.orientation === 'h' ? params.value[0] : params.value[1]
                }
            };
            let isTagHasData = false
            groupedData[grp].forEach(elem => {
                if (elem.tag && data[elem.id] && data[elem.id][0] && data[elem.id][0].a) {
                    isTagHasData = true
                    let xValue = groupFor === 'label' ? elem[groupFor] : units.find(u => u._id == elem[groupFor])?.name;
                    let yValue = data[elem.id][0].a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces);

                    seriesItem.stack = barmode == 'stack' ? xValue : seriesItem.name
                    let d;
                    if (widget.orientation === 'h') {
                        d = { value: [yValue, xValue] }
                    } else {
                        d = { value: [xValue, yValue] }
                    }
                    seriesItem.data.push(d)
                    xAxisData.add(xValue);
                    t++;
                }
            });
            if (isTagHasData)
                series.push(seriesItem);
        });

        let xAxis = widget.orientation === 'h' ? {
            type: 'value'
        } : {
            type: 'category',
            data: Array.from(xAxisData)
        };

        let yAxis = widget.orientation === 'h' ? {
            type: 'category',
            data: Array.from(xAxisData)
        } : {
            type: 'value'
        };

        let option = {
            textStyle: { fontFamily: FONT_FAMILY.family },
            xAxis: xAxis,
            yAxis: yAxis,
            series: series.length ? series : [{ data: [] }],
            barGap: '0%',
            barCategoryGap: '20%',
            confine: true,
            tooltip: {
                trigger: 'item',
                position: 'inside',
                formatter: function (params) {
                    let seriesName = params.seriesName;
                    return `${seriesName}: ${widget.orientation === 'h' ? params.value[0] : params.value[1]}`;
                }
            }
        };

        return { option, isData: t };
    }

    BarChartM(widget, data, colors, graphOptions) {
        let groupFor = widget.groupFor, barmode = widget?.graphOptions?.miscSettings?.barmode, groupBy = widget?.mongoPanelData[0]?.groupBy, tableMode = widget?.graphOptions?.miscSettings?.tableMode
        let t = 0
        let graphData = []

        let collectionConfig = this.targetCollectionList.find(i => i.name === widget?.mongoPanelData[0]?.targetCollection )
        let groupConfig = collectionConfig?.groupBy.find(i => i.name === groupBy)
        groupBy = groupConfig?.displayName
        if (!groupFor) {
            let { trace, isData } = this.handleMongoData(data, widget)
            trace.marker["color"] = colors.slice(0, trace.x.length);
            t = isData
            graphData.push(trace)
        } else {
            let groupGraphdata = this.handleMongoGroupBarData(data, widget, colors);
            graphData = groupGraphdata.graphData;
            t = groupGraphdata.isData;
            graphOptions.layout.barmode = barmode;
        }
        if (tableMode && graphData.length) {
            let values = []
            let columnNames = []
            let firstColumnValues = widget?.orientation == 'h' ? [...graphData[0].y] : [...graphData[0].x]
            firstColumnValues = firstColumnValues.map(value => "<b>" + value + '</b>')
            values.push(firstColumnValues)
            columnNames = [['<b>' + groupBy?.toUpperCase() + '</b>'], ...graphData.map(i => ['<b>' + i.name?.toUpperCase() + '</b>'])]
            graphData.forEach(i => {
                values.push([...i.text])
            })
            t = values.length;

            graphData = [{
                type: 'table',
                header: {
                    values: columnNames,
                    align: ["center"],
                    line: { width: 0.5, color: '#D8DCE6' },
                    fill: { color: 'white' },
                    height: 45,
                    font: { family: FONT_FAMILY.family, size: 16, color: "#b5b5c3" }
                },
                cells: {
                    values: values,
                    align: ["left", "center"],
                    line: { color: "#D8DCE6", width: 0.5 },
                    fill: { color: ['white'] },
                    height: 40,
                    font: { family: FONT_FAMILY.family, size: 14, color: "#3f4254" }
                }
            }]
        }
        graphOptions.data = graphData;
        return { graphOptions, isData: t != 0 }
    }

    handleMongoGroupBarData(data: any, widget: any, colors: any) {
        let groupFor = widget.groupFor;
        let barmode = widget?.graphOptions?.miscSettings?.barmode;
        let isData = 0;
        let graphData = [];
        const processedData = {};

        for (let key in data) {
            data[key].forEach(item => {
                const group = item.group;
                if (!processedData[group]) {
                    processedData[group] = { total: 0, groupForValues: {} };
                }
                processedData[group].total += item.a;

                const valuesToGroup = item[groupFor];
                for (const value of valuesToGroup) {
                    if (!processedData[group].groupForValues[value]) {
                        processedData[group].groupForValues[value] = 0;
                    }
                    processedData[group].groupForValues[value] += 1;
                }
            })
        }

        const uniqueValues = new Set();
        const xValues = Object.keys(processedData);
        const traces = [];

        for (const group of xValues) {
            const groupData = processedData[group];
            for (const value in groupData.groupForValues) {
                uniqueValues.add(value);
            }
        }
        const uniqueValuesArray = Array.from(uniqueValues);
        // Create traces for each unique value in the 'groupFor' field
        let colorIndex = 0
        for (const value of uniqueValuesArray) {
            const yValues = xValues.map(group => processedData[group].groupForValues[value] || 0);
            let trace = {
                x: widget?.orientation == 'h' ? yValues : xValues,
                y: widget?.orientation == 'h' ? xValues : yValues,
                // text: widget?.orientation == 'h' ? xValues : yValues,
                text: yValues,
                name: `${this.resolveNames(value, groupFor)}`,
                hovertemplate: widget?.orientation == 'h' ? '%{fullData.name}: %{x:.2f}<extra></extra>' : '%{fullData.name}: %{y:.2f}<extra></extra>',
                orientation: widget.orientation,
                textposition: 'auto',
                type: 'bar',
                marker: {
                    color: colors[colorIndex % colors.length]
                }
            }
            traces.push(trace);
            colorIndex++;
            isData++;
        }
        graphData = traces;

        return { graphData, isData };
    }
    mongoBarChart(data, graphOptions, widget) {
        let metrics = [...widget.mongoPanelData]
        metrics.forEach((panel, index) => panel["variable"] = `A${index + 1}`)
        let dataToBeShown = metrics.find(metric => metric.dispAttribute)
        let orientation = widget?.orientation
        let categoryAxis = orientation == 'h' ? 'yaxis' : 'xaxis'
        let valueAxis = orientation == 'h' ? 'xaxis' : 'yaxis'
        let { groupBy, sideBy, stackBy, sortBy } = widget?.graphOptions?.miscSettings?.mongoPanelMisc
        let preparedData: any = this.prepareMongoBarData(data, metrics, groupBy, sideBy, stackBy, sortBy, orientation)
        preparedData.data.forEach(trace => {
            const isHorizontal = orientation === 'h';
            const pairToYMap = new Map();

            let groupValues: any[], sideValues: any[] = [];
            if (sideBy && stackBy) {
                [groupValues, sideValues] = isHorizontal ? trace.y : trace.x;
                for (let i = 0; i < groupValues.length; i++) {
                    const key = JSON.stringify([groupValues[i].toString(), sideValues[i].toString()]);
                    const value = isHorizontal ? trace.x[i] : trace.y[i];
                    pairToYMap.set(key, value);
                }
            } else {
                groupValues = isHorizontal ? trace.y : trace.x;
                for (let i = 0; i < groupValues.length; i++) {
                    const key = groupValues[i];
                    const value = isHorizontal ? trace.x[i] : trace.y[i];
                    pairToYMap.set(key, value);
                }
            }

            const newLabels = [];
            const newSubLabels = [];
            const newValues = [];

            preparedData.categorySortedArray.forEach((item) => {
                const key = (sideBy && stackBy) ? JSON.stringify([item[0]?.id.toString(),item[1]?.id.toString()]) : item.id;
                const value = pairToYMap.has(key) ? pairToYMap.get(key) : null;

                if (sideBy && stackBy) {
                    newLabels.push(item[0]?.name.toString());
                    newSubLabels.push(item[1]?.name.toString());
                } else {
                    newLabels.push(item.name);
                }
                newValues.push(value);
            });

            if (sideBy && stackBy) {
                if (isHorizontal) {
                    trace.x = newValues;
                    trace.y = [newLabels, newSubLabels];
                } else {
                    trace.x = [newLabels, newSubLabels];
                    trace.y = newValues;
                }
            } else {
                if (isHorizontal) {
                    trace.x = newValues;
                    trace.y = newLabels;
                } else {
                    trace.x = newLabels;
                    trace.y = newValues;
                }
            }
        });

        graphOptions.data = preparedData.data.sort((a, b) => {
            return b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' })
        })
        graphOptions.layout = {
            ...graphOptions.layout,
            barmode: 'stack'
        }
        if (sideBy && stackBy) {
            graphOptions.layout[categoryAxis]["type"] = 'multicategory'
            delete graphOptions.layout[valueAxis]["type"]
        }
        else if (sideBy)
            graphOptions.layout.barmode = "group"
        if (sideBy || stackBy){
            graphOptions.layout[valueAxis]['title'] = dataToBeShown?.label || ''
            delete graphOptions.layout[categoryAxis]['title']
        }
        return { isData: preparedData.isData, graphOptions }

    }
    evaluateExpression(expression, dataMap) {
        try {
            const replacedExpression = expression.replace(/\b[A-Z]\d+\b/g, match => dataMap[match] || 0);
            return evaluate(replacedExpression); // Be cautious with eval in real scenarios
        } catch (error) {
            console.error("Error evaluating expression:", expression, error);
            return 0;
        }
    }
    parseDisplayTime(value) {
        if (!value || typeof value !== "string") return moment.invalid();

        const trimmed = value.trim();

        // Match "Q2 - 24" or "Q1-24"
        const quarterMatch = trimmed.match(/^Q\s?([1-4])\s?-\s?(\d{2})$/i);
        if (quarterMatch) {
            const quarter = parseInt(quarterMatch[1]);
            const year = 2000 + parseInt(quarterMatch[2]);
            return moment().year(year).quarter(quarter).startOf('quarter');
        }

        // Match "Week12 - 24" or "Week1-25"
        const weekMatch = trimmed.match(/^FW\s?(\d{1,2})\s?-\s?(\d{2})$/i);
        if (weekMatch) {
            const week = parseInt(weekMatch[1]);
            const year = 2000 + parseInt(weekMatch[2]);
            return moment().year(year).week(week).startOf('week');
        }

        // Match "Jan - 25" or "Feb-24"
        const monthYearMatch = trimmed.match(/^([A-Za-z]+)\s*-\s*(\d{2})$/);
        if (monthYearMatch) {
            const month = monthYearMatch[1];
            const year = 2000 + parseInt(monthYearMatch[2]);
            return moment(`${month} ${year}`, "MMM YYYY");
        }

        const fullYearMatch = trimmed.match(/^(\d{4})$/);
        if (fullYearMatch) {
            return moment(`${trimmed}-01-01`, "YYYY-MM-DD");
        }
        // Try parsing standard date formats
        const date = moment(trimmed, ["DD-MMM-YY", moment.ISO_8601], true);
        return date.isValid() ? date : moment.invalid();
    }

    sortData(data, sortBy, isDate, order = "asc") {
        return data.sort((a, b) => {
            const valA = a?._id?.[sortBy]?.name;
            const valB = b?._id?.[sortBy]?.name;

            if (valA === undefined || valB === undefined) return 0;

            if (+valB && +valA && typeof +valA === "number" && typeof +valB === "number") {
                return order === "asc" ? valA - valB : valB - valA;
            }

            if (isDate) {
                const dateA = this.parseDisplayTime(valA);
                const dateB = this.parseDisplayTime(valB);
                if (!dateA?.isValid?.() || !dateB?.isValid?.()) return 0;
                return order === "asc"
                    ? dateA.valueOf() - dateB.valueOf()
                    : dateB.valueOf() - dateA.valueOf();
            }

            // Default string sort
            return order === "asc"
                ? String(valA).toLowerCase().localeCompare(String(valB).toLowerCase(), undefined, { numeric: true })
                : String(valB).toLowerCase().localeCompare(String(valA).toLowerCase(), undefined, { numeric: true });
        });
    }
    prepareMongoBarData(data, metrics, groupBy, sideBy = null, stackBy = null, sortBy = 'asc', orientation = 'h') {
        if (sideBy) sideBy = sideBy.replaceAll('.', '');
        if (stackBy) stackBy = stackBy.replaceAll('.', '');
        if (groupBy) groupBy = groupBy.replaceAll('.', '');
        let categoryAxis = orientation == 'h' ? 'y' : 'x'
        let valueAxis = orientation == 'h' ? 'x' : 'y'
        let dataToBeShown = metrics.find(metric => metric.dispAttribute)
        let isData = false
        if (!dataToBeShown || !groupBy)
            return { isData, data: [] }
        let isCalculated = dataToBeShown?.calc
        if (isCalculated && !dataToBeShown?.relation)
            return { isData, data: [] }
        let processData = JSON.parse(JSON.stringify(data[dataToBeShown.targetCollection] || []))
        if(!processData?.length) return { isData, data: [] }
        let isDateGroup = this.targetCollectionList.find(collection => collection.name == dataToBeShown.targetCollection)?.fields?.find(field => field.name == groupBy)?.isDateField
        const groupedData = {};
        processData.forEach(entry => {
            if (entry._id && typeof entry._id === 'object') {
                entry._id[groupBy] = { id: entry._id[groupBy], name: this.mongoObjectTypes.has(groupBy) ? this.getName(entry._id[groupBy]) : this.resolveNames(entry._id[groupBy], groupBy) || entry._id[groupBy] } // Formatting week
                if (sideBy) entry._id[sideBy] = { id: entry._id[sideBy], name: this.mongoObjectTypes.has(sideBy) ? this.getName(entry._id[sideBy]) : this.resolveNames(entry._id[sideBy], sideBy) || entry._id[sideBy] }
                if (stackBy) entry._id[stackBy] = { id: entry._id[stackBy], name: this.mongoObjectTypes.has(stackBy) ? this.getName(entry._id[stackBy]) : this.resolveNames(entry._id[stackBy], stackBy) || entry._id[stackBy] }
            }
        })
        processData = this.sortData(processData, groupBy, isDateGroup, sortBy)
        const uniqueSet = new Set();
        processData.forEach(entry => {
            const groupKey = entry._id[groupBy]; // Formatting week
            const sideKey = entry._id[sideBy] || "ALL";
            const stackKey = entry._id[stackBy] || "ALL";
            let value = entry[dataToBeShown.label]
            let key;
            if (stackBy && sideBy) {
                key = JSON.stringify([JSON.stringify(entry._id?.[groupBy]), JSON.stringify(entry._id?.[sideBy])]);

            } else {
                key = JSON.stringify(JSON.stringify(entry._id?.[groupBy]));
            }
            if (key) {
                uniqueSet.add(key);
            }
            if (!groupedData[groupKey?.id]) groupedData[groupKey?.id] = {};

            if (!stackBy && sideBy) {
                if (!groupedData[groupKey?.id][sideKey?.id]) groupedData[groupKey?.id][sideKey?.id] = 0;
                groupedData[groupKey?.id][sideKey?.id] += value;

            } else if (!sideBy && stackBy) {
                if (!groupedData[groupKey?.id][stackKey?.id]) groupedData[groupKey.id][stackKey.id] = 0;
                groupedData[groupKey.id][stackKey.id] += value;

            } else if (sideBy && stackBy) {
                if (!groupedData[groupKey.id][sideKey.id]) groupedData[groupKey.id][sideKey.id] = {};
                if (!groupedData[groupKey.id][sideKey.id][stackKey.id]) groupedData[groupKey.id][sideKey.id][stackKey.id] = 0;
                groupedData[groupKey.id][sideKey.id][stackKey.id] += value;

            } else {
                if (!groupedData[groupKey?.id] || typeof groupedData[groupKey?.id] == 'object') {
                    groupedData[groupKey?.id] = 0
                };
                groupedData[groupKey?.id] += value;
            }
        });
        const categorySortedArray = Array.from(uniqueSet, (item: string) => {
            const parsed = JSON.parse(item);       // First parse to get array of strings
            if(Array.isArray(parsed)) {
                return parsed.map((v: string) => JSON.parse(v)); // Second parse to get raw values
            }
            return JSON.parse(parsed);
        });
        let colorIndex = 0;
        if (sideBy && stackBy) {
            const formattedData = {};
            Object.keys(groupedData).forEach(groupKey => {
                Object.keys(groupedData[groupKey]).forEach(sideKey => {
                    Object.keys(groupedData[groupKey][sideKey]).forEach(stackKey => {
                        const stackKeyName = this.mongoObjectTypes.has(stackBy) ? this.getName(stackKey) : this.resolveNames(stackKey, stackBy) || stackKey;
                        if (!formattedData[stackKey]) {
                            formattedData[stackKey] = { [categoryAxis]: [[], []], [valueAxis]: [], type: 'bar', orientation: orientation, name: stackKeyName, customId: stackKey, marker: { color: this.widgetDashboardService.colors[colorIndex] },categorySortedArray };
                            colorIndex++;
                        }
                        formattedData[stackKey][categoryAxis][0].push(groupKey);
                        formattedData[stackKey][categoryAxis][1].push(sideKey);
                        formattedData[stackKey][valueAxis].push(groupedData[groupKey][sideKey][stackKey]);
                        if (!isData && groupedData[groupKey][sideKey][stackKey]) isData = true
                    });
                });
            });
            return { isData, data: Object.values(formattedData) ,categorySortedArray};

        } else if (sideBy) {
            const formattedData = {};
            Object.keys(groupedData).forEach(groupKey => {
                Object.keys(groupedData[groupKey]).forEach(sideKey => {
                    const sideKeyName = this.mongoObjectTypes.has(sideBy) ? this.getName(sideKey) : this.resolveNames(sideKey, sideBy) || sideKey;
                    if (!formattedData[sideKey]) {
                        formattedData[sideKey] = { x: [], y: [], type: 'bar', orientation: orientation, name: sideKeyName, customId: sideKey, marker: { color: this.widgetDashboardService.colors[colorIndex] },categorySortedArray };
                        colorIndex++;
                    }
                    formattedData[sideKey][categoryAxis].push(groupKey);
                    formattedData[sideKey][valueAxis].push(groupedData[groupKey][sideKey]);
                    if (!isData && groupedData[groupKey][sideKey]) isData = true
                });
            });
            return { isData, data: Object.values(formattedData),categorySortedArray };

        } else if (stackBy) {
            const formattedData = {};
            Object.keys(groupedData).forEach(groupKey => {
                Object.keys(groupedData[groupKey]).forEach(stackKey => {
                    const stackKeyName = this.mongoObjectTypes.has(stackBy) ? this.getName(stackKey) : this.resolveNames(stackKey, stackBy) || stackKey;
                    if (!formattedData[stackKey]) {
                        formattedData[stackKey] = { x: [], y: [], type: 'bar', orientation: orientation, name: stackKeyName, customId: stackKey, marker: { color: this.widgetDashboardService.colors[colorIndex] }, categorySortedArray };
                        colorIndex++;
                    }
                    formattedData[stackKey][categoryAxis].push(groupKey);
                    formattedData[stackKey][valueAxis].push(groupedData[groupKey][stackKey]);
                    if (!isData && groupedData[groupKey][stackKey]) isData = true
                });
            });
            return { isData, data: Object.values(formattedData), categorySortedArray};

        } else {
            return {
                isData: Object.values(groupedData).filter(Boolean),
                data: [{
                    [categoryAxis]: Object.keys(groupedData),
                    [valueAxis]: Object.values(groupedData),
                    type: 'bar',
                    orientation: orientation,
                    categorySortedArray,
                    name: dataToBeShown.label
                }],
                categorySortedArray
            };
        }
    }
    handleMongoData(data: any, widget: any): { trace: any; isData: any; } {
        let trace = {
            "x": [],
            "y": [],
            "text": [],
            "textposition": "auto",
            orientation: widget.orientation,
            hovertemplate: widget?.orientation == 'h' ? '%{y}: %{x:.2f}<extra></extra>' : '%{x}: %{y:.2f}<extra></extra>',
            "type": "bar",
            "showlegend": false,
            "marker": {},
            "name": 'Total'
        };
        let isData = 0;

        for (let key in data) {
            data[key].forEach(item => {
                isData++;
                let a = item.a ?? 0;
                if (widget?.orientation == 'h') {
                    trace.y.push(item.group);
                    trace.x.push(a.toString());
                } else {
                    trace.x.push(item.group);
                    trace.y.push(a.toString());
                }
                trace.text.push(a.toString());
            });
        }

        return { trace, isData };
    }

    BarChartMECharts(widget, data, colors, echartOptions) {
        let groupFor = widget.groupFor;
        let groupBy = widget?.mongoPanelData[0]?.groupBy;
        let tableMode = widget?.graphOptions?.miscSettings?.tableMode;
        let t = 0;

        echartOptions = {
            xAxis: {},
            yAxis: {},
            series: []
        };

        let collectionConfig = this.targetCollectionList.find(i => i.name === widget?.mongoPanelData[0]?.targetCollection);
        let groupConfig = collectionConfig?.fields.find(i => i.name === groupBy);
        groupBy = groupConfig?.displayName;

        if (!groupFor) {
            let { echartOptions: chartOption, isData } = this.handleMongoDataECharts(data, widget);
            t = isData;
            (chartOption.series[0] as any).itemStyle = {
                color: (params: any) => colors[params.dataIndex % colors.length],
                borderRadius: widget.orientation === 'h' ? [0, 5, 5, 0] : [5, 5, 0, 0]
            };
            echartOptions = { ...chartOption };
        } else {
            let groupGraphData = this.handleMongoGroupBarDataECharts(data, widget, colors);
            echartOptions = groupGraphData.option;
            t = groupGraphData.isData;
            echartOptions.series.forEach((seriesItem: any, idx: number) => {
                (seriesItem as any).itemStyle = {
                    color: colors[idx % colors.length],
                    borderRadius: widget.orientation === 'h' ? [0, 5, 5, 0] : [5, 5, 0, 0]
                };
            });
            if (echartOptions.series[0])
                echartOptions.series[0].barCategoryGap = '20%';
        }
        if(!groupConfig?.isDateField) {
            echartOptions.xAxis.axisLabel = {
                rotate: 30,
                // interval: 0
            };
        }

        echartOptions.yAxis.axisLabel = {
            rotate: 30,
            // interval: 0,
        };

        echartOptions.legend = this.getLegendConfig(widget?.legend?.orientation, widget?.legend?.position)
        echartOptions.legend.show = widget.graphOptions.miscSettings.showlegend
        echartOptions.legend.overlapLegend = widget.graphOptions.miscSettings.overlapLegends

        echartOptions.grid = {
            left: 25,
            right: 25,
            top: 10,
            bottom: 10,
            containLabel: true
        };

        return { echartOptions, isData: t !== 0 };
    }

    HistogramBarChartMECharts(widget, data, colors, echartOptions) {
        let t = 0;

        echartOptions = {
            xAxis: {},
            yAxis: {},
            series: [{data:[]}]
        };
        if (!Object.keys(data).length || widget?.mongoPanelData.length !== Object.keys(data).length) return { echartOptions, isData: false }
        let { echartOptions: chartOption, isData, msg } = this.handleMongoDataHistogramECharts(data, widget);
        if (!isData) return { echartOptions, isData: false, msg }
        t = isData;
        echartOptions = {...chartOption}
        // echartOptions.xAxis.axisLabel = {
        //     rotate: 30,
        //     // interval: 0
        // };
        echartOptions.yAxis.axisLabel = {
            rotate: 30,
            // interval: 0,
        };

        echartOptions.legend = this.getLegendConfig(widget?.legend?.orientation, widget?.legend?.position)
        echartOptions.legend.show = widget.graphOptions.miscSettings.showlegend
        echartOptions.legend.overlapLegend = widget.graphOptions.miscSettings.overlapLegends

        echartOptions.grid = {
            left: 10,
            right: 10,
            top: 10,
            bottom: 10,
            containLabel: true
        };

        return { echartOptions, isData: t !== 0, msg };
    }

    getLegendConfig(orientation, position) {
        const orient = orientation === 'v' ? 'vertical' : 'horizontal';
        let legendConfig: any = {
            type: 'scroll',
            tooltip: {
                show: true,
            },
            orient: orient,
            height: '95%',
            backgroundColor: 'white',
            textStyle: {
                fontSize: 10
            },
            formatter: function (name) {
                return name.length > 25 ? name.substring(0, 24) + '...' : name;
            }
        };
        switch (position) {
            case 1: // Top-Left
                legendConfig.left = 'left';
                legendConfig.top = 'top';
                break;
            case 2: // Top-Right
                legendConfig.left = 'right';
                legendConfig.top = 'top';
                break;
            case 3: // Bottom-Left
                legendConfig.left = 'left';
                legendConfig.bottom = 'bottom';
                break;
            case 4: // Bottom-Right
                legendConfig.left = 'right';
                legendConfig.bottom = 'bottom';
                break;
            default:
                legendConfig.left = 'right';
                legendConfig.top = 'top';
                break;
        }
        return legendConfig;
    }

    handleMongoDataECharts(data: any, widget: any) {
        let isData = 0;
        let seriesItem = {
            type: 'bar',
            data: [],
            barWidth: 'auto',
            label: {
                show: true,
                position: 'inside',
                formatter: '{c}'
            }
        };
        const xAxisData = [];

        for (let key in data) {
            data[key].forEach(item => {
                isData++;
                let a = item.a ?? 0;
                if (widget?.orientation === 'h') {
                    xAxisData.push(item);
                    seriesItem.data.push(a);
                } else {
                    xAxisData.push(item);
                    seriesItem.data.push(a);
                }
            });
        }

        let echartOptions = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            xAxis: {
                type: widget?.orientation === 'h' ? 'value' : 'category',
                data: widget?.orientation === 'h' ? null : xAxisData
            },
            yAxis: {
                type: widget?.orientation === 'h' ? 'category' : 'value',
                data: widget?.orientation === 'h' ? xAxisData : null
            },
            series: [seriesItem],
            barCategoryGap: '20%',
        };
        return { echartOptions, isData };
    }


    // getCalcAttributePayload(widget, panel) {
    //     let formula = panel['relation'];
    //     const variablePattern = /\bA\d+\b/g;
    //     const variables = formula.match(variablePattern) || [];
    //     let calcfields = [];
    //     const targetCollectionTypes = new Set<string>();

    //     for (const v of variables) {
    //         const index = parseInt(v.substring(1)) - 1; // Convert A1 -> 0, A2 -> 1, etc.
    //         if (!widget.mongoPanelData[index]) {
    //             throw new Error(`Variable ${v} is missing from the result set.`);
    //         }
    //         calcfields.push(widget.mongoPanelData[index].attribute);
    //         targetCollectionTypes.add(widget.mongoPanelData[index].targetCollection);
    //     }
    //     if (targetCollectionTypes.size == 1) {
    //         let mongoPanel = {
    //             id: panel['id'],
    //             label: panel["label"],
    //             filters: panel["filters"],
    //             relation: panel['relation'],
    //             targetCollection: targetCollectionTypes.values().next().value,
    //             fields: calcfields
    //         };
    //         if (panel.groupBy === 'yes') {
    //             mongoPanel['groupBy'] = panel['label']
    //         } else {
    //             mongoPanel['attribute'] = panel['label']
    //             mongoPanel['aggregation'] = panel['subTargetOperator']
    //         }
    //         return mongoPanel;
    //     } else {
    //         console.error(Error('Atrributes from different target collections cannot be used in the same calculation.'));
    //     }
    //     return;
    // }

    handleMongoGroupBarDataECharts(data: any, widget: any, colors: any) {
        let groupFor = widget.groupFor;
        let barmode = widget?.graphOptions?.miscSettings?.barmode;
        let isData = 0;
        const processedData = {};

        for (let key in data) {
            data[key].forEach(item => {
                const group = item.group.name;
                if (!processedData[group]) {
                    processedData[group] = { total: 0, groupForValues: {} };
                }
                processedData[group].total += item.a;

                const valuesToGroup = item[groupFor];
                for (const value of valuesToGroup) {
                    if (!processedData[group].groupForValues[value]) {
                        processedData[group].groupForValues[value] = 0;
                    }
                    processedData[group].groupForValues[value] += 1;
                }
            });
        }

        const uniqueValues = new Set();
        const xValues = Object.keys(processedData);
        const series = [];

        for (const group of xValues) {
            const groupData = processedData[group];
            for (const value in groupData.groupForValues) {
                uniqueValues.add(value);
            }
        }
        const uniqueValuesArray = Array.from(uniqueValues);

        let colorIndex = 0;
        for (const value of uniqueValuesArray) {
            const yValues = xValues.map(group => processedData[group].groupForValues[value] || 0);
            let seriesItem = {
                name: `${this.resolveNames(value, groupFor)}`,
                type: 'bar',
                data: yValues,
                ...(barmode == 'stack' && { stack: 'stack' }),
                barWidth: 'auto',
                label: {
                    show: true,
                    position: 'inside',
                    formatter: '{c}'
                }
            };

            series.push(seriesItem);
            colorIndex++;
            isData++;
        }

        let option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                data: uniqueValuesArray.map(value => this.resolveNames(value, groupFor))
            },
            xAxis: {
                type: widget?.orientation === 'h' ? 'value' : 'category',
                data: widget?.orientation === 'h' ? null : xValues
            },
            yAxis: {
                type: widget?.orientation === 'h' ? 'category' : 'value',
                data: widget?.orientation === 'h' ? xValues : null,
                axisLabel: {
                    fontSize: 12,
                }
            },
            series: series,
            barGap: '0%',
            barCategoryGap: '20%',
        };
        return { option, isData };
    }

    handleMongoDataHistogramECharts(data: any, widget: any) {
        let isData = 0;
        // data = data[Object.keys(data)[0]]  // need to update logic to consider the selected display field for data
        let dispItemIndex = widget?.mongoPanelData.findIndex(mpd => mpd.dispAttribute)
        Object.keys(data).forEach(key => {
            const mongoPanelItem = widget.mongoPanelData.find((panelData: any) => panelData.id == key);
            const targetCollection = this.targetCollectionList.find(tcl => tcl.name === mongoPanelItem.targetCollection)
            if(!targetCollection) return;
            const targetField = targetCollection.fields.find(field => field.name === mongoPanelItem.attribute);
            // if(targetField?.storedUnit === 'minutes' && targetField.displayUnit === 'hours' && !this.isDataConverted[`${widget._id}_${key}`]) {
            //     data[key] = data[key].map((item: number) => Number((item / 60).toFixed(2)));
            //     this.isDataConverted[`${widget._id}_${key}`] = true
            // }
        })
        if (dispItemIndex == -1 || !(widget?.mongoPanelData[dispItemIndex].calc || data[Object.keys(data)[dispItemIndex]].length)) {
            let echartOptions = {
                xAxis: {},
                yAxis: {},
                series: [{data:[]}]
            };
            return { echartOptions, isData: 0, msg: '' }
        }
        let isDate;
        let binsData;
        if(widget?.mongoPanelData[dispItemIndex].calc){
            try {
                data = this.createBinsForCustomFormula(widget?.mongoPanelData[dispItemIndex].relation, data)
                if(!data.length) throw Error('')
                if(data.length && typeof data[0] == 'string' && moment(data[0]).isValid()) isDate = true;

            } catch(e) {
                let echartOptions = {
                    xAxis: {},
                    yAxis: {},
                    series: [{data:[]}]
                };
                return { echartOptions, isData: 0, msg: e.message}
            }
        }
        else {
            let col = widget.mongoPanelData[dispItemIndex].targetCollection
            let attr = widget.mongoPanelData[dispItemIndex].attribute
            let colConfig = this.targetCollectionList.find(c => c.name == col)
            isDate = colConfig?.fields.find(c => !c.categorical && c.name == attr && c.isDateField)
            data = data[Object.keys(data)[dispItemIndex]]
        }
        binsData = this.createBins(data, widget.bins, !!isDate)

        const bins = binsData.map(d => Object.keys(d)[0])
        const counts = binsData.map(d => Object.values(d)[0])

        isData = counts.length > 0 ? 1 : 0;
        const isHorizontal = widget?.orientation === 'h';

        // Construct the ECharts options with dynamic axis handling
        const echartOptions = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            },
            xAxis: isHorizontal
                ? {
                    type: 'value', splitLine: {
                        show: true,
                        lineStyle: {
                            type: 'dashed'
                        }
                    },
                } // Frequency on x-axis (horizontal bars)
                : { type: 'category', data: bins, axisLabel: { rotate: isDate ? 0 : 30 } }, // Bin labels on x-axis (vertical bars),

            yAxis: isHorizontal
                ? { type: 'category', data: bins, axisLabel: { rotate: 30 } } // Bin labels on y-axis (horizontal bars)
                : {
                    type: 'value', splitLine: {
                        show: true,
                        lineStyle: {
                            type: 'dashed'
                        }
                    },
                }, // Frequency on y-axis (vertical bars)

            series: [
                {
                    name: '',
                    type: 'bar',
                    data: counts,
                    barWidth: '60%',
                    itemStyle: {
                        color: '#374AFB',
                        borderRadius: isHorizontal ? [0, 5, 5, 0] : [5, 5, 0, 0]
                    },
                    orientation: isHorizontal ? 'horizontal' : 'vertical'
                }
            ]
        };

        return { echartOptions, isData, msg:'' };
    }
    createBinsForCustomFormula(formula: string, data) {
        // Extract variable names (A1, A2, etc.)
        const variablePattern = /\bA\d+\b/g;
        const variables = formula.match(variablePattern) || [];
        // Map variables to corresponding keys in `data`
        const variableKeys = Object.keys(data);
        const mappedVariables = {};

        for (const v of variables) {
            const index = parseInt(v.substring(1)) - 1; // Convert A1 -> 0, A2 -> 1, etc.
            if (!variableKeys[index]) {
                throw new Error(`Variable ${v} is missing from the result set.`);
            }
            mappedVariables[v] = data[variableKeys[index]];
        }

        // Get the length of the first non-empty array
        const nonEmptyArrays: any[] = Object.values(mappedVariables).filter((arr: any[]) => arr.length > 0);
        if (nonEmptyArrays.length === 0) return [];

        const length = nonEmptyArrays[0].length;
        Object.keys(mappedVariables).find(key => {
            if(mappedVariables[key]?.currentTime) {
                const currentTime = mappedVariables[key].date.toISOString();
                mappedVariables[key] = new Array(length);
                mappedVariables[key].fill(currentTime, 0, length)
            }
        })

        // Determine the types present in the dataset
        const dataTypes = new Set<string>();
        Object.values(mappedVariables).forEach((arr: any[]) => {
            arr.forEach(value => dataTypes.add(typeof value));
        });

        if (dataTypes.size > 1) {
            throw new Error("Variables are not compatible.");
        }

        // Ensure all referenced arrays have the same length
        if (Object.values(mappedVariables).some((arr: any[]) => arr.length !== length)) {
            throw new Error("Mismatched data lengths for variables.");
        }

        const isDateOperation = dataTypes.has("string");
        // Handle date operations
        if (isDateOperation) {

            // If the formula is just a single variable (e.g., "A1"), return its values directly
            if (variables.length === 1 && formula.trim() === variables[0]) {
                return mappedVariables[variables[0]]; // Return original date strings
            }

            // Check if formula contains anything other than subtraction (`-`)
            const invalidVariableOpsRegex = /\bA\d+\s*[\+*/]\s*A\d+\b/g;
            if (invalidVariableOpsRegex.test(formula)) {
                throw new Error("Invalid operation on date values. Only subtraction ('-') is allowed.");
            }

            // Convert all date strings to Date objects
            const dateArrays: Record<string, Date[]> = {};
            for (const key in mappedVariables) {
                dateArrays[key] = mappedVariables[key].map(dateStr => new Date(dateStr));
            }

            const computedResults: any[] = [];
            for (let i = 0; i < length; i++) {
                const localScope: Record<string, any> = {};
                variables.forEach(v => {
                    localScope[v] = dateArrays[v][i].getTime(); // Convert date to milliseconds
                });

                // Replace variables in the formula with actual timestamp values
                const expression = formula.replace(/\bA\d+\b/g, match => `localScope["${match}"]`);

                try {
                    const result = eval(expression); // Perform milliseconds calculation
                    computedResults.push(parseFloat((result / 60000).toFixed(2))); // Convert milliseconds to minutes
                } catch {
                    throw new Error("Invalid operation on date values.");
                }
            }
            return computedResults;
        }


        // Compute result by applying the formula to each row
        const computedResults = [];
        for (let i = 0; i < length; i++) {
            const localScope = {};
            variables.forEach(v => {
                localScope[v] = mappedVariables[v][i];
            });

            // Replace variables in the formula with actual values
            const expression = formula.replace(/\bA\d+\b/g, match => localScope[match].toString());

            // Evaluate the expression safely
            try {
                computedResults.push(Number(eval(expression)));
            } catch {
                throw new Error("Invalid formula expression.");
            }
        }

        return computedResults;

    }

    createBins(data: any, binSize = 2, isDate = false) {
        if (isDate) return this.generateDateRangeFrequencyMap(data, binSize)
        return this.generateFrequencyMap(data, binSize)
    }

    createRanges(min, max, numRanges) {
        min = Math.floor(min);
        max = Math.ceil(max);
        let totalValues = max - min;
        let ranges = [];
        let step = totalValues / numRanges;
        let start = min;
        for (let i = 0; i < numRanges; i++) {
            let end = (i === numRanges - 1) ? max : start + step;

            let roundedStart = Math.round(start);
            let roundedEnd = Math.round(end);

            // Avoid pushing duplicates like {start: 1, end: 1}
            if (ranges.length === 0 || ranges[ranges.length - 1].end !== roundedEnd) {
                ranges.push({ start: roundedStart, end: roundedEnd, count: 0 });
            }
            start = end;
        }
        return ranges;
    }

    generateFrequencyMap(arr, numRanges) {
        let min = Infinity;
        let max = -Infinity;
        for(let i = 0; i < arr.length; i++) {
            if (arr[i] < min) min = arr[i];
            if (arr[i] > max) max = arr[i];
        }
        let ranges = this.createRanges(min, max, numRanges);
        // Create frequency map based on generated ranges
        arr.forEach((num) => {
            for (let range of ranges) {
                if (((num > range.start) || num === Math.floor(min)) && num <= range.end) {
                    range.count++;
                    break;
                }
            }
        });

        return ranges.map(r => {
            return { [`${Number(r.start)}-${Number(r.end)}`]: r.count }
        })
    }

    createDateRanges(minDate, maxDate, numRanges) {
        let min = new Date(minDate);
        let max = new Date(maxDate);
        let totalDays: any = Math.floor((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        numRanges = Math.min(numRanges, totalDays);

        let ranges = [];
        let rangeSize = Math.floor(totalDays / numRanges);
        let remainder = totalDays % numRanges;

        let start = new Date(min);
        for (let i = 0; i < numRanges; i++) {
            let extra = remainder > 0 ? 1 : 0;
            let end = new Date(start);
            end.setDate(end.getDate() + rangeSize + extra - 1);
            end.setHours(23, 59, 59, 999)
            remainder--;
            ranges.push({ start: new Date(start), end: new Date(end) });
            start = new Date(end);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0)
        }

        return ranges;
    }

    generateDateRangeFrequencyMap(timestamps, numRanges) {
        let dates = timestamps.map(ts => new Date(ts));
        let minDate = new Date(Math.min(...dates));
        minDate.setHours(0, 0, 0, 0);
        let maxDate = new Date(Math.max(...dates));
        maxDate.setHours(23, 59, 59, 999);

        let ranges = this.createDateRanges(minDate, maxDate, numRanges);

        let frequencyMap = ranges.map(range => ({
            // Since we are expecting the date to be in 'dd/MM/yyyy' format, we can use toLocaleDateString with 'en-IN' locale
            start: range.start.toLocaleDateString("en-IN"),
            end: range.end.toLocaleDateString("en-IN"),
            count: 0
        }));

        // Count timestamps in each range
        dates.forEach(date => {
            for (let i = 0; i < ranges.length; i++) {
                if (date >= ranges[i].start && date <= ranges[i].end) {
                    frequencyMap[i].count++;
                    break;
                }
            }
        });

        return frequencyMap.map(r => {
            return { [`${this.formatDateRange(r.start,r.end)}`]: r.count };
        })
    }

    formatDateRange(start: string, end: string): string {
        const formattedStart = this.formatDateString(start);
        const formattedEnd = this.formatDateString(end);
        return `${formattedStart} - ${formattedEnd}`;
    }

    formatDateString(dateString: string): string {
        const [day, month, year] = dateString.split('/').map(Number);
        const date = new Date(year, month - 1, day); // Month is zero-based in JS Dates
        return this.datePipe.transform(date, 'dd MMM yy') ?? ''; // Fallback to empty string if date is invalid
    }

    pieChartData(resData, widget, colors, systemNameForQuickPlot, graphOptions){
        let t = 0, labelForQuickPlot = "";
        let dataKeys = Object.keys(resData);
        let graphData = {
            labels: [],
            values: [],
            type: "pie",
            mode: "pie",
            hole: widget.hole,
            textinfo: 'percent+value',
            marker: {
                colors: colors.slice(0,dataKeys.length+1),
            }
        }
        dataKeys.forEach((key, index) => {
            let name;
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                if (index == 0) {
                    labelForQuickPlot = this.widgetDashboardService.getLabel(widget.panelDataNew, key);
                }
                name = `${systemNameForQuickPlot[key]}_${labelForQuickPlot}`
            }
            else {
                name = this.widgetDashboardService.getLabel(widget.panelDataNew, key)
            }
            let data = resData[key];
            let val = 0
            data.forEach(element => {
                val += element.a
            })
            if (val) {
                graphData.labels.push(name)
                graphData.values.push(Math.abs(val).toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                t++;
            }
        })
        graphOptions.data = graphData
        return { graphOptions, isData: (t != 0 && this.isQuickPlotValid(widget, labelForQuickPlot)) }
    }
    getPieChartPosition({ position, orientation }) {
        if (orientation === 'v') {
            if (position === 1) {
                return ['58%', '50%'];
            }
            if (position === 2) {
                return ['42%', '50%'];
            }
            if (position === 3) {
                return ['58%', '50%'];
            }
            if (position === 4) {
                return ['42%', '50%'];
            }
        } else {
            if (position === 1) {
                return ['50%', '58%'];
            }
            if (position === 2) {
                return ['50%', '58%'];
            }
            if (position === 3) {
                return ['50%', '42%'];
            }
            if (position === 4) {
                return ['50%', '42%'];
            }
        }
        return ['50%', '50%'];
    }
    getSemiPieChartPosition({ position, orientation }) {
        if (position === 1) {
            return ['50%', '90%'];
        }
        if (position === 2) {
            return ['50%', '90%'];
        }
        if (position === 3 || position === 4) {
            if (orientation === 'v') {
                return ['50%', '60%'];
            }
            else return ['50%', '65%']
        }
        return ['50%', '50%'];
    }
    echartPieChart(resData, widget, colors, systemNameForQuickPlot, echartOptions) {
        let graphformatter = {
            formatter: function (params) {
                return `<div>
                            ${params.name}<br>
                            <strong>${params.value}</strong><br>
                            <strong>${params.percent.toFixed(widget.graphOptions.miscSettings.decimalPlaces)}%</strong>
                        </div>`;
            },
        }
        echartOptions.tooltip = { ...echartOptions.tooltip, ...graphformatter };
        let pieChartSeries = {
            name: 'Pie chart',
            type: 'pie',
            label: {
                show: false
            },
            labelLine: {
                show: false
            },
        }
        echartOptions.series[0] = { ...echartOptions.series[0], ...pieChartSeries };
        let dataKeys = Object.keys(resData);
        let labelForQuickPlot = '';
        let t = 0;
        echartOptions.series[0].data = []
        dataKeys.forEach((key, index) => {
            let name;
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                if (index == 0) {
                    labelForQuickPlot = this.widgetDashboardService.getLabel(widget.panelDataNew, key);
                }
                name = `${systemNameForQuickPlot[key]}_${labelForQuickPlot}`
            }
            else {
                name = this.widgetDashboardService.getLabel(widget.panelDataNew, key)
            }
            let data = resData[key];
            let val = 0
            data.forEach(element => {
                val += element.a
            })
            if (val) {
                echartOptions.series[0].data.push({
                    name,
                    value: Math.abs(val).toFixed(widget.graphOptions.miscSettings.decimalPlaces),
                    itemStyle: { color: colors[index], borderColor: colors[index] }
                });
                echartOptions.legend.data.push({ name, icon: 'circle' });
                t++;
            }
        })
        if (widget.graphOptions.miscSettings?.legendType === 'b') {
            this.setGraphOptionsforSidePieChart(echartOptions, widget)
        }
        return { echartOptions, isData: (t != 0 && !!(!widget.graphOptions?.miscSettings?.quickPlot || labelForQuickPlot))}
    }
    histogramData(resData, widget, colors, systemNameForQuickPlot, graphOptions) {
        let t = 0, labelForQuickPlot = "";
        let dataKeys = Object.keys(resData);
        dataKeys.forEach((key, index) => {
            let name;
            if (widget.graphOptions?.miscSettings?.quickPlot) {
                if (index == 0) {
                    labelForQuickPlot = this.widgetDashboardService.getLabel(widget.panelDataNew, key);
                }
                name = `${systemNameForQuickPlot[key]}_${labelForQuickPlot}`
            }
            else {
                name = this.widgetDashboardService.getLabel(widget.panelDataNew, key)
            }
            let options = {
                type: 'histogram',
                name: name,
                marker: {
                    color: widget.additionalGraph ? widget.graphOptions.miscSettings.barColor || colors[index] : colors[index],
                    width: 1
                },
                x: []
            }
            let data = resData[key];
            data.forEach(element => {
                if (element.a) {
                    options.x.push(element.a?.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                    t++;
                }
            })
            options['hovertemplate'] = `%{y:.${widget.graphOptions.miscSettings.decimalPlaces}f}`
            if (name) {
                graphOptions.data.push(options)
            }
        })
        return { graphOptions, isData: (t != 0 && this.isQuickPlotValid(widget, labelForQuickPlot)) }
    }

    createHistogramCurve(widget, graphOptions) {
        // capability curve with histogram data
        let data = graphOptions.data[0].x
        const dataValues = data.map(dataPoint => +dataPoint)
        const normalPDF = (x, mean, std) => {
            return (1 / (std * Math.sqrt(2 * Math.PI))) *
                Math.exp(-0.5 * Math.pow((x - mean) / std, 2));
        }

        const mean =
            dataValues.reduce((a, b) => a + b, 0) / dataValues.length;

        const std = Math.sqrt(
            dataValues
                .map(x => Math.pow(x - mean, 2))
                .reduce((a, b) => a + b) / dataValues.length
        );

        const xCurve = [];
        const yCurve = [];
        for (let i = mean - 4 * std; i <= mean + 4 * std; i += 0.1) {
            xCurve.push(i);
            yCurve.push(normalPDF(i, mean, std));
        }
        let curveData = {
            x: xCurve,
            y: yCurve,
            yaxis: 'y2',
            type: 'scatter',
            mode: 'lines',
            name: 'Normal Distribution Curve',
            line: {
                color: widget.graphOptions.miscSettings.curveColor,
            },
            hovertemplate: `(%{x:.${widget.graphOptions.miscSettings.decimalPlaces}f}, %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f})`
        }
        graphOptions.layout.yaxis2 = {
            overlaying: 'y',
            side: 'right',
            visible: false
        }
        graphOptions.data.push(curveData)
        return graphOptions;
    }

    addLimitShapeAndAnnotation(graphOptions, specificationLimits) {
        const shapes = [];
        const annotations = [];

        for (const { axis, value, label } of specificationLimits) {
            if (!value || !label) continue;

            const isVertical = axis === 'x';

            shapes.push({
                type: 'line',
                ...(isVertical
                    ? {
                        x0: value,
                        x1: value,
                        y0: 0,
                        y1: 1,
                        xref: 'x',
                        yref: 'paper'
                    }
                    : {
                        x0: 0,
                        x1: 1,
                        y0: value,
                        y1: value,
                        xref: 'paper',
                        yref: 'y'
                    }),
                line: {
                    dash: 'dot',
                    width: 2,
                    color: '#000'
                }
            });

            annotations.push({
                x: isVertical ? value : 1,
                y: isVertical ? 1 : value,
                xref: isVertical ? 'x' : 'paper',
                yref: isVertical ? 'paper' : 'y',
                text: label,
                showarrow: false,
                yanchor: 'bottom'
            });
        }

        graphOptions.shapes = shapes;
        graphOptions.annotations = annotations;
        return graphOptions;
    }

      BoxChart(data, widget, graphOptions) {
        let colors = this.widgetDashboardService.colors
        widget.panelDataNew = widget?.panelDataNew.filter(item=> (item.tag != null && item.tag?.length && item.unit != null && item.unit?.length))
        let t = 0
        let graphData = []
        let objectKeys = Object.keys(data)
        objectKeys.forEach((obj,index)=>{
            let objValue = data[obj]
            if(objValue.length) {
                data[obj] = data[obj].filter(ele=> ele.a !== null && ele.a !== undefined)
                let objData = data[obj].map(ele=>ele.a.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                if(objData.length) {
                    t++;
                    let trace ={
                        name: this.widgetDashboardService.getLabel(widget?.panelDataNew, obj),
                        pointpos: 0,
                        type: 'box',
                        marker: {
                        color: colors[index]
                        },
                        boxpoints: false,
                        showlegend: false
                    }
                    let trace1 = {
                        name: this.widgetDashboardService.getLabel(widget.panelDataNew, obj),
                        mode: 'markers',
                        type: 'scatter',
                        marker: {
                        symbol: 'diamond',
                        color: 'yellow'
                        },
                        showlegend: false
                    }
                    if(widget.orientation === 'h') {
                        delete trace['y']
                        trace['x'] = objData
                        trace1['y'] = [this.widgetDashboardService.getLabel(widget.panelDataNew, obj)]
                        trace1['x'] = [objData[objData.length - 1]]
                        trace1['hovertemplate'] = `(%{y}, %{x:.${widget.graphOptions.miscSettings.decimalPlaces}f})`
                    } else {
                        delete trace['x']
                        trace['y'] = objData
                        trace1['x'] = [this.widgetDashboardService.getLabel(widget.panelDataNew, obj)]
                        trace1['y'] = [objData[objData.length - 1]]
                        trace1['hovertemplate'] = `(%{x}, %{y:.${widget.graphOptions.miscSettings.decimalPlaces}f})`
                    }
                    graphData.push(trace,trace1)
                }
            }
        })
        graphOptions.data = graphData;
        let isData = t !== 0
        return {isData, graphOptions}
      }

    getWeekLabels(groupedData, startDate) {
        startDate = moment(startDate).format('D MMM YYYY')
        const momentDate = moment(startDate);
        let from, to;
        let label = ''
        for (const week of Object.keys(groupedData)) {
            let match= week.match(/\d+ <br> \((.+?) - (.+?)\)/); // for getting range of week
            if (match) {
                const [, startStr, endStr] = match;
                from = moment(startStr, 'D MMM YYYY');
                to = moment(endStr, 'D MMM YYYY');
                if (momentDate.isBetween(from, to, null, '[]')) {
                    label = week
                    break
                }
            }
        }
        label = label.replace(/-\d+$/, '') // removing tagId key from label because we are adding while preparing data
        return {label, from, to}
    }

      getXaxisValue(range, groupFor) {
        let xLabels = []
        if(groupFor != 'week') {
            let startDate = new Date(range.from);
            let endDate = new Date(range.to)
            while(startDate <= endDate) {
                if(groupFor === "year") {
                    startDate.setMonth(0); // setting first month Jan
                    startDate.setDate(1); // setting first date of Jan
                    let startYear = new Date(startDate).getFullYear();
                    xLabels.push(startYear)
                    startDate.setFullYear(startDate.getFullYear() + 1)
                } else if(groupFor === "month") {
                    startDate.setDate(1); // setting first date of the month
                    const monthYear = moment(startDate).format('MMM YYYY')
                    xLabels.push(monthYear);
                    startDate.setMonth(startDate.getMonth() + 1); // 18jan // feb 18 // mar 18 // apr18
                } else if(groupFor === "day") {
                    startDate.setHours(0); // setting 0th hour to a day
                    xLabels.push(moment(startDate).format("YYYY-MM-DD"));
                    startDate.setDate(startDate.getDate() + 1)
                } else if(groupFor === "hour") {
                    xLabels.push(moment(startDate).set({ minute: 0 }).format("YYYY-MM-DD HH:mm"));
                    startDate.setHours(startDate.getHours() + 1)
                }
            }
        } else {
            moment.updateLocale('en', {
                week: {
                    dow: 1 // Monday is the first day of the week
                }
            });
            const startTime = moment(range.from);
            const endTime = moment(range.to);
            let days = endTime.diff(startTime, 'days') + 1;
            let newStartDate = startTime.clone();

            while (days > 0) {
                const weekday = newStartDate.weekday();
                const rangeEnd = newStartDate.clone().add(weekday === 0 ? 6 : 6 - weekday, 'day');
                days -= weekday === 0 ? 7 : 7 - weekday;
                const label = `Week ${newStartDate.week()} <br> (${newStartDate.format('D MMM YYYY')} -  ${days >= 0 ? rangeEnd.format('D MMM YYYY') : endTime.format('D MMM YYYY')})`;
                xLabels.push(label)
                newStartDate = rangeEnd.add(1, 'day');
            }
        }
        return xLabels;

      }

      getBoxChartData(data, widget, colors) {
        let t =0
        let graphData = []
        let objectKeys = Object.keys(data)
        objectKeys.forEach((obj,index)=>{
            let objValue = data[obj]
            if(objValue.length) {
                data[obj] = data[obj].filter(ele=> ele.a !== null && ele.a !== undefined)
                let objData = data[obj].map(ele=>ele.a.toFixed(widget.graphOptions.miscSettings.decimalPlaces))
                if(objData.length) {
                    t++;
                    let trace ={
                        name: this.widgetDashboardService.getLabel(widget?.panelDataNew, obj),
                        pointpos: 0,
                        type: 'box',
                        marker: {
                        color: colors[index]
                        },
                        boxpoints: false,
                    }
                    if(widget.orientation === 'h') {
                        delete trace['y']
                        trace['x'] = objData
                    } else {
                        delete trace['x']
                        trace['y'] = objData
                    }
                    graphData.push(trace)
                }
            }
        })
        return {t, graphData}
      }

      BoxChartTimeSeries(data, widget, graphOptions, timeRangeObj) {
        let colors = this.widgetDashboardService.colors
        widget.panelDataNew = widget?.panelDataNew.filter(item=> (item.tag != null && item.tag?.length && item.unit != null && item.unit?.length))
        let dataPresent = 0
        if(!widget.groupFor) {
            const { t , graphData} = this.getBoxChartData(data, widget, colors)
            dataPresent = t
            graphOptions.data = graphData
        } else {
            const groupedData = {};
            let maxLength = 0;
            let week_key, week_from, week_to;
            let range = this.widgetDashboardService.prepareTimeRange(timeRangeObj?.id, timeRangeObj?.customRange)
            let xLabels = this.getXaxisValue(range, widget.groupFor)
            for (const key in data) {
                xLabels.forEach(xLabelKey=>{
                    if (!groupedData[`${xLabelKey}-${key}`]) {
                        groupedData[`${xLabelKey}-${key}`] = [];
                    }
                })
            }
            for (const key in data) {
                const dataArray = data[key];
                dataArray.forEach(entry => {
                    dataPresent++;
                    const date = new Date(entry.t);
                    if(new Date(range.from) > date) {
                        return;
                    }
                    let xLabelKey;
                    if(widget.groupFor === 'year') {
                        xLabelKey = date.getFullYear();
                    } else if (widget.groupFor === 'week') {
                        let formatted_date = moment(date).format('D MMM YYYY')
                        let momentDate = moment(formatted_date)
                        if(week_key && momentDate.isBetween(week_from, week_to, null, '[]')) {
                             xLabelKey = week_key
                        } else {
                           let {label, from, to} = this.getWeekLabels(groupedData, date)
                            week_key = xLabelKey = label
                            week_from = from
                            week_to = to
                        }
                    } else if(widget.groupFor === 'day') {
                        xLabelKey = moment(date).format('YYYY-MM-DD');
                    } else if(widget.groupFor === 'hour') {
                        xLabelKey =  moment(date).set({minute: 0}).format("YYYY-MM-DD HH:mm");
                    } else {
                        xLabelKey = moment(date).format('MMM YYYY');
                    }
                    let a = `${xLabelKey}-${key}`
                    if (entry?.a !== null && entry?.a !== undefined) {
                        groupedData[a].push(entry.a.toFixed(widget.graphOptions.miscSettings.decimalPlaces));
                    }
                    if(groupedData[a].length >= maxLength) {
                        maxLength = groupedData[a].length
                    }
                });
            }

            for(const key in data) {
                let trace = {
                    id: key,
                    name: this.widgetDashboardService.getLabel(widget?.panelDataNew,key),
                    y: [],
                    x: [],
                    type: 'box',
                    boxpoints: false,
                    orientation: widget.orientation
                }
                graphOptions.data.push(trace)
            }
            graphOptions.data.forEach((option,i)=>{
                xLabels.forEach(element =>{
                    let key = element + '-' + option.id
                    let value = groupedData[key]
                    let numOfNulls = maxLength - groupedData[key].length
                    if(widget.orientation === 'h') {
                        option.x.push(...value,...Array(numOfNulls).fill(null))
                        option.y.push(...Array(maxLength).fill(element))
                    } else {
                        option.y.push(...value,...Array(numOfNulls).fill(null))
                        option.x.push(...Array(maxLength).fill(element))
                    }
                })
                option['marker'] = {
                    color: colors[i]
                }
            })
            graphOptions.layout.boxmode = 'group'
        }
        let isData = dataPresent !== 0
        return {isData, graphOptions}
      }

      getSortedData(data) {
        let uniqueValues = Array.from(new Set(data.map(item => item.x[0])))
        function customSort(a, b) {
        return uniqueValues.indexOf(a.x[0]) - uniqueValues.indexOf(b.x[0]);
        }
        data.sort(customSort);
        return data
      }

        setSurveyChartPanelData(panelDataNew) {
            this.SurveyChartPanelDataNew = panelDataNew;
        }

        getSurveyChartPanelData() {
            return this.SurveyChartPanelDataNew;
        }

    resolveNames(value, groupFor) {
        switch (groupFor) {
            case 'state': return this.alertService.getStateString(Number(value));
            case 'statusValue': return this.alertService.getStatusString(Number(value));
            case 'type': return this.alertService.getAlertTypeString(Number(value));
            case 'severityValue': return this.alertService.getSeverityString(Number(value));
            default: value;
        }
    }

    getLabel(value, fieldName, collectionName) {
        switch (collectionName) {
            case 'alerts': switch (fieldName) {
                case 'state': return this.alertService.getStateString(Number(value));
                case 'statusValue': return this.alertService.getStatusString(Number(value));
                case 'type': return this.alertService.getAlertTypeString(Number(value));
                case 'severityValue': return this.alertService.getSeverityString(Number(value));
                case 'category': return this.alertService.getAlertCategoryString(Number(value));
                case 'component': return value ? value : value == "" ? "Empty" : "Other";
                default: return this.getName(value);
            }
            case 'tags': switch (fieldName) {
                case 'tagId': return value ? value : value == "" ? "Empty" : "Other";
                case 'type': return value ? value : value == "" ? "Empty" : "Other";
                case 'calculated': return value ?? "Other";
                case 'modelling': return value ?? "Other";
                case 'isclientTag': return value ?? "Other";
                default: return this.getName(value);
            }
            case 'sapnotifications': switch (fieldName) {
                case 'facility': return this.getName(value);
                case 'system': return this.getName(value);
                case 'unit': return this.getName(value);
                case 'user': return this.getName(value);
                default: return value ? value : value == "" ? "Empty" : "Other";
            }
            case 'test item': switch (fieldName) {
                default: return this.getName(value);
            }
            default: return this.getName(value);
        }
    }

    removeCustomFieldForApiCall(filter) {
        return {
            ...filter,
            filter: filter.filter.map(sub => ({
                ...sub,
                subFilter: sub.subFilter.map(item => {
                    if (item.range?.custom) {
                        let { custom, from, to } = item.range;
                        if (custom.id && custom.id != 99) {
                            let dateRange = this.widgetDashboardService.prepareTimeRange(custom.id, {})
                            from = dateRange.from
                            to = dateRange.to
                        }
                        return {
                            ...item,
                            range: { from, to }
                        };
                    }
                    return item;
                })
            }))
        };
    }

    prepareMongoPanelData(widget) {
        let mongoPanelData = widget.mongoPanelData
        let finalPayload = [];
        let unqueSource = new Set()
        for (let groupData of mongoPanelData) {
            if (!groupData.dispAttribute) continue;
            if (!unqueSource.has(groupData.targetCollection)) unqueSource.add(groupData.targetCollection)
            let extraFieldForCalc = {}
            if (groupData?.calc) {
                extraFieldForCalc = this.handleCalcAttribute(mongoPanelData, groupData?.label)
            }
            if (!groupData?.calc || (groupData?.calc && !this.isInValidForm))
                finalPayload.push({
                    targetCollection: groupData.targetCollection || this.getCalculatedCollectionName(mongoPanelData, groupData?.label),
                    attribute: groupData.attribute || groupData.label,
                    label: groupData?.label,
                    aggregation: groupData?.subTargetOperator,
                    ...(groupData?.filters?.filter?.length && { filters: this.removeCustomFieldForApiCall(groupData.filters) }),
                    ...(extraFieldForCalc),
                })
        }
        if (widget?.graphOptions?.graphType == 14 && finalPayload?.length) {
            let { groupBy, sideBy, stackBy, displayBy, bins } = widget?.graphOptions?.miscSettings?.mongoPanelMisc
            let filters = finalPayload[0]
            Array.from(unqueSource).forEach((source) => {
                if (groupBy) {
                    finalPayload.push({
                        targetCollection: source,
                        label: groupBy.replaceAll('.', ''),
                        groupBy: groupBy,
                        filters: filters?.filters,
                        ...((displayBy && this.targetCollectionList.find(collection => collection.name == source)?.fields?.find(field => field.name == groupBy)?.displayBy?.length) && { groupByCategory: displayBy })
                    })
                }
                if (sideBy) {
                    finalPayload.push({
                        targetCollection: source,
                        label: sideBy.replaceAll('.', ''),
                        groupBy: sideBy,
                        filters: filters?.filters,

                    })
                }
                if (stackBy) {
                    let stackField = this.targetCollectionList.find(collection => collection.name == source)?.fields?.find(field => field.name == stackBy)
                    finalPayload.push({
                        targetCollection: source,
                        label: stackBy.replaceAll('.', ''),
                        groupBy: stackBy,
                        filters: filters?.filters,
                        ...((bins && (stackField === undefined || stackField?.numerical)) && { bins: bins }),
                        ...(!stackField ? this.handleCalcAttribute(mongoPanelData, stackBy) : {})
                    })
                }
            });
        }
        return this.isInValidForm ? [] : finalPayload
    }

    prepareHistogramPanelData(widget) {
        let mongoPanelData = JSON.parse(JSON.stringify(widget.mongoPanelData))
        for (let groupData of mongoPanelData) {
            groupData.filters = this.removeCustomFieldForApiCall(groupData.filters)
        }
        return mongoPanelData
    }

    getCalculatedCollectionName(mongoPanelData, row) {
        row = mongoPanelData.find(data => data.label == row && data.calc)
        if (!row) return {}
        const variablePattern = /\bA\d+\b/g;
        const matches = row.relation.match(variablePattern) || [];
        const variableIndices = matches.map(v => parseInt(v.substring(1), 10) - 1);
        return mongoPanelData[variableIndices[0]].targetCollection
    }
    handleCalcAttribute(mongoPanelData, row) {
        row = mongoPanelData.find(data => data.label == row && data.calc)
        if (!row) return {}
        const variablePattern = /\bA\d+\b/g;
        const matches = row.relation.match(variablePattern) || [];
        const variableIndices = matches.map(v => parseInt(v.substring(1), 10) - 1);
        let variables = [];
        let filter;
        let prePopulateBaseFilter = []
        variableIndices.forEach(index => {
            const entry = mongoPanelData[index];
            let field = this.targetCollectionList.find(collection => collection.name == entry.targetCollection)?.fields?.find(field => field.name == entry.attribute)
            if (!filter && entry?.filters?.filter?.length) {
                filter = entry.filters
            }
            if (entry.attribute) {
                if (field?.prePopulateBaseFilter?.length) prePopulateBaseFilter.push(field?.prePopulateBaseFilter)
                variables.push(entry.attribute);
            }
            else this.isInValidForm = true;
        })
        let finalObject = { relation: row.relation, fields: variables }
        if (filter) {
            filter = this.removeCustomFieldForApiCall(filter)
            finalObject['filters'] = filter
        }
        if (prePopulateBaseFilter.length) {
            finalObject['prePopulateBaseFilter'] = prePopulateBaseFilter
        }
        if (row.filters?.filter?.length) {
            row.filters = this.removeCustomFieldForApiCall(row.filters)
            finalObject['numericalFilters'] = row.filters
        }
        return finalObject
    }

    createDrillDownObjectForGroupByCharts(chartParams, widget, tagValueResponse, timeRangeObj ) {
        const mongoPanel = widget?.mongoPanelData;
        const selectedDrilldownOptions = widget?.drilldownOptions;

        let selectedMongoPanelData = mongoPanel.find(data => data.dispAttribute);
        if (!selectedMongoPanelData) return;

        const drillDownObject = this.prepareDrillDownObjectForGroupByCharts(selectedMongoPanelData, mongoPanel, timeRangeObj, selectedDrilldownOptions);
        drillDownObject.data.drilldownFilters = this.prepareDrillDownFiltersForBarCharts(chartParams,widget,selectedMongoPanelData);

        return drillDownObject;
    }

    prepareDrillDownFiltersForBarCharts(chartParams, widget, selectedMongoPanelData): any[] {
        const mongoPanelParams = widget?.graphOptions?.miscSettings?.mongoPanelMisc;
        const targetCollection = this.getTargetCollectionForDrilldownAndSlicer(selectedMongoPanelData, widget.mongoPanelData);
        if(!mongoPanelParams) return [];

        const drilldownFilters = [];
        let groupByValue,sideByValue,stackByValue;
        let chartNumber = chartParams.pointIndex;
        if(mongoPanelParams?.sideBy && mongoPanelParams?.stackBy) {
            groupByValue = chartParams.data?.categorySortedArray[chartNumber][0]?.id;
            sideByValue = chartParams.data?.categorySortedArray[chartNumber][1]?.id;
            stackByValue = chartParams.data?.customId;
        } else if(mongoPanelParams?.sideBy) {
            groupByValue = chartParams.data?.categorySortedArray[chartNumber]?.id;
            sideByValue = chartParams.data?.customId;
        } else if(mongoPanelParams?.stackBy) {
            groupByValue = chartParams.data?.categorySortedArray[chartNumber]?.id;
            stackByValue = chartParams.data?.customId;
        } else {
            groupByValue = chartParams.data?.categorySortedArray[chartNumber]?.id;
        }

        if(mongoPanelParams?.groupBy && groupByValue != null) {
            const fields = this.targetCollectionList.find(tc => tc.name == targetCollection).fields
            const computingOnDateFields = fields.find(f => f.name == mongoPanelParams.groupBy).isDateField

            const drilldownFilter: IGroupByChartNormalDrilldownFilter = {
                expression: mongoPanelParams.groupBy,
                type: 'raw',
                operator: computingOnDateFields ? 'between' : '=',
                value: computingOnDateFields ? this.getParsedDateRangeForDrilldownFilter(groupByValue) : groupByValue,
                filters: {
                    operator: 'and',
                    filter: []
                }
            }
            drilldownFilters.push(drilldownFilter);
        }

        if(sideByValue != null && mongoPanelParams?.sideBy) {
            const fields = this.targetCollectionList.find(tc => tc.name == targetCollection).fields
            const computingOnDateFields = fields.find(f => f.name == mongoPanelParams.sideBy).isDateField


            const drilldownFilter: IGroupByChartNormalDrilldownFilter = {
                expression: mongoPanelParams.sideBy,
                type: 'raw',
                operator: computingOnDateFields ? 'between' : '=',
                value: computingOnDateFields ? this.getParsedDateRangeForDrilldownFilter(sideByValue) : sideByValue,
                filters: {
                    operator: 'and',
                    filter: []
                }
            }
            drilldownFilters.push(drilldownFilter);
        }

        if(mongoPanelParams?.stackBy && stackByValue != null) {
            let stackField = this.targetCollectionList.find(collection => collection.name == targetCollection)?.fields?.find(field => field.name == mongoPanelParams?.stackBy)
            if(stackField) {
                const value = {
                    from: 0,
                    to: 0
                }
                if(stackField?.numerical) {
                    const match = stackByValue.match(/(\d+)\s*-\s*(\d+)/);
                    if (match) {
                        value.from = parseInt(match[1], 10);
                        value.to = parseInt(match[2], 10);
                    }
                }
                const drilldownFilter: IGroupByChartNormalDrilldownFilter = {
                    expression: mongoPanelParams.stackBy,
                    type: 'raw',
                    operator: stackField?.numerical ? 'between' : '=',
                    value: stackField?.numerical ? value : stackByValue,
                    filters: {
                        operator: 'and',
                        filter: []
                    }
                }
                drilldownFilters.push(drilldownFilter);
            } else {
                const mongoPanel = widget?.mongoPanelData;
                const calculatedField = mongoPanel.find(data => data.label == mongoPanelParams?.stackBy && data.calc);
                const filters = this.getFiltersForDrilldown(calculatedField, mongoPanel);
                const {fields, computingOnDateFields} = this.getFieldsForCalculatedAttributesOfDrilldown(mongoPanel, calculatedField);
                const value = {
                    from: 0,
                    to: 0
                }
                const match = stackByValue.match(/(\d+)\s*-\s*(\d+)/);

                if (match) {
                    value.from = parseInt(match[1], 10);
                    value.to = parseInt(match[2], 10);
                }

                const drilldownFilter: IGroupByChartCalcDrilldownFilter = {
                    expression: calculatedField?.relation || mongoPanelParams?.stackBy,
                    label: calculatedField?.label || mongoPanelParams?.stackBy,
                    type: 'calculated',
                    operator: 'between',
                    value,
                    filters,
                    fields
                }
                drilldownFilters.push(drilldownFilter);
            }
        }
        return drilldownFilters;
    }

    getParsedDateRangeForDrilldownFilter(value) {
        if (!value || typeof value !== "string") return null;

        const trimmed = value.trim();
        const format = "YYYY-MM-DDTHH:mm:ss.SSSZ";

        // Match "Q2 - 24" or "Q1-24"
        const quarterMatch = trimmed.match(/^Q\s?([1-4])\s?-\s?(\d{2})$/i);
        if (quarterMatch) {
            const quarter = parseInt(quarterMatch[1]);
            const year = 2000 + parseInt(quarterMatch[2]);
            return {
                from: moment().utc().year(year).quarter(quarter).startOf('quarter').format(format),
                to: moment().utc().year(year).quarter(quarter).endOf('quarter').format(format)
            };
        }

        // Match "FW12 - 24" or "FW1-25"
        const weekMatch = trimmed.match(/^FW\s?(\d{1,2})\s?-\s?(\d{2})$/i);
        if (weekMatch) {
            const week = parseInt(weekMatch[1]);
            const year = 2000 + parseInt(weekMatch[2]);
            return {
                from: moment().utc().year(year).week(week).startOf('week').format(format),
                to: moment().utc().year(year).week(week).endOf('week').format(format)
            };
        }

        // Match "Jan - 25" or "Feb-24"
        const monthYearMatch = trimmed.match(/^([A-Za-z]+)\s*-\s*(\d{2})$/);
        if (monthYearMatch) {
            const month = monthYearMatch[1];
            const year = 2000 + parseInt(monthYearMatch[2]);
            const m = moment.utc(`${month} ${year}`, "MMM YYYY");
            return {
                from: m.clone().startOf('month').format(format),
                to: m.clone().endOf('month').format(format)
            };
        }

        // Match "2024"
        const fullYearMatch = trimmed.match(/^(\d{4})$/);
        if (fullYearMatch) {
            const year = fullYearMatch[1];
            return {
                from: moment.utc(`${year}-01-01`).startOf('day').format(format),
                to: moment.utc(`${year}-12-31`).endOf('day').format(format)
            };
        }

        // Try parsing standard date formats
        const date = moment.utc(trimmed, ["DD-MMM-YY", moment.ISO_8601], true);
        return date.isValid()
            ? { from: date.startOf('day').format(format), to: date.endOf('day').format(format) }
            : null;
    }


    prepareDrillDownObjectForGroupByCharts(selectedMongoPanelData, mongoPanelData, timeRangeObj, selectedDrilldownOptions): IGroupByChartDrillDownObject {
        const dateRange = this.widgetDashboardService.prepareTimeRange(timeRangeObj?.id, timeRangeObj?.customRange);
        const targetCollection = this.getTargetCollectionForDrilldownAndSlicer(selectedMongoPanelData, mongoPanelData);

        const { projectedFields, populatedFields } = this.createProjectedAndPopulatedFilterObject(selectedDrilldownOptions, targetCollection);

        const filters = this.getFiltersForDrilldown(selectedMongoPanelData, mongoPanelData);

        const attributeFilter = {
            attribute: selectedMongoPanelData.attribute,
            type: selectedMongoPanelData.calc ? 'calculated' : 'raw',
            filters
        }

        if(selectedMongoPanelData?.calc) {
            const { fields, computingOnDateFields } = this.getFieldsForCalculatedAttributesOfDrilldown(mongoPanelData, selectedMongoPanelData);
            attributeFilter['fields'] = fields;
            attributeFilter['label'] = selectedMongoPanelData.label;
            attributeFilter['numericalFilters'] = selectedMongoPanelData.filters;
            attributeFilter['attribute'] = computingOnDateFields ? `(${selectedMongoPanelData.relation})/60000` : selectedMongoPanelData.relation;
        }

        return {
            from: dateRange.from,
            to: dateRange.to,
            projectedFields,
            populatedFields,
            data: {
                targetCollection,
                drilldownFilters: [],
                attributeFilter
            }
        };
    }

    createDrillDownObject(chartParams, widget, tagValueResponse, timeRangeObj) {
        const mongoPanel = widget?.mongoPanelData;
        const selectedDrilldownOptions = widget?.drilldownOptions;

        let selectedMongoPanelData = this.getSelectedMongoPanelData(mongoPanel);
        if (!selectedMongoPanelData) return;

        const drillDownObject = this.prepareDrillDownObject(selectedMongoPanelData, mongoPanel, timeRangeObj, selectedDrilldownOptions);

        if (!selectedMongoPanelData.calc) {
            this.addRawDrilldownFilters(drillDownObject, selectedMongoPanelData, chartParams, widget, tagValueResponse);
        } else {
            this.addCalculatedDrilldownFilters(drillDownObject, selectedMongoPanelData, mongoPanel, tagValueResponse, widget, chartParams);
        }

        return drillDownObject;
    }

    getSelectedMongoPanelData(mongoPanel) {
        let selectedMongoPanelData = mongoPanel.find(data => data.dispAttribute);
        if (selectedMongoPanelData?.calc) {
            const variables = this.extractFormulaVariables(selectedMongoPanelData.relation);
            if (variables.length === 1 && selectedMongoPanelData.relation.trim() === variables[0]) {
                const index = parseInt(variables[0].substring(1)) - 1;
                selectedMongoPanelData = mongoPanel[index];
            }
        }
        return selectedMongoPanelData;
    }

    private addRawDrilldownFilters(drillDownObject, selectedMongoPanelData, chartParams, widget, tagValueResponse) {
        const id = selectedMongoPanelData.id;
        const selectedTagData = tagValueResponse[id] as Array<any>;
        const drilldownFilter = this.generateDrilldownFilterForRawAttribute(selectedMongoPanelData, chartParams, widget, selectedTagData);
        drillDownObject.data.drilldownFilters = [drilldownFilter];
    }

    private addCalculatedDrilldownFilters(drillDownObject, selectedMongoPanelData, mongoPanel, tagValueResponse, widget, chartParams) {
        const { fields, computingOnDateFields } = this.getFieldsForCalculatedAttributesOfDrilldown(mongoPanel, selectedMongoPanelData);

        const { minimum, maximum, isDate } = this.getMinMaxValuesForCalculatedDrilldown(
            selectedMongoPanelData.relation,
            tagValueResponse,
            widget,
            chartParams
        );

        drillDownObject.data.drilldownFilters = {
            expression: computingOnDateFields && !isDate
                ? `(${selectedMongoPanelData.relation})/60000`
                : selectedMongoPanelData.relation,
            minimum,
            maximum,
            fields,
        };
    }

    chooseOperatorForDrillDownFilter(chartParams: any, isDate: boolean = false, type: 'min' | 'max' = 'min'): FilterOperator {
        if (isDate) {
            return type === 'min' ? '>=' : '<=';
        }
        const isFirstBar = chartParams.dataIndex === 0;
        if (type === 'min') {
            return isFirstBar ? '>=' : '>';
        }
        return '<=';
    }


    generateDrilldownFilterForRawAttribute(selectedMongoPanelData: any, chartParams: any, widget, selectedTagData: any[]): INormalDrilldownFilter {
        const id = selectedMongoPanelData.id;
        let min = Infinity, max = -Infinity;
        if (this.isValidDateString(selectedTagData[0])) {
            for (let tagValue of selectedTagData) {
                min = Math.min(min, new Date(tagValue).getTime());
                max = Math.max(max, new Date(tagValue).getTime());
            }
            min = new Date(min).setHours(0, 0, 0, 0);
            max = new Date(max).setHours(23, 59, 59, 999);
            const ranges = this.createDateRanges(min, max, widget.bins);
            return {
                expression: selectedMongoPanelData.attribute,
                minimum: { value: new Date(ranges[chartParams.dataIndex].start).toUTCString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, true, 'min') },
                maximum: { value: new Date(ranges[chartParams.dataIndex].end).toUTCString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, true, 'max') },
            }
        }
        else if (this.isValidNumber(selectedTagData[0])) {
            for (let tagValue of selectedTagData) {
                min = Math.min(min, Number(tagValue));
                max = Math.max(max, Number(tagValue));
            }
            const ranges = this.createRanges(min, max, widget.bins)
            return {
                expression: selectedMongoPanelData.attribute,
                minimum: { value: ranges[chartParams.dataIndex].start.toString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, false, 'min') },
                maximum: { value: ranges[chartParams.dataIndex].end.toString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, false, 'max') },
            }
        }
        return {
            expression: '',
            minimum: { value: '', operator: '=' },
            maximum: { value: '', operator: '=' },

        }
    }

    getMinMaxValuesForCalculatedDrilldown(relation, tagValueResponse, widget, chartParams) {
        let isDate = false;
        const data = this.createBinsForCustomFormula(relation, tagValueResponse);
        if (data.length && typeof data[0] == 'string' && moment(data[0]).isValid()) isDate = true;
        let min = Infinity, max = -Infinity;
        if (isDate) {
            for (let tagValue of data) {
                min = Math.min(min, new Date(tagValue).getTime());
                max = Math.max(max, new Date(tagValue).getTime());
            }
            min = new Date(min).setHours(0, 0, 0, 0);
            max = new Date(max).setHours(23, 59, 59, 999);
            const ranges = this.createDateRanges(min, max, widget.bins);
            return {
                minimum: { value: new Date(ranges[chartParams.dataIndex].start).toUTCString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, true, 'min') },
                maximum: { value: new Date(ranges[chartParams.dataIndex].end).toUTCString(), operator: this.chooseOperatorForDrillDownFilter(chartParams, true, 'max') },
                isDate: isDate
            }

        }
        else {
            for (let tagValue of data) {
                min = Math.min(min, Number(tagValue));
                max = Math.max(max, Number(tagValue));
            }
            const ranges = this.createRanges(min, max, widget.bins)
            return {
                minimum: { value: ranges[chartParams.dataIndex].start, operator: this.chooseOperatorForDrillDownFilter(chartParams, false, 'min') },
                maximum: { value: ranges[chartParams.dataIndex].end, operator: this.chooseOperatorForDrillDownFilter(chartParams, false, 'max') },
                isDate: isDate
            }
        }
    }

    flattenDataOfObject(data: any[]) {
        const flattenedData = data.map(item => {
            const flattenedItem: any = {};
            for (const key in item) {
                if (typeof item[key] === 'object' && item[key] !== null) {
                    for (const subKey in item[key]) {
                        flattenedItem[`${key}_${subKey}`] = item[key][subKey];
                    }
                } else {
                    flattenedItem[key] = item[key];
                }
            }
            return flattenedItem;
        });
        return flattenedData;
    }

    prepareDrilldownTableData(data: any[], selectedDrilldownOptions: string[], collectionName: string) {
        // Step 1: Flatten the data
        const flattenedData = this.flattenDataOfObject(data);
        // Step 2: Create drilldown config mapping for selected options
        const config = this.drilldownOptions[collectionName];

        // Step 1: Create drilldown config mapping for selected options
        const drilldownConfigMap = selectedDrilldownOptions.map(option => {
            const configEntry = config.find(c => c.name === option);
            return {
                name: option,
                displayName: configEntry?.displayName || option,
                dataObjectKey: configEntry.dataObjectKey || option.replace(/\./g, '_'), // fallback
                isDateField: configEntry?.isDateField || false, // Check if the field is a date field
                resolveName: configEntry?.resolveName || false, // Check if the field needs to be resolved
                dependsOn: configEntry?.dependsOn || null, // Check if the field has a dependency
            };
        });

        // Step 2: Map the data into rows with display names and corresponding values
        const formattedData = flattenedData.map(entry => {
            const row: Record<string, any> = {};

            // Add the ID field to the row
            row['id'] = entry['_id'];

            // Map the selected options to the row
            drilldownConfigMap.forEach(({ displayName, dataObjectKey, isDateField, resolveName, dependsOn }) => {
                let value = 'NA'; // Default value for undefined or null fields
                if(dependsOn) {
                    let dependsOnValue = entry[dependsOn];
                    value = this.getDisplayValueForDependsOn(dependsOn, dependsOnValue, entry[dataObjectKey]);
                }
                else {
                    value = resolveName ? this.getLabel(entry[dataObjectKey], dataObjectKey, collectionName) : entry[dataObjectKey];
                    value = value ? value : 'NA'; // Handle null or undefined values
                }
                if (isDateField && value !== 'NA') {
                    value = moment(value).format('DD-MMM-YYYY HH:mm'); // Format date fields
                }
                row[displayName] = value;
            });
            return row;
        });

        return formattedData;
    }


    getFieldsForCalculatedAttributesOfDrilldown(mongoPanelData: any, selectedMongoPanelData: any) {
        const variablePattern = /\bA\d+\b/g;
        const variables = selectedMongoPanelData.relation.match(variablePattern) || [];
        const fields = [];
        let computingOnDateFields = false;
        for (let i = 0; i < variables.length; i++) {
            const v = variables[i];
            const index = parseInt(v.substring(1)) - 1; // Convert A1 -> 0, A2 -> 1, etc.
            if (i == 0) {
                const selectedAttribute = mongoPanelData[index].attribute;
                const selectedTargetCollection = mongoPanelData[index].targetCollection;
                const fields = this.targetCollectionList.find(tc => tc.name == selectedTargetCollection).fields
                computingOnDateFields = fields.find(f => f.name == selectedAttribute).isDateField
            }
            fields.push(mongoPanelData[index].attribute);
        }
        return {
            fields: fields,
            computingOnDateFields: computingOnDateFields
        }
    }

    isValidDateString(value: any): boolean {
        if (typeof value !== 'string') {
            return false;
        }
        const d = new Date(value);
        return !isNaN(d.getTime());
    }

    isValidNumber(value: any): boolean {
        if (typeof value === 'number') {
            return !isNaN(value);
        } else if (typeof value === 'string') {
            // Convert string to number and check if it is a valid number.
            const num = Number(value);
            return !isNaN(num);
        }
        return false;
    }

    prepareDrillDownObject(selectedMongoPanelData, mongoPanelData, timeRangeObj, selectedDrilldownOptions): IDrillDownObject {
        const dateRange = this.widgetDashboardService.prepareTimeRange(timeRangeObj?.id, timeRangeObj?.customRange);

        const targetCollection = this.getTargetCollectionForDrilldownAndSlicer(selectedMongoPanelData, mongoPanelData);

        const { projectedFields, populatedFields } = this.createProjectedAndPopulatedFilterObject(selectedDrilldownOptions, targetCollection);

        const filters = this.getFiltersForDrilldown(selectedMongoPanelData, mongoPanelData);

        return {
            drilldownType: selectedMongoPanelData.calc ? "calc" : "normal",
            from: dateRange.from,
            to: dateRange.to,
            data: {
                targetCollection,
                filters,
                drilldownFilters: this.initializeDrilldownFilters(selectedMongoPanelData),
            },
            projectedFields,
            populatedFields,
        };
    }

    getTargetCollectionForDrilldownAndSlicer(selectedMongoPanelData, mongoPanelData): string {
        if (!selectedMongoPanelData.calc) {
            return selectedMongoPanelData.targetCollection;
        }

        const formulaVariables = this.extractFormulaVariables(selectedMongoPanelData.relation);
        for (const variable of formulaVariables) {
            const index = parseInt(variable.substring(1)) - 1; // Convert A1 -> 0, A2 -> 1, etc.
            const panelData = mongoPanelData[index];
            if (panelData.attribute !== 'currentTime') {
                return panelData.targetCollection;
            }
        }

        return mongoPanelData[0]?.targetCollection; // Fallback to the first panel data's target collection
    }

    extractFormulaVariables(formula: string): string[] {
        const variablePattern = /\bA\d+\b/g;
        return formula.match(variablePattern) || [];
    }

    private getFiltersForDrilldown(selectedMongoPanelData, mongoPanelData): any {
        if (!selectedMongoPanelData.calc) {
            return selectedMongoPanelData.filters;
        }

        const formulaVariables = this.extractFormulaVariables(selectedMongoPanelData.relation);
        for (const variable of formulaVariables) {
            const index = parseInt(variable.substring(1)) - 1; // Convert A1 -> 0, A2 -> 1, etc.
            const panelData = mongoPanelData[index];
            if (panelData.attribute !== 'currentTime') {
                return panelData.filters;
            }
        }

        return null; // Fallback if no filters are found
    }

    private initializeDrilldownFilters(selectedMongoPanelData): any {
        return selectedMongoPanelData.calc ? {} as ICalcDrilldownFilter : [] as INormalDrilldownFilter[];
    }

    createProjectedAndPopulatedFilterObject(selectedDrillDownOptions: any[], targetCollection: string) {
        const populatedFields = new Map<string, IPopulatedField>();
        let projectedFields: string[] = []
        selectedDrillDownOptions.forEach((option) => {
            this.drilldownOptions[targetCollection].forEach((item) => {
                if (option === item.name) {
                    if (item.localField && item.foreignField) {
                        if (!populatedFields.has(item.localField)) {
                            populatedFields.set(item.localField, {
                                collectionName: item.collectionName,
                                localName: item.localField,
                                attributes: item.foreignField
                            })
                        }
                        else {
                            const existingField = populatedFields.get(item.localField);
                            existingField.attributes += `,${item.foreignField}`;
                            populatedFields.set(item.localField, existingField);
                        }
                    }
                    else {
                        projectedFields.push(item.name);
                    }
                    // Adding the dependsOn field to projectedFields if it exists
                    if(item.dependsOn && !projectedFields.includes(item.dependsOn)) {
                        projectedFields.push(item.dependsOn)
                    }
                }
            })
        })
        return {
            projectedFields: projectedFields.join(','),
            populatedFields: Array.from(populatedFields.values())
        }
    }

    buildSlicerFilters(slicerOptions: string[], widgetSlicerState:WidgetSlicerState): ISlicerFilter[] {
        const slicerFilters: ISlicerFilter[] = [];
        for (const slicer of slicerOptions) {
            const selectedOptions = widgetSlicerState[slicer]?.filter(opt => opt.selected);
            const selectedIds = selectedOptions?.map(opt => opt.id) || [];

            const hasOther = selectedIds.includes('empty');
            const realValues = selectedIds.filter(id => id !== 'empty');

            if (hasOther) {
                // Match values + null/missing
                const filter: ISlicerFilter = {
                    column: slicer,
                    values: realValues,
                    check: 'inOrMissing'
                };
                slicerFilters.push(filter);
            } else if (realValues.length > 0) {
                // Only known values
                const filter: ISlicerFilter = {
                    column: slicer,
                    values: realValues,
                    check: 'in'
                };
                slicerFilters.push(filter);
            }
        }
        return slicerFilters;
    }
}
